/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import AipHistoryEnhanced from "./AipHistoryEnhanced";
import useAipHistoryTracker from "../hooks/useAipHistoryTracker";
import CareCountSection from "./integrations/carecount/CareCountSection";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeartbeat,
  faPlusSquare,
  faHome,
  faUsers,
  faThermometerHalf,
  faLungs,
  faTint,
  faWeight,
  faSmile,
  faFrown,
  faTachometerAlt,
  faQuestionCircle,
  faHourglassHalf,
  faTimesCircle,
  faCheckCircle,
  faChartLine,
  faClipboardList,
  faChevronRight,
  faCircleInfo,
  faBrain,
  faExclamationTriangle,
  faClipboardCheck,
  faFileLines,
  faUser,
  faPills,
  faComments,
  faUtensils,
  faDroplet,
  faHeartPulse,
  faLaptop,
  faMusic,
  faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";
import {
  Activity as LucideActivity,
  FileText as LucideFileText,
  Watch as LucideWatch,
  Stethoscope as LucideStethoscope,
} from "lucide-react";
import CareLink360Section from "./integrations/carelink360/CareLink360Section";
import SensiReportSection from "./integrations/sensireport/SensiReportSection";
import MedWandSection from "./integrations/medwand/MedWandSection";
import AbleCareSection from "./integrations/ablecare/AbleCareSection";
import {
  ABLECARE_SAMPLE_CLIENT_IDS,
  ABLECARE_SAMPLE_CLIENT_NAMES,
} from "./integrations/ablecare/ableCareMockData";
import PacSanaSection from "./integrations/pacsana/PacSanaSection";
import {
  PACSANA_SAMPLE_CLIENT_IDS,
  PACSANA_SAMPLE_CLIENT_NAMES,
} from "./integrations/pacsana/pacSanaMockData";
import VitalInsightsSection from "./integrations/vitalInsights/VitalInsightsSection";
import { normalizeViAgencyDashboardUrl } from "../utils/vitalInsightsUrl";
import { buildScoreInsights } from "../utils/aipScoreInsights";
import {
  getClientWellSkyStatusLabel,
  getWellSkyDeactivationTooltip,
  isWellSkyClient,
  mergeWellSkyStatusFields,
} from "../utils/clientWellSkyStatus";
import {
  deriveAipWellSkyLineage,
  hydrateCareTeamModelFromWellSkyMetrics,
  isAdlUiFieldFromWellSky,
} from "../utils/aipWellSkyLineage";
import {
  fetchClient,
  calculateClientScores,
  fetchLatestAdviceForClient,
  fetchAdviceForClient,
  fetchAppointments,
  fetchQuestionSet,
  updateClient,
  fetchAipHistory,
} from "../utils/api";
import { API_BASE } from "../apiConfig";
import calculateScoresV2 from "../utils/aipCalculatorV2";
import ScoreResultModal from "./ScoreResultModal";
import UnityCareVisit from "./UnityCareVisit";
import "./AipAnalysis.css";
import "./dashboard-cards.css";
import WellBeingDetailsSection from "./WellBeingDetailsSection";
import CareTeamDetailsSection from "./CareTeamDetailsSection";
import EnvironmentDetailsSection from "./EnvironmentDetailsSection";
import { getWellSkyClientData } from "../api/integrationApi";
import {
  getEsCategorySummaries,
  isB2cSelfAssessment,
} from "../utils/esSelfAssessmentDisplay";
import {
  loadWellSkySnapshotPatch,
  mergeWellSkySnapshotPatch,
} from "../utils/wellskySnapshotMapper";

// Helper to calculate age
const calculateAge = (dob) => {
  if (!dob) return "";
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  // Handle invalid dates or future dates
  if (isNaN(age)) return "";
  return age >= 0 ? `${age} years` : "";
};

// --- Basic Info Tab ---
const formatDOB = (dob) => {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

const parseDOBInput = (val) => {
  if (!val) return "";
  const raw = String(val).trim();
  if (!raw) return "";

  // Accept MMDDYYYY (e.g. 09211980)
  if (/^\d{8}$/.test(raw)) {
    const m = raw.slice(0, 2);
    const d = raw.slice(2, 4);
    const y = raw.slice(4, 8);
    const mm = Number(m);
    const dd = Number(d);
    const yy = Number(y);
    if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 && yy >= 1900 && yy <= 2100) {
      const date = new Date(`${y}-${m}-${d}T00:00:00Z`);
      if (!isNaN(date.getTime())) return date.toISOString();
    }
  }

  // Accept mm/dd/yyyy (or m/d/yyyy)
  const parts = raw.split("/");
  if (parts.length === 3) {
    const [m, d, y] = parts;
    const date = new Date(`${y}-${m}-${d}T00:00:00Z`);
    if (!isNaN(date.getTime())) return date.toISOString();
  }
  // Accept ISO / other Date-parsable strings
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString();
  return "";
};

const isNonEmptyText = (val) =>
  typeof val === "string" && val.trim().length > 0;

const splitName = (fullName) => {
  const raw = String(fullName || "").trim();
  if (!raw) return { firstName: "", lastName: "" };
  const parts = raw.split(/\s+/).filter(Boolean);
  const firstName = parts.shift() || "";
  const lastName = parts.join(" ");
  return { firstName, lastName };
};

// Resize and compress avatar image to keep payload small (avoids Network Error from large base64)
const compressAvatarImage = (file, maxSize = 400, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      try {
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
};

// Iteratively compress until avatar data URL is small enough for reliable API save.
const compressAvatarForSave = async (file, maxChars = 140000) => {
  let maxSize = 300;
  let quality = 0.7;
  let dataUrl = await compressAvatarImage(file, maxSize, quality);
  let attempts = 0;

  while (dataUrl.length > maxChars && attempts < 8) {
    if (maxSize > 140) {
      maxSize = Math.max(140, maxSize - 30);
    } else {
      quality = Math.max(0.35, quality - 0.08);
    }
    dataUrl = await compressAvatarImage(file, maxSize, quality);
    attempts += 1;
  }

  return dataUrl;
};

// Match AddClientModal strategy: produce a very small square avatar data URL.
const buildTinyAvatarDataUrl = async (dataUrl) => {
  const img = new Image();
  img.decoding = "async";
  img.src = dataUrl;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const sizes = [72, 64, 56, 48];
  const qualities = [0.72, 0.62, 0.52, 0.42];
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const cropSide = Math.min(img.width || 0, img.height || 0);
  const sx = Math.max(0, Math.floor((img.width - cropSide) / 2));
  const sy = Math.max(0, Math.floor((img.height - cropSide) / 2));

  for (const s of sizes) {
    canvas.width = s;
    canvas.height = s;
    ctx.clearRect(0, 0, s, s);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, s, s);
    for (const q of qualities) {
      const out = canvas.toDataURL("image/jpeg", q);
      if (out.length <= 7000) return out;
    }
  }

  canvas.width = 48;
  canvas.height = 48;
  ctx.clearRect(0, 0, 48, 48);
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, 48, 48);
  return canvas.toDataURL("image/jpeg", 0.4);
};

// Re-compress an existing data URL (used for retry when avatar save fails).
const recompressAvatarDataUrl = (dataUrl, maxChars = 90000) =>
  new Promise((resolve, reject) => {
    try {
      if (
        typeof dataUrl !== "string" ||
        !dataUrl.startsWith("data:image/")
      ) {
        resolve(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        try {
          let maxSize = 220;
          let quality = 0.6;
          let attempts = 0;
          let output = dataUrl;

          while (attempts < 8) {
            const canvas = document.createElement("canvas");
            let w = img.width;
            let h = img.height;
            if (w > maxSize || h > maxSize) {
              if (w > h) {
                h = Math.round((h * maxSize) / w);
                w = maxSize;
              } else {
                w = Math.round((w * maxSize) / h);
                h = maxSize;
              }
            }
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            output = canvas.toDataURL("image/jpeg", quality);
            if (output.length <= maxChars) break;

            if (maxSize > 120) {
              maxSize = Math.max(120, maxSize - 20);
            } else {
              quality = Math.max(0.3, quality - 0.08);
            }
            attempts += 1;
          }
          resolve(output);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error("Failed to load avatar data URL"));
      img.src = dataUrl;
    } catch (err) {
      reject(err);
    }
  });

const WellSkyFieldLabel = ({ children, wellsky = false }) => (
  <label className={wellsky ? "wellsky-field-label" : undefined}>
    <span>{children}</span>
    {wellsky ? <span className="wellsky-field-badge">WellSky</span> : null}
  </label>
);

const BasicInfo = ({
  data,
  onChange,
  clientIdDisplay,
  clientMeta,
  disabled,
  renderAsCards,
  hideSocioeconomic = false,
}) => {
  const dobSource = (data?.dob || clientMeta?.dob || "");
  const age = calculateAge(dobSource);
  const dobFocusedRef = useRef(false);
  const [dobText, setDobText] = useState(() => formatDOB(dobSource));
  const CardWrapper = renderAsCards ? "div" : "div";
  const cardClass = renderAsCards ? " client-info-card" : "";

  // Keep input text in sync with stored DOB, but don't fight the user's cursor while typing.
  useEffect(() => {
    if (dobFocusedRef.current) return;
    setDobText(formatDOB(dobSource));
  }, [dobSource]);

  // Debug logging
  console.log("✅ BasicInfo component rendering", {
    hasData: !!data,
    clientId: clientIdDisplay,
    disabled: disabled,
    fieldsEditable: !disabled,
    dob: data?.dob,
    gender: data?.gender,
  });

  const familyList =
    Array.isArray(data.familyMembers) && data.familyMembers.length > 0
      ? data.familyMembers
      : [{ relation: "", contactName: "", phone: "", email: "" }];

  const updateFamilyMember = (index, field, value) => {
    const next = familyList.map((member, i) =>
      i === index ? { ...member, [field]: value } : member,
    );
    onChange("familyMembers", next);
  };

  const addFamilyMember = () => {
    onChange("familyMembers", [
      ...familyList,
      { relation: "", contactName: "", phone: "", email: "" },
    ]);
  };

  const removeFamilyMember = (index) => {
    if (familyList.length === 1) return; // keep at least one visible row
    const next = familyList.filter((_, i) => i !== index);
    onChange("familyMembers", next);
  };

  return (
    <>
      <div className={"tab-pane" + (renderAsCards ? " client-info-cards-inner" : "")}>
        {/* Contact & Identity - matches Edit Client form fields */}
        <div className={"form-section" + cardClass}>
          <h3 className="section-title">Contact &amp; Identity</h3>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={data.firstName ?? clientMeta?.firstName ?? ""}
                onChange={(e) => onChange("firstName", e.target.value)}
                placeholder="Enter first name..."
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={data.lastName ?? clientMeta?.lastName ?? ""}
                onChange={(e) => onChange("lastName", e.target.value)}
                placeholder="Enter last name..."
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                value={data.phone ?? data.phoneNumber ?? ""}
                onChange={(e) => onChange("phone", e.target.value)}
                placeholder="123-456-7890"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Phone 2</label>
              <input
                type="tel"
                value={data.phone2 ?? ""}
                onChange={(e) => onChange("phone2", e.target.value)}
                placeholder="Optional secondary phone"
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Profile Photo (JPG, JPEG, PNG)</label>
            <div className="avatar-upload-row" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <div
                className="avatar-preview-circle"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#e0f2fe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {(data.avatarUrl || data.profileImage || data.photoUrl) ? (
                  <img
                    src={data.avatarUrl || data.profileImage || data.photoUrl}
                    alt="Client"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: "20px", fontWeight: "700", color: "#0284c7" }}>
                    {((data.firstName || data.lastName || "?") + "").trim().charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || disabled) return;
                    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) return;
                    if (file.size > 2 * 1024 * 1024) return;
                    try {
                      // Use same tiny-avatar strategy as Edit Client modal (known-good path).
                      const reader = new FileReader();
                      const src = await new Promise((resolve, reject) => {
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                      });
                      if (typeof src !== "string") throw new Error("Invalid image");
                      let tiny = await buildTinyAvatarDataUrl(src);
                      if (typeof tiny !== "string" || !tiny.startsWith("data:image/")) {
                        tiny = await compressAvatarForSave(file, 100000);
                      }
                      if (typeof tiny === "string") onChange("avatarUrl", tiny);
                    } catch (err) {
                      console.warn("Avatar upload failed", err);
                    }
                    e.target.value = "";
                  }}
                  disabled={disabled}
                />
                {(data.avatarUrl || data.profileImage || data.photoUrl) && (
                  <button
                    type="button"
                    onClick={() => onChange("avatarUrl", "")}
                    disabled={disabled}
                    style={{ marginLeft: 8, fontSize: 12 }}
                  >
                    Remove photo
                  </button>
                )}
                <div className="help-text">Optional. This photo will show in the circle avatar.</div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <WellSkyFieldLabel wellsky>Email address</WellSkyFieldLabel>
            <input
              type="email"
              value={data.email ?? clientMeta?.email ?? ""}
              onChange={(e) => onChange("email", e.target.value)}
              placeholder="name@example.com"
              disabled={disabled}
            />
          </div>
        </div>

        <div className={"form-section" + cardClass}>
          <h3 className="section-title">Personal Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Client ID</label>
              <input type="text" value={clientIdDisplay || ""} readOnly />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="text"
                value={dobText}
                placeholder="mm/dd/yyyy"
                onChange={(e) => setDobText(e.target.value)}
                onFocus={() => {
                  dobFocusedRef.current = true;
                }}
                onBlur={() => {
                  dobFocusedRef.current = false;
                  const raw = (dobText || "").trim();
                  if (!raw) {
                    onChange("dob", "");
                    return;
                  }
                  const iso = parseDOBInput(raw);
                  if (iso) {
                    onChange("dob", iso);
                    // Normalize the display after saving.
                    setDobText(formatDOB(iso));
                  }
                }}
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Age</label>
              <input
                type="text"
                value={age}
                readOnly
                className="read-only-text"
              />
              <span className="help-text">
                Automatically calculated from date of birth
              </span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Gender</label>
              <select
                value={data.gender}
                onChange={(e) => onChange("gender", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Gender</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
            <div className="form-group">
              <label>Ethnic Group</label>
              <select
                value={data.ethnicGroup}
                onChange={(e) => onChange("ethnicGroup", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Ethnic Group</option>
                <option value="White">White</option>
                <option value="Black or African American">
                  Black or African American
                </option>
                <option value="Asian">Asian</option>
                <option value="Hispanic or Latino">Hispanic or Latino</option>
                <option value="Native American">Native American</option>
                <option value="Pacific Islander">Pacific Islander</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <WellSkyFieldLabel wellsky>Primary language</WellSkyFieldLabel>
              <input
                type="text"
                value={data.primaryLanguage || ""}
                onChange={(e) => onChange("primaryLanguage", e.target.value)}
                placeholder="English, Spanish, etc."
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {!hideSocioeconomic && (
          <div className={"form-section" + cardClass}>
            <h3 className="section-title">Socioeconomic Status & Living</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Household Income</label>
                <select
                  value={data.householdIncome}
                  onChange={(e) => onChange("householdIncome", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select Income Range</option>
                  <option value="Under $25,000">Under $25,000</option>
                  <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                  <option value="$50,000 - $75,000">$50,000 - $75,000</option>
                  <option value="$75,000 - $100,000">$75,000 - $100,000</option>
                  <option value="Over $100,000">Over $100,000</option>
                </select>
              </div>
              <div className="form-group">
                <label>Housing Situation</label>
                <select
                  value={data.housingSituation}
                  onChange={(e) => onChange("housingSituation", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select Housing</option>
                  <option value="I own my home">I own my home</option>
                  <option value="Renting">Renting</option>
                  <option value="Living with family">Living with family</option>
                  <option value="Assisted living">Assisted living</option>
                  <option value="House">House</option>
                  <option value="Apartment/Condo">Apartment/Condo</option>
                  <option value="Independent Living (Facility)">
                    Independent Living (Facility)
                  </option>
                  <option value="Assisted Living">Assisted Living</option>
                  <option value="Memory Care">Memory Care</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Marital Status</label>
                <select
                  value={data.maritalStatus}
                  onChange={(e) => onChange("maritalStatus", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select Status</option>
                  <option value="Married">Married</option>
                  <option value="Single">Single</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                  <option value="Domestic Partner">Domestic Partner</option>
                </select>
              </div>
              <div className="form-group">
                <label>Minors Under 18</label>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={data.minorsUnder18}
                  onChange={(e) => onChange("minorsUnder18", e.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="form-group">
                <label>Veteran Status</label>
                <select
                  value={data.veteranStatus}
                  onChange={(e) => onChange("veteranStatus", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select Status</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="form-group">
                <label>Home Assistance</label>
                <select
                  value={data.homeAssistance}
                  onChange={(e) => onChange("homeAssistance", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">N/A</option>
                  <option value="Alone">Alone</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Family Member(s)">Family Member(s)</option>
                  <option value="Friend">Friend</option>
                </select>
              </div>
              <div className="form-group">
                <label>Transportation Access</label>
                <select
                  value={data.transportation}
                  onChange={(e) => onChange("transportation", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">N/A</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div className="form-group">
                <WellSkyFieldLabel wellsky>Household composition</WellSkyFieldLabel>
                <input
                  type="text"
                  value={data.householdComposition || ""}
                  onChange={(e) => onChange("householdComposition", e.target.value)}
                  placeholder="Who lives in the home"
                  disabled={disabled}
                />
              </div>
              <div className="form-group">
                <label>Pet Ownership</label>
                <select
                  value={data.petOwnership}
                  onChange={(e) => onChange("petOwnership", e.target.value)}
                  disabled={disabled}
                >
                  <option value="">N/A</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={"form-section" + cardClass}>
          <h3 className="section-title">Family Information</h3>
          <p
            className="muted"
            style={{ marginTop: "-6px", marginBottom: "10px" }}
          >
            Add family members or emergency contacts. At least one entry is
            shown by default.
          </p>
          <div className="family-list">
            {familyList.map((member, index) => (
              <div key={index} className="family-card">
                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Name</label>
                    <input
                      type="text"
                      value={member.contactName}
                      onChange={(e) =>
                        updateFamilyMember(index, "contactName", e.target.value)
                      }
                      placeholder="Full name"
                      disabled={disabled}
                    />
                  </div>
                  <div className="form-group">
                    <label>Relation</label>
                    <input
                      type="text"
                      value={member.relation}
                      onChange={(e) =>
                        updateFamilyMember(index, "relation", e.target.value)
                      }
                      placeholder="Spouse, Son, Daughter, Emergency Contact"
                      disabled={disabled}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={member.phone}
                      onChange={(e) =>
                        updateFamilyMember(index, "phone", e.target.value)
                      }
                      placeholder="(555) 555-5555"
                      disabled={disabled}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={member.email}
                      onChange={(e) =>
                        updateFamilyMember(index, "email", e.target.value)
                      }
                      placeholder="name@example.com"
                      disabled={disabled}
                    />
                  </div>
                </div>

                {!disabled && familyList.length > 1 && (
                  <div className="form-row">
                    <button
                      type="button"
                      className="button danger-button"
                      onClick={() => removeFamilyMember(index)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {!disabled && (
            <button
              type="button"
              className="button secondary-button"
              style={{ marginTop: "10px" }}
              onClick={addFamilyMember}
            >
              + Add Family Member
            </button>
          )}
        </div>

        <div className={"form-section" + cardClass}>
          <h3 className="section-title">Location</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Street Address *</label>
              <input
                type="text"
                value={data.street ?? clientMeta?.street ?? ""}
                onChange={(e) => onChange("street", e.target.value)}
                placeholder="123 Main St"
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={data.city ?? clientMeta?.city ?? ""}
                onChange={(e) => onChange("city", e.target.value)}
                placeholder="City"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                value={data.state ?? clientMeta?.state ?? ""}
                onChange={(e) => onChange("state", e.target.value)}
                placeholder="State"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Zip *</label>
              <input
                type="text"
                value={data.zip ?? clientMeta?.zip ?? ""}
                onChange={(e) => onChange("zip", e.target.value)}
                placeholder="Zip"
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-group">
            <WellSkyFieldLabel wellsky>Mailing address (if different)</WellSkyFieldLabel>
            <input
              type="text"
              value={data.mailingAddress || ""}
              onChange={(e) => onChange("mailingAddress", e.target.value)}
              placeholder="Optional mailing address"
              disabled={disabled}
            />
          </div>
        </div>

      </div>

      {/* Save Confirmation Modal */}
    </>
  );
};

// --- Health Metrics Tab ---
const HealthMetrics = ({ data, onChange, disabled }) => {
  console.log("🩺 HealthMetrics rendering, disabled:", disabled);
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const onBlurClamp =
    (field, { min, max, decimals = null } = {}) =>
    (e) => {
      const raw = (e?.target?.value ?? "").toString().trim();
      if (raw === "") {
        onChange(field, "");
        return;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const clamped = clamp(num, min, max);
      const formatted =
        typeof decimals === "number"
          ? clamped.toFixed(decimals)
          : String(Math.round(clamped));
      onChange(field, formatted);
    };
  const [conditionOptions, setConditionOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadConditionOptions = async () => {
      try {
        const response = await fetch("/diagnosed-conditions.json");
        if (!response.ok) return;
        const payload = await response.json();
        const list = Array.isArray(payload?.conditions) ? payload.conditions : [];
        if (isMounted) setConditionOptions(list);
      } catch (err) {
        console.warn("Failed to load diagnosed conditions list", err);
      }
    };

    loadConditionOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const supplementOptions = [
    { key: "supplementMultivitamin", label: "Multivitamin" },
    { key: "supplementVitaminD", label: "Vitamin D" },
    { key: "supplementCalcium", label: "Calcium" },
    { key: "supplementVitaminB12", label: "Vitamin B12" },
    { key: "supplementFishOilOmega3", label: "Fish oil / Omega-3" },
    { key: "supplementMagnesium", label: "Magnesium" },
    { key: "supplementProbiotics", label: "Probiotics" },
    { key: "supplementHerbal", label: "Herbal supplements" },
    { key: "supplementProtein", label: "Protein supplements" },
    {
      key: "supplementJoint",
      label: "Joint supplements (glucosamine, etc.)",
    },
    { key: "supplementSleepAids", label: "Sleep aids (melatonin)" },
    { key: "supplementImmuneBoosters", label: "Immune boosters" },
    { key: "supplementOther", label: "Other" },
  ];

  return (
    <div className="tab-pane">
      <h3 className="section-header-red">
        <FontAwesomeIcon icon={faPlusSquare} /> Vital Signs
      </h3>

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faThermometerHalf} className="red-icon" />{" "}
            Body Temperature
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              step="0.1"
              min="95"
              max="104"
              value={data.bodyTemp}
              onChange={(e) => onChange("bodyTemp", e.target.value)}
              onBlur={onBlurClamp("bodyTemp", {
                min: 95,
                max: 104,
                decimals: 1,
              })}
              disabled={disabled}
            />
            <span className="unit">°F</span>
          </div>
          <div className="metric-range">range: 95°F - 104°F</div>
        </div>
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faHeartbeat} className="red-icon" /> Resting
            Heart Rate
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              step="1"
              min="50"
              max="130"
              value={data.heartRate}
              onChange={(e) => onChange("heartRate", e.target.value)}
              onBlur={onBlurClamp("heartRate", { min: 50, max: 130 })}
              disabled={disabled}
            />
            <span className="unit">BPM</span>
          </div>
          <div className="metric-range">Normal range: 50-130 BPM</div>
        </div>
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faLungs} className="blue-icon" /> Respiratory
            Rate
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              step="1"
              min="8"
              max="30"
              value={data.respiratoryRate}
              onChange={(e) => onChange("respiratoryRate", e.target.value)}
              onBlur={onBlurClamp("respiratoryRate", { min: 8, max: 30 })}
              disabled={disabled}
            />
            <span className="unit">breaths/min</span>
          </div>
          <div className="metric-range">
            Normal range: 8-30 breaths/min
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faTint} className="red-icon" /> Blood
            Pressure
          </div>
          <div className="bp-status-box">
            {[
              "NORMAL",
              "ELEVATED",
              "STAGE 1 HYPERTENSION",
              "STAGE 2 HYPERTENSION",
              "HYPERTENSIVE CRISIS",
            ].map((status) => (
              <div
                key={status}
                className={`bp-option ${
                  data.bpStatus === status ? "selected" : ""
                } ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && onChange("bpStatus", status)}
              >
                {status}
              </div>
            ))}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faTachometerAlt} className="red-icon" />{" "}
            Oxygen Saturation
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              step="0.01"
              min="0.65"
              max="1"
              value={data.oxygenSat}
              onChange={(e) => onChange("oxygenSat", e.target.value)}
              onBlur={onBlurClamp("oxygenSat", {
                min: 0.65,
                max: 1,
                decimals: 2,
              })}
              disabled={disabled}
            />
            <span className="unit">SpO₂</span>
          </div>
          <div className="metric-range">Normal range: 0.65 - 1.00</div>
        </div>
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faWeight} className="green-icon" /> Weight
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              step="0.1"
              min="0"
              max="1000"
              value={data.weight}
              onChange={(e) => onChange("weight", e.target.value)}
              onBlur={onBlurClamp("weight", { min: 0, max: 1000, decimals: 1 })}
              disabled={disabled}
            />
            <span className="unit">lbs</span>
          </div>
        </div>
      </div>

      <h3 className="section-header-blue">
        <FontAwesomeIcon icon={faClipboardList} /> Additional Biometrics
      </h3>
      <div className="form-row two-col">
        <div className="form-group">
          <label>Body Mass Index</label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={data.bodyMassIndex || ""}
            onChange={(e) => onChange("bodyMassIndex", e.target.value)}
            onBlur={onBlurClamp("bodyMassIndex", { min: 0, max: 100, decimals: 1 })}
            disabled={disabled}
          />
        </div>
        <div className="form-group">
          <label>Total Cholesterol</label>
          <input
            type="number"
            step="1"
            min="0"
            value={data.totalCholesterol || ""}
            onChange={(e) => onChange("totalCholesterol", e.target.value)}
            onBlur={onBlurClamp("totalCholesterol", { min: 0, max: 500 })}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-row two-col">
        <div className="form-group">
          <label>LDL Cholesterol</label>
          <input
            type="number"
            step="1"
            min="0"
            value={data.ldlCholesterol || ""}
            onChange={(e) => onChange("ldlCholesterol", e.target.value)}
            onBlur={onBlurClamp("ldlCholesterol", { min: 0, max: 300 })}
            disabled={disabled}
          />
        </div>
        <div className="form-group">
          <label>HDL Cholesterol</label>
          <input
            type="number"
            step="1"
            min="0"
            value={data.hdlCholesterol || ""}
            onChange={(e) => onChange("hdlCholesterol", e.target.value)}
            onBlur={onBlurClamp("hdlCholesterol", { min: 0, max: 200 })}
            disabled={disabled}
          />
        </div>
      </div>

      <h3 className="section-header-blue">
        <FontAwesomeIcon icon={faClipboardList} /> Diagnosed Medical
        Conditions
      </h3>
      <datalist id="diagnosed-condition-options">
        {conditionOptions.map((condition) => (
          <option key={condition} value={condition} />
        ))}
      </datalist>
      {[1, 2, 3, 4].map((idx) => (
        <div className="form-row two-col" key={`diagnosed-condition-row-${idx}`}>
          <div className="form-group">
            <label>{`Condition ${idx}`}</label>
            <input
              type="text"
              list="diagnosed-condition-options"
              value={data[`diagnosedCondition${idx}`] || ""}
              onChange={(e) =>
                onChange(`diagnosedCondition${idx}`, e.target.value)
              }
              disabled={disabled}
              placeholder="Start typing to search conditions..."
            />
          </div>
          <div className="form-group">
            <label>{`Date of Diagnosis ${idx}`}</label>
            <input
              type="date"
              value={data[`diagnosedConditionDate${idx}`] || ""}
              onChange={(e) =>
                onChange(`diagnosedConditionDate${idx}`, e.target.value)
              }
              disabled={disabled}
            />
          </div>
        </div>
      ))}

      <h3 className="section-header-blue">
        <FontAwesomeIcon icon={faClipboardList} /> Medication & Allergies
      </h3>
      <div className="form-group">
        <label>Medication Allergies</label>
        <textarea
          rows="3"
          className="form-textarea"
          value={data.medicationAllergies || ""}
          onChange={(e) => onChange("medicationAllergies", e.target.value)}
          disabled={disabled}
        />
      </div>

      {[1, 2, 3, 4, 5].map((idx) => (
        <div className="form-group" key={`prescribed-medication-${idx}`}>
          <label>{`Prescribed Medication ${idx}`}</label>
          <input
            type="text"
            value={data[`prescribedMedication${idx}`] || ""}
            onChange={(e) =>
              onChange(`prescribedMedication${idx}`, e.target.value)
            }
            disabled={disabled}
          />
        </div>
      ))}

      <div className="form-group">
        <label>Medication Adherence</label>
        <select
          value={data.medicationAdherence || ""}
          onChange={(e) => onChange("medicationAdherence", e.target.value)}
          disabled={disabled}
        >
          <option value="">Select Adherence</option>
          <option value="Always (100%)">Always (100%)</option>
          <option value="Almost always (miss 1-2 doses per month)">
            Almost always (miss 1-2 doses per month)
          </option>
          <option value="Sometimes (miss 1-2 doses per week)">
            Sometimes (miss 1-2 doses per week)
          </option>
          <option value="Often miss doses">Often miss doses</option>
          <option value="Rarely take as prescribed">
            Rarely take as prescribed
          </option>
          <option value="Not currently on medications">
            Not currently on medications
          </option>
        </select>
      </div>

      <div className="form-group">
        <label>Dietary Supplements</label>
        <select
          value={data.dietarySupplements || ""}
          onChange={(e) => onChange("dietarySupplements", e.target.value)}
          disabled={disabled}
        >
          <option value="">Select</option>
          <option value="No">No</option>
          <option value="Yes - Occasionally">Yes - Occasionally</option>
          <option value="Yes - Daily">Yes - Daily</option>
        </select>
      </div>

      <div className="form-group">
        <label>Types of Supplements</label>
        <div className="supplement-grid">
          {supplementOptions.map((opt) => (
            <label
              key={opt.key}
              className={`supplement-chip ${
                Boolean(data[opt.key]) ? "selected" : ""
              } ${disabled ? "disabled" : ""}`}
            >
              <input
                type="checkbox"
                className="supplement-checkbox"
                checked={Boolean(data[opt.key])}
                onChange={(e) => onChange(opt.key, e.target.checked)}
                disabled={disabled}
              />
              <span className="supplement-chip-label">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Food Allergies</label>
        <textarea
          rows="3"
          className="form-textarea"
          value={data.foodAllergies || ""}
          onChange={(e) => onChange("foodAllergies", e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* <h3 className="section-header-blue">
        <FontAwesomeIcon icon={faVolumeUp} /> Vocal Energy Metrics
      </h3> */}
      {/* <div className="metrics-inline">
        <div className="metric-card compact">
          <div className="metric-header">
            <FontAwesomeIcon icon={faVolumeUp} className="blue-icon" /> Power
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              value={data.vocalPower}
              onChange={(e) => onChange("vocalPower", e.target.value)}
              disabled={disabled}
            />
            <span className="unit">/100</span>
          </div>
        </div>
        <div className="metric-card compact">
          <div className="metric-header">
            <FontAwesomeIcon icon={faTachometerAlt} className="blue-icon" />{" "}
            Speed
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              value={data.vocalSpeed}
              onChange={(e) => onChange("vocalSpeed", e.target.value)}
              disabled={disabled}
            />
            <span className="unit">/100</span>
          </div>
        </div>
        <div className="metric-card compact">
          <div className="metric-header">
            <FontAwesomeIcon icon={faHeartbeat} className="blue-icon" /> Dynamic
          </div>
          <div className="metric-input-group">
            <input
              type="number"
              value={data.vocalDynamic}
              onChange={(e) => onChange("vocalDynamic", e.target.value)}
              disabled={disabled}
            />
            <span className="unit">/100</span>
          </div>
        </div>
        <div className="metric-card compact">
          <div className="metric-header">
            <FontAwesomeIcon icon={faFrown} className="orange-icon" />{" "}
            Depression
          </div>
          <div className="level-selector inline">
            {["Low", "Medium", "High"].map((level) => (
              <div
                key={level}
                className={`level-opt ${level.toLowerCase()} ${
                  data.vocalDepression === level ? "selected" : ""
                } ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && onChange("vocalDepression", level)}
              >
                {level}
              </div>
            ))}
          </div>
        </div>
        <div className="metric-card compact">
          <div className="metric-header">
            <FontAwesomeIcon icon={faFrown} className="red-icon" /> Anxiety
          </div>
          <div className="level-selector inline">
            {["Low", "Medium", "High"].map((level) => (
              <div
                key={level}
                className={`level-opt ${level.toLowerCase()} ${
                  data.vocalAnxiety === level ? "selected" : ""
                } ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && onChange("vocalAnxiety", level)}
              >
                {level}
              </div>
            ))}
          </div>
        </div>
      </div> */}
    </div>
  );
};

const SnapshotTextArea = ({
  label,
  name,
  value,
  onChange,
  disabled,
  wellsky = false,
  rows = 3,
}) => (
  <div className="form-group">
    <WellSkyFieldLabel wellsky={wellsky}>{label}</WellSkyFieldLabel>
    <textarea
      rows={rows}
      className="form-textarea"
      value={value || ""}
      onChange={(e) => onChange(name, e.target.value)}
      disabled={disabled}
    />
  </div>
);

const SnapshotTextField = ({
  label,
  name,
  value,
  onChange,
  disabled,
  wellsky = false,
  type = "text",
  placeholder = "",
}) => (
  <div className="form-group">
    <WellSkyFieldLabel wellsky={wellsky}>{label}</WellSkyFieldLabel>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(name, e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    />
  </div>
);

const ReferralInformation = ({ data, onChange, disabled }) => (
  <div className="tab-pane client-info-section">
    <div className="client-info-section-header">
      <div>
        <h3 className="client-info-section-title">Referral Information</h3>
        <p className="client-info-section-subtitle">
          Intake and referral details from WellSky when linked
        </p>
      </div>
    </div>
    <div className="fields">
      <SnapshotTextField
        label="Referral source (hospital, physician, family, self-referral, etc.)"
        name="referralSource"
        value={data.referralSource}
        onChange={onChange}
        disabled={disabled}
        wellsky
      />
      <div className="form-row two-col">
        <SnapshotTextField
          label="Referral date"
          name="referralDate"
          type="date"
          value={data.referralDate}
          onChange={onChange}
          disabled={disabled}
          wellsky
        />
        <SnapshotTextField
          label="Admission date"
          name="admissionDate"
          type="date"
          value={data.admissionDate}
          onChange={onChange}
          disabled={disabled}
          wellsky
        />
      </div>
      <SnapshotTextField
        label="Discharge date (when applicable)"
        name="dischargeDate"
        type="date"
        value={data.dischargeDate}
        onChange={onChange}
        disabled={disabled}
        wellsky
      />
      <SnapshotTextField
        label="Case manager assignment"
        name="caseManagerAssignment"
        value={data.caseManagerAssignment}
        onChange={onChange}
        disabled={disabled}
        wellsky
      />
      <SnapshotTextField
        label="Branch/office location"
        name="branchOfficeLocation"
        value={data.branchOfficeLocation}
        onChange={onChange}
        disabled={disabled}
        wellsky
      />
      <div className="form-row two-col">
        <SnapshotTextField
          label="Primary payer (Medicare, Medicaid, private insurance)"
          name="primaryPayer"
          value={data.primaryPayer}
          onChange={onChange}
          disabled={disabled}
          wellsky
        />
        <SnapshotTextField
          label="Secondary/tertiary payer"
          name="secondaryTertiaryPayer"
          value={data.secondaryTertiaryPayer}
          onChange={onChange}
          disabled={disabled}
          wellsky
        />
      </div>
    </div>
  </div>
);

const CareInformation = ({ data, onChange, disabled }) => (
  <div className="tab-pane client-info-section">
    <div className="client-info-section-header">
      <div>
        <h3 className="client-info-section-title">Care Information</h3>
        <p className="client-info-section-subtitle">
          Care plan, services, and coordination details from WellSky when linked
        </p>
      </div>
    </div>
    <div className="fields">
      <SnapshotTextArea label="Care goals" name="careGoals" value={data.careGoals} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Services ordered (nursing, PT, OT, speech therapy, aide services, etc.)" name="servicesOrdered" value={data.servicesOrdered} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextField label="Frequency and duration of services" name="frequencyDurationOfServices" value={data.frequencyDurationOfServices} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextField label="Care level/acuity" name="careLevelAcuity" value={data.careLevelAcuity} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Functional limitations (mobility, ADLs)" name="functionalLimitations" value={data.functionalLimitations} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Special instructions/precautions (fall risk, infection control, etc.)" name="specialInstructionsPrecautions" value={data.specialInstructionsPrecautions} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Initial assessment results" name="initialAssessmentResults" value={data.initialAssessmentResults} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="OASIS data (for Medicare-certified home health)" name="oasisData" value={data.oasisData} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Reassessment findings" name="reassessmentFindings" value={data.reassessmentFindings} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Wound assessments (if applicable)" name="woundAssessments" value={data.woundAssessments} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextField label="Preferred visit days/times" name="preferredVisitDaysTimes" value={data.preferredVisitDaysTimes} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextField label="Caregiver preferences (gender, language, etc.)" name="caregiverPreferences" value={data.caregiverPreferences} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Primary caregiver (family member) details" name="primaryCaregiverDetails" value={data.primaryCaregiverDetails} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Case notes" name="caseNotes" value={data.caseNotes} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Family communication records" name="familyCommunicationRecords" value={data.familyCommunicationRecords} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Incident reports specific to the client" name="incidentReportsClient" value={data.incidentReportsClient} onChange={onChange} disabled={disabled} wellsky />
      <SnapshotTextArea label="Satisfaction survey responses" name="satisfactionSurveyResponses" value={data.satisfactionSurveyResponses} onChange={onChange} disabled={disabled} wellsky />
    </div>
  </div>
);

const MedicalHistory = ({ data, onChange, disabled }) => (
  <div className="tab-pane client-info-section">
    <div className="client-info-section-header">
      <div>
        <h3 className="client-info-section-title">Medical History</h3>
        <p className="client-info-section-subtitle">
          Longitudinal medical history and physician records from WellSky when linked
        </p>
      </div>
    </div>
    <div className="fields">
      <SnapshotTextArea label="Medical history" name="medicalHistorySummary" value={data.medicalHistorySummary} onChange={onChange} disabled={disabled} wellsky rows={5} />
      <SnapshotTextArea label="Physician(s) of record — name, contact, NPI number" name="physiciansOfRecord" value={data.physiciansOfRecord} onChange={onChange} disabled={disabled} wellsky rows={4} />
      <SnapshotTextArea label="Advance directives (DNR, living will status)" name="advanceDirectives" value={data.advanceDirectives} onChange={onChange} disabled={disabled} wellsky rows={3} />
    </div>
  </div>
);

// --- Assessment Row Component ---
const AssessmentRow = ({
  title,
  status = "not-assessed",
  onStatusChange,
  disabled,
  tooltipText,
  wellsky = false,
}) => (
  <div className="assessment-row">
    <div className="assessment-title assessment-title-wellsky">
      {title}
      {wellsky ? <span className="wellsky-field-badge">WellSky</span> : null}
      {tooltipText ? (
        <span className="tooltip-wrap">
          <FontAwesomeIcon icon={faQuestionCircle} className="info-icon" />
          <span className="tooltip-bubble">
            {tooltipText}
            <span className="tooltip-arrow" />
          </span>
        </span>
      ) : (
        <FontAwesomeIcon icon={faQuestionCircle} className="info-icon" />
      )}
    </div>
    <div className="assessment-options">
      <button
        className={`assess-btn not-assessed ${
          status === "not-assessed" ? "active" : ""
        }`}
        onClick={() => onStatusChange("not-assessed")}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faHourglassHalf} /> NOT ASSESSED
      </button>
      <button
        className={`assess-btn dependent ${
          status === "dependent" ? "active" : ""
        }`}
        onClick={() => onStatusChange("dependent")}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faTimesCircle} /> DEPENDENT
      </button>
      <button
        className={`assess-btn assistance ${
          status === "assistance" ? "active" : ""
        }`}
        onClick={() => onStatusChange("assistance")}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faQuestionCircle} /> NEEDS ASSISTANCE
      </button>
      <button
        className={`assess-btn independent ${
          status === "independent" ? "active" : ""
        }`}
        onClick={() => onStatusChange("independent")}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faCheckCircle} /> INDEPENDENT
      </button>
    </div>
  </div>
);

// --- ADL Assessment Tab ---
const AdlAssessment = ({ data, onChange, disabled }) => {
  console.log("🚶 ADL Assessment rendering, disabled:", disabled);

  return (
    <div className="tab-pane adl-tab">
      <div className="assessment-list">
        <AssessmentRow
          title="Bathing"
          status={data.adlBathing}
          onStatusChange={(s) => onChange("adlBathing", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "adlBathing")}
          tooltipText="Bathing: Cleaning oneself (whole body)."
        />
        <AssessmentRow
          title="Dressing"
          status={data.adlDressing}
          onStatusChange={(s) => onChange("adlDressing", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "adlDressing")}
          tooltipText="Dressing: Putting on and taking off clothing."
        />
        <AssessmentRow
          title="Eating"
          status={data.adlEating || data.adlFeeding}
          onStatusChange={(s) => {
            onChange("adlEating", s);
            onChange("adlFeeding", s); // back-compat
          }}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "adlEating")}
          tooltipText="Eating: Getting food from plate to mouth (not food prep)."
        />
        <AssessmentRow
          title="Transferring"
          status={data.adlTransferring || data.adlMobility}
          onStatusChange={(s) => {
            onChange("adlTransferring", s);
            onChange("adlMobility", s); // back-compat
          }}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "adlTransferring")}
          tooltipText="Transferring: Moving between bed, chair, or wheelchair."
        />
        <AssessmentRow
          title="Toileting"
          status={data.adlToileting || data.adlRestroom}
          onStatusChange={(s) => {
            onChange("adlToileting", s);
            onChange("adlRestroom", s); // back-compat
          }}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "adlToileting")}
          tooltipText="Toileting: Getting to the toilet and cleaning up."
        />
        <AssessmentRow
          title="Continence"
          status={data.adlContinence}
          onStatusChange={(s) => onChange("adlContinence", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "adlContinence")}
          tooltipText="Continence: Controlling bladder and bowel functions."
        />
      </div>
    </div>
  );
};

// --- IADL Assessment Tab ---
const IadlAssessment = ({ data, onChange, disabled }) => {
  console.log("🏠 IADL Assessment rendering, disabled:", disabled);

  return (
    <div className="tab-pane iadl-tab">
      <div className="assessment-list">
        <AssessmentRow
          title="Meal Preparation"
          status={data.iadlMealPreparation}
          onStatusChange={(s) => onChange("iadlMealPreparation", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "iadlMealPreparation")}
          tooltipText="Meal Preparation: Planning menus, buying groceries, cooking, and cleaning up."
        />
        <AssessmentRow
          title="Managing Finances"
          status={data.iadlFinances}
          onStatusChange={(s) => onChange("iadlFinances", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "iadlFinances")}
          tooltipText="Managing Finances: Paying bills, budgeting, handling money, and managing investments."
        />
        <AssessmentRow
          title="Housekeeping & Laundry"
          status={data.iadlHousekeepingLaundry}
          onStatusChange={(s) => onChange("iadlHousekeepingLaundry", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "iadlHousekeepingLaundry")}
          tooltipText="Housekeeping & Laundry: Cleaning, doing dishes, tidying up, and washing clothes."
        />
        <AssessmentRow
          title="Shopping"
          status={data.iadlShopping}
          onStatusChange={(s) => onChange("iadlShopping", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "iadlShopping")}
          tooltipText="Shopping: Buying groceries, medications, and other necessities."
        />
        <AssessmentRow
          title="Medication Management"
          status={data.iadlMedication}
          onStatusChange={(s) => onChange("iadlMedication", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "iadlMedication")}
          tooltipText="Medication Management: Taking medicines correctly, refilling prescriptions, and monitoring side effects."
        />
        <AssessmentRow
          title="Transportation"
          status={data.iadlTransportation}
          onStatusChange={(s) => onChange("iadlTransportation", s)}
          disabled={disabled}
          wellsky={isAdlUiFieldFromWellSky(data, "iadlTransportation")}
          tooltipText="Transportation: Driving, using public transit, or arranging rides to appointments and errands."
        />
        <AssessmentRow
          title="Communication"
          status={data.iadlCommunication}
          onStatusChange={(s) => onChange("iadlCommunication", s)}
          disabled={disabled}
          tooltipText="Communication: Using the phone, email, and other devices to stay connected."
        />
        <AssessmentRow
          title="Home Maintenance"
          status={data.iadlHomeMaintenance}
          onStatusChange={(s) => onChange("iadlHomeMaintenance", s)}
          disabled={disabled}
          tooltipText="Home Maintenance: Basic upkeep and repairs."
        />
      </div>
    </div>
  );
};

// --- Grip Strength / Gait Speed — Able Care falls-risk screening ---
const GripStrengthGaitSpeed = ({ data, clientId, showSampleData, canEdit, onSaveManual }) => (
  <div className="tab-pane">
    <AbleCareSection
      clientId={clientId}
      dob={data.dob || data.dateOfBirth}
      gender={data.gender}
      showSampleData={showSampleData}
      manualValues={{
        gripStrengthLbs: data.gripStrengthLbs,
        gripLeftLbs: data.gripLeftLbs,
        gripRightLbs: data.gripRightLbs,
        chairStand: data.chairStand,
        gaitSpeedMs: data.gaitSpeedMs,
        timedUpAndGo: data.timedUpAndGo,
      }}
      canEdit={canEdit}
      onSaveManual={onSaveManual}
    />
  </div>
);

const ImportantClientInfoSummary = ({
  data = {},
  clientMeta = {},
  clientId,
  currentAipScore = 0,
}) => {
  const ageText = calculateAge(data.dob || data.dateOfBirth) || "N/A";
  const addressText =
    data.address ||
    [data.street, data.city, data.state, data.zip].filter(Boolean).join(", ") ||
    "N/A";
  const clientIdText =
    clientMeta.clientNumber ??
    clientMeta.clientId ??
    clientMeta._id ??
    clientId ??
    "N/A";
  const phone1Text = data.phone || data.phoneNumber || "N/A";
  const phone2Text = data.phone2 || "N/A";
  const dnrRaw = data.dnrOnFile ?? data.dnr ?? data.doNotResuscitate;
  const dnrText =
    dnrRaw === true || String(dnrRaw || "").toLowerCase() === "yes"
      ? "Yes"
      : "No";

  const emergencyContacts = (Array.isArray(data.familyMembers) ? data.familyMembers : [])
    .filter((m) => m && (m.contactName || m.relation || m.phone || m.email))
    .map((m, idx) => ({
      id: idx + 1,
      contactName: m.contactName || "N/A",
      relation: m.relation || "N/A",
      phone: m.phone || "N/A",
      email: m.email || "N/A",
    }));

  const medicalConditions = [1, 2, 3, 4].map((idx) => ({
    id: idx,
    condition: data[`diagnosedCondition${idx}`] || "N/A",
    severity: data[`diagnosedConditionSeverity${idx}`] || "N/A",
  }));

  const Row = ({ label, value }) => (
    <div className="important-info-row">
      <div className="important-info-label">{label}</div>
      <div className="important-info-value">{value || "N/A"}</div>
    </div>
  );

  return (
    <div className="important-info-summary">
      <div className="important-info-section">
        <h3 className="important-info-title">Core Information</h3>
        <Row label="First Name" value={data.firstName || clientMeta.firstName || "N/A"} />
        <Row label="Last Name" value={data.lastName || clientMeta.lastName || "N/A"} />
        <Row label="Client ID" value={clientIdText} />
        <Row label="Current AIP Score" value={String(currentAipScore ?? 0)} />
        <Row label="Address" value={addressText} />
        <Row label="Phone Number 1" value={phone1Text} />
        <Row label="Phone Number 2" value={phone2Text} />
        <Row label="DOB" value={formatDOB(data.dob || data.dateOfBirth) || "N/A"} />
        <Row label="Age" value={ageText} />
        <Row label="Gender" value={data.gender || "N/A"} />
      </div>

      <div className="important-info-section">
        <h3 className="important-info-title">Emergency Contacts</h3>
        {emergencyContacts.length === 0 ? (
          <div className="important-info-empty">No emergency contacts listed.</div>
        ) : (
          emergencyContacts.map((c) => (
            <div key={`em-${c.id}`} className="important-info-contact-card">
              <div className="important-info-contact-head">{c.contactName}</div>
              <Row label="Relationship" value={c.relation} />
              <Row label="Phone" value={c.phone} />
              <Row label="Email" value={c.email} />
            </div>
          ))
        )}
      </div>

      <div className="important-info-section">
        <h3 className="important-info-title">Medical Conditions</h3>
        {medicalConditions.map((m) => (
          <div key={`mc-${m.id}`} className="important-info-condition-card">
            <div className="important-info-condition-head">Condition {m.id}</div>
            <Row label={`Medical Condition ${m.id}`} value={m.condition} />
            <Row label={`Severity ${m.id}`} value={m.severity} />
          </div>
        ))}
        <Row label="Food Allergies" value={data.foodAllergies || "N/A"} />
        <Row label="Medication Allergies" value={data.medicationAllergies || "N/A"} />
        <Row label="Do Not Resuscitate (DNR) on file" value={dnrText} />
      </div>
    </div>
  );
};

// --- Living Conditions Tab ---
const LivingConditions = ({ data, onChange, disabled }) => {
  console.log("🏡 Living Conditions rendering, disabled:", disabled);

  return (
    <div className="tab-pane">
      <div className="two-col-layout">
        {/* Left Column: Physical Health */}
        <div className="column-panel">
          <div className="form-group">
            <label>BMI Category</label>
            <select
              value={data.bmiCategory}
              onChange={(e) => onChange("bmiCategory", e.target.value)}
              disabled={disabled}
            >
              <option value="">Select BMI Category</option>
              <option value="Underweight">Underweight</option>
              <option value="Normal Weight">Normal Weight</option>
              <option value="Overweight">Overweight</option>
              <option value="Obese">Obese</option>
            </select>
          </div>
          <div className="form-group">
            <label>Weight Change Status</label>
            <select
              value={data.weightChange}
              onChange={(e) => onChange("weightChange", e.target.value)}
              disabled={disabled}
            >
              <option value="">Select Weight Change</option>
              <option value="Ideal (Stable)">Ideal (Stable)</option>
              <option value="Loss">Loss</option>
              <option value="Gain">Gain</option>
            </select>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Bed Bound</label>
              <select
                value={data.bedBound}
                onChange={(e) => onChange("bedBound", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Client Transfer Device</label>
              <select
                value={data.transferDevice}
                onChange={(e) => onChange("transferDevice", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option value="Hoyer Lift">Hoyer Lift</option>
                <option value="Gait Belt">Gait Belt</option>
                <option value="Transfer Board">Transfer Board</option>
                <option value="No Transfer Device Used">
                  No Transfer Device Used
                </option>
              </select>
            </div>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Right Hand Grip Strength</label>
              <select
                value={data.rightGrip}
                onChange={(e) => onChange("rightGrip", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Grip</option>
                <option value="Firm">Firm</option>
                <option value="Weak">Weak</option>
              </select>
            </div>
            <div className="form-group">
              <label>Left Hand Grip Strength</label>
              <select
                value={data.leftGrip}
                onChange={(e) => onChange("leftGrip", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Grip</option>
                <option value="Firm">Firm</option>
                <option value="Weak">Weak</option>
              </select>
            </div>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Gait Speed</label>
              <select
                value={data.gaitSpeed}
                onChange={(e) => onChange("gaitSpeed", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Speed</option>
                <option value="Ideal">Ideal</option>
                <option value="Slow">Slow</option>
              </select>
            </div>
            <div className="form-group">
              <label>Mobility Device</label>
              <select
                value={data.mobilityDevice}
                onChange={(e) => onChange("mobilityDevice", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Device</option>
                <option value="Free Gait">Free Gait</option>
                <option value="Cane">Cane</option>
                <option value="Walker">Walker</option>
                <option value="Wheelchair">Wheelchair</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Gait Observation</label>
            <select
              value={data.gaitObservation}
              onChange={(e) => onChange("gaitObservation", e.target.value)}
              disabled={disabled}
            >
              <option value="">N/A</option>
              <option value="Steady">Steady</option>
              <option value="Unsteady">Unsteady</option>
            </select>
          </div>
          <div className="form-group">
            <label>Falls Last Year</label>
            <select
              value={data.fallsLastYear}
              onChange={(e) => onChange("fallsLastYear", e.target.value)}
              disabled={disabled}
            >
              <option value="">N/A</option>
              <option value="0 falls">0 falls</option>
              <option value="1 fall">1 fall</option>
              <option value="2+ falls">2+ falls</option>
            </select>
          </div>
        </div>

        {/* Right Column: Lifestyle */}
        <div className="column-panel">
          <h3 className="section-title-red">Lifestyle & Living Arrangement</h3>
          <div className="form-group">
            <label>Living Arrangement</label>
            <select
              value={data.livingArrangement}
              onChange={(e) => onChange("livingArrangement", e.target.value)}
              disabled={disabled}
            >
              <option value="">N/A</option>
              <option>With Adult Child</option>
              <option>Alone</option>
              <option>With Spouse</option>
            </select>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Home Assistance</label>
              <select
                value={data.homeAssistance}
                onChange={(e) => onChange("homeAssistance", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Transportation Access</label>
              <select
                value={data.transportation}
                onChange={(e) => onChange("transportation", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Pet Ownership</label>
              <select
                value={data.petOwnership}
                onChange={(e) => onChange("petOwnership", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Pet Type</label>
              <input
                type="text"
                value={data.petType}
                onChange={(e) => onChange("petType", e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>

          <label
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#666",
              marginTop: "10px",
              display: "block",
            }}
          >
            Behavioral Factors
          </label>
          <div className="form-row two-col" style={{ marginTop: "5px" }}>
            <div className="form-group">
              <label>Smoker</label>
              <select
                value={data.smoker}
                onChange={(e) => onChange("smoker", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option>No</option>
                <option>Yes</option>
              </select>
            </div>
            <div className="form-group">
              <label>Alcohol Consumer</label>
              <select
                value={data.alcohol}
                onChange={(e) => onChange("alcohol", e.target.value)}
                disabled={disabled}
              >
                <option value="">N/A</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Alcohol Consumption Category</label>
            <select
              value={data.alcoholConsumption}
              onChange={(e) => onChange("alcoholConsumption", e.target.value)}
              disabled={disabled}
            >
              <option value="">N/A</option>
              <option>Light</option>
              <option>Moderate</option>
              <option>Heavy</option>
            </select>
          </div>
          <div className="form-group">
            <label>Income Range</label>
            <select
              value={data.incomeRange}
              onChange={(e) => onChange("incomeRange", e.target.value)}
              disabled={disabled}
            >
              <option value="">N/A</option>
              <option>$51k-75k</option>
              <option>$25k-50k</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Care Team Tab ---
const CareTeam = ({
  careTeamModel,
  onCareTeamChange,
  disabled,
  wellSkyVisitMetrics = false,
}) => {
  console.log("👥 Care Team rendering, disabled:", disabled);
  const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const coveragePct = Math.min(
    100,
    Math.max(0, (num(careTeamModel.daysPerWeek) / 7) * 100),
  );
  // Reliability: delivered on time vs scheduled (independent fields)
  const deliveredReliability = Math.min(
    num(careTeamModel.deliveredOnTime),
    num(careTeamModel.scheduledVisits),
  );
  const reliabilityPct =
    num(careTeamModel.scheduledVisits) > 0
      ? Math.round(
          (deliveredReliability / num(careTeamModel.scheduledVisits)) * 100,
        )
      : 0;
  // Continuity: same person vs total visits (independent fields)
  const samePersonContinuity = Math.min(
    num(careTeamModel.visitsBySamePerson),
    num(careTeamModel.totalVisits),
  );
  const continuityPct =
    num(careTeamModel.totalVisits) > 0
      ? Math.round(
          (samePersonContinuity / num(careTeamModel.totalVisits)) * 100,
        )
      : 0;

  return (
    <div className="tab-pane">
      <h3 className="section-header-red flex items-center gap-2">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
          <FontAwesomeIcon icon={faUsers} />
        </span>
        <span>Care Team Assessment</span>
        {wellSkyVisitMetrics ? (
          <span className="wellsky-field-badge">WellSky visits</span>
        ) : null}
      </h3>
      {wellSkyVisitMetrics ? (
        <p className="aip-wellsky-lineage-banner" style={{ marginBottom: 16 }}>
          <strong>WellSky data:</strong> Coverage, reliability, and continuity
          use visit history synced from WellSky (same source as Care Count).
          Reliability here means weeks with at least one visit (last 13 weeks),
          not EVV on-time clock-in.
        </p>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {/* Coverage */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
                <FontAwesomeIcon icon={faClipboardCheck} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Coverage</p>
                <p className="text-3xl font-bold text-slate-900">
                  {coveragePct.toFixed(0)}%
                </p>
              </div>
            </div>
            <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
              Weekly Care
            </span>
          </div>

          <div className="mt-4 space-y-3">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Days of care per week
            </label>
            <input
              type="number"
              min="0"
              max="7"
              value={careTeamModel.daysPerWeek}
              onChange={(e) =>
                onCareTeamChange((prev) => ({
                  ...prev,
                  daysPerWeek: Number(e.target.value),
                }))
              }
              disabled={disabled}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 shadow-inner transition focus:border-sky-400 focus:bg-white focus:outline-none disabled:opacity-60"
            />
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
              <span>Coverage level</span>
              <span className="text-slate-900">{coveragePct.toFixed(0)}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-sky-400 via-sky-500 to-sky-600 transition-all duration-300"
                style={{ width: `${coveragePct}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">
              Number of days per week that care services are provided (0-7)
            </p>
          </div>
        </div>

        {/* Reliability */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <FontAwesomeIcon icon={faPlusSquare} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Reliability
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {reliabilityPct.toFixed(1)}%
                </p>
              </div>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              On time
            </span>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Total scheduled visits
              </label>
              <input
                type="number"
                min="0"
                value={careTeamModel.scheduledVisits}
                onChange={(e) =>
                  onCareTeamChange((prev) => {
                    const scheduledVisits = Math.max(
                      0,
                      Number(e.target.value || 0),
                    );
                    return {
                      ...prev,
                      scheduledVisits,
                      // Keep reliability data logically valid when scheduled count drops.
                      deliveredOnTime: Math.min(
                        Number(prev.deliveredOnTime || 0),
                        scheduledVisits,
                      ),
                    };
                  })
                }
                disabled={disabled}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 shadow-inner transition focus:border-emerald-400 focus:bg-white focus:outline-none disabled:opacity-60"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Visits delivered on time
              </label>
              <input
                type="number"
                min="0"
                max={Math.max(0, Number(careTeamModel.scheduledVisits || 0))}
                value={careTeamModel.deliveredOnTime}
                onChange={(e) =>
                  onCareTeamChange((prev) => {
                    const scheduled = Math.max(
                      0,
                      Number(prev.scheduledVisits || 0),
                    );
                    const entered = Math.max(0, Number(e.target.value || 0));
                    return {
                      ...prev,
                      deliveredOnTime: Math.min(entered, scheduled),
                    };
                  })
                }
                disabled={disabled}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 shadow-inner transition focus:border-emerald-400 focus:bg-white focus:outline-none disabled:opacity-60"
              />
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
              <span>Reliability rate</span>
              <span className="text-slate-900">
                {reliabilityPct.toFixed(1)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 transition-all duration-300"
                style={{ width: `${reliabilityPct}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">
              Percentage of scheduled visits delivered on time
            </p>
          </div>
        </div>

        {/* Continuity */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Continuity</p>
                <p className="text-3xl font-bold text-slate-900">
                  {continuityPct.toFixed(1)}%
                </p>
              </div>
            </div>
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
              Same caregiver
            </span>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Total visits
              </label>
              <input
                type="number"
                min="0"
                value={careTeamModel.totalVisits}
                onChange={(e) =>
                  onCareTeamChange((prev) => ({
                    ...prev,
                    totalVisits: Number(e.target.value),
                  }))
                }
                disabled={disabled}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 shadow-inner transition focus:border-indigo-400 focus:bg-white focus:outline-none disabled:opacity-60"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Visits by same person
              </label>
              <input
                type="number"
                min="0"
                value={careTeamModel.visitsBySamePerson}
                onChange={(e) =>
                  onCareTeamChange((prev) => {
                    const entered = Number(e.target.value);
                    const clamped = Math.min(
                      entered,
                      Number(prev.totalVisits ?? 0),
                    );
                    return { ...prev, visitsBySamePerson: clamped };
                  })
                }
                disabled={disabled}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 shadow-inner transition focus:border-indigo-400 focus:bg-white focus:outline-none disabled:opacity-60"
              />
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
              <span>Continuity rate</span>
              <span className="text-slate-900">
                {continuityPct.toFixed(1)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600 transition-all duration-300"
                style={{ width: `${continuityPct}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">
              Percentage of visits provided by the same caregiver
            </p>
          </div>
        </div>

        {/* Coordination */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md xl:col-span-3">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 text-violet-600">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Coordination
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  Care plan & family communication
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Care Plan Status
              </span>
              <div className="flex items-center gap-2">
                <button
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    careTeamModel.carePlanUpToDate
                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  onClick={() =>
                    !disabled &&
                    onCareTeamChange((prev) => ({
                      ...prev,
                      carePlanUpToDate: true,
                    }))
                  }
                  disabled={disabled}
                >
                  Up to Date
                </button>
                <button
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    !careTeamModel.carePlanUpToDate
                      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  onClick={() =>
                    !disabled &&
                    onCareTeamChange((prev) => ({
                      ...prev,
                      carePlanUpToDate: false,
                    }))
                  }
                  disabled={disabled}
                >
                  Needs Update
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between pb-2">
              <div className="text-sm font-semibold text-slate-600">
                Family Communication Satisfaction
              </div>
              <div className="text-xs text-slate-500">Tap to rate</div>
            </div>
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5].map((num) => {
                const active =
                  parseInt(careTeamModel.familySatisfaction) === num;
                return (
                  <button
                    key={num}
                    className={`flex flex-1 min-w-[120px] items-center justify-between rounded-xl border px-3 py-3 text-left text-sm transition hover:-translate-y-0.5 hover:shadow ${
                      active
                        ? "border-violet-200 bg-violet-50 text-violet-700 shadow-sm"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                    onClick={() =>
                      !disabled &&
                      onCareTeamChange((prev) => ({
                        ...prev,
                        familySatisfaction: num,
                      }))
                    }
                    disabled={disabled}
                  >
                    <span className="font-semibold">Rating {num}</span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                        active
                          ? "bg-white text-violet-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <FontAwesomeIcon icon={faSmile} />
                      {num === 1
                        ? "Poor"
                        : num === 2
                          ? "Fair"
                          : num === 3
                            ? "Good"
                            : num === 4
                              ? "Very Good"
                              : "Excellent"}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Rate family satisfaction with care team communication.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- CareLink360 Tab ---
const CARE_LINK_SECTIONS = [
  {
    id: "medication",
    title: "1. Medication and Clinical Compliance",
    icon: faPills,
    colorClass: "cl-orange",
    metrics: [
      { label: "Medication Adherence Rate", key: "medicationAdherenceRate" },
      { label: "Missed dose trends", key: "missedDoseTrends" },
      { label: "Late dose frequency", key: "lateDoseFrequency" },
    ],
  },
  {
    id: "conversational",
    title: "2. Conversational Signals",
    icon: faComments,
    colorClass: "cl-blue",
    metrics: [
      { label: "Voice Chat Frequency", key: "voiceChatFrequency" },
      { label: "Response delay", key: "responseDelay" },
      { label: "Confusion flags", key: "confusionFlags" },
      { label: "Repetition rate", key: "repetitionRate" },
    ],
  },
  {
    id: "dailyLiving",
    title: "3. Daily Living Performance",
    icon: faUtensils,
    colorClass: "cl-teal",
    metrics: [
      { label: "Nutrition", key: "nutrition" },
      { label: "Hydration", key: "hydration" },
      { label: "Exercise / Movement Adherence", key: "exerciseAdherence" },
      { label: "Self-reported pain", key: "selfReportedPain" },
    ],
  },
  {
    id: "cognitive",
    title: "4. Cognitive Activity Metrics",
    icon: faBrain,
    colorClass: "cl-purple",
    metrics: [
      { label: "Cognitive activity sessions per day", key: "cognitiveSessionsPerDay" },
      { label: "Total cognitive engagement time", key: "cognitiveEngagementTime" },
      { label: "Time-of-day engagement patterns", key: "engagementPatterns" },
      { label: "Completion vs. abandonment rate", key: "completionVsAbandonmentRate" },
      { label: "Response speed during tasks", key: "responseSpeed" },
      { label: "Activity levels", key: "activityLevels" },
    ],
  },
  {
    id: "sensory",
    title: "5. Sensory and Emotional Engagement",
    icon: faMusic,
    colorClass: "cl-pink",
    metrics: [
      { label: "Music listening frequency", key: "musicListeningFrequency" },
      { label: "Duration", key: "musicDuration" },
      { label: "Time-of-day listening pattern", key: "listeningPattern" },
    ],
  },
  {
    id: "educational",
    title: "6. Educational Content Engagement",
    icon: faGraduationCap,
    colorClass: "cl-blue",
    metrics: [
      { label: "Educational video sessions", key: "educationalVideoSessions" },
      { label: "Learning topics", key: "learningTopics" },
      { label: "Completion rate", key: "educationalCompletionRate" },
      { label: "Rewatch patterns", key: "rewatchPatterns" },
    ],
  },
];

const CareLink360 = ({ clientIdDisplay, data = {} }) => {
  const [expandedSections, setExpandedSections] = useState(() =>
    CARE_LINK_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );
  const toggleSection = (id) =>
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const getValue = (key) => data[key] ?? "—";

  return (
    <div className="carelink360-wrap">
      <div className="carelink360-header">
        <div className="carelink360-brand">
          <div className="carelink360-brand-icon">
            <FontAwesomeIcon icon={faLaptop} />
          </div>
          <div className="carelink360-brand-text">
            <h2 className="carelink360-title">
              Cognitive and Wellness Monitoring
            </h2>
            <p className="carelink360-subtitle">
              Client #{clientIdDisplay || "—"} · Activity & engagement metrics
            </p>
          </div>
        </div>
      </div>

      <div className="carelink360-sections">
        {CARE_LINK_SECTIONS.map((section) => (
          <div
            key={section.id}
            className={`carelink360-section ${expandedSections[section.id] ? "expanded" : ""}`}
          >
            <button
              type="button"
              className="carelink360-section-header"
              onClick={() => toggleSection(section.id)}
            >
              <div className="carelink360-section-icon-wrap">
                <FontAwesomeIcon
                  icon={section.icon}
                  className={`carelink360-section-icon ${section.colorClass}`}
                />
              </div>
              <span className="carelink360-section-title">{section.title}</span>
              <FontAwesomeIcon
                icon={faChevronRight}
                className={`carelink360-chevron ${expandedSections[section.id] ? "open" : ""}`}
              />
            </button>
            {expandedSections[section.id] && (
              <div className="carelink360-section-body">
                <div className="carelink360-metrics-grid">
                  {section.metrics.map((m) => (
                    <div key={m.key} className="carelink360-metric-row">
                      <span className="carelink360-metric-label">{m.label}</span>
                      <span className="carelink360-metric-value">{getValue(m.key)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="carelink360-note">
        <div className="carelink360-note-icon">
          <FontAwesomeIcon icon={faCircleInfo} />
        </div>
        <div className="carelink360-note-text">
          <strong>Note:</strong> CareLink360 data contributes to the overall health
          score. Connect your CareLink360 integration to view live metrics.
        </div>
      </div>
    </div>
  );
};

const DailyLifeResilience = ({ data = {}, onChange, disabled }) => {
  return (
    <div className="carelink360-wrap">
      <div className="carelink360-header">
        <div className="carelink360-brand">
          <div className="carelink360-brand-icon">
            <FontAwesomeIcon icon={faHeartPulse} />
          </div>
          <div className="carelink360-brand-text">
            <h2 className="carelink360-title">Daily Life and Resilience</h2>
            <p className="carelink360-subtitle">
              Daily routine, hydration, activity, sleep, and resilience profile
            </p>
          </div>
        </div>
      </div>

      <div className="carelink360-section expanded">
        <div className="carelink360-section-header" role="presentation">
          <div className="carelink360-section-icon-wrap">
            <FontAwesomeIcon
              icon={faClipboardCheck}
              className="carelink360-section-icon cl-teal"
            />
          </div>
          <span className="carelink360-section-title">Lifestyle Assessment</span>
        </div>
        <div className="carelink360-section-body">
          <div className="form-row two-col">
            <div className="form-group">
              <label>Nutrition</label>
              <select
                value={data.dailyNutrition || ""}
                onChange={(e) => onChange("dailyNutrition", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Nutrition</option>
                <option value="Excellent - Balanced, whole foods daily">
                  Excellent - Balanced, whole foods daily
                </option>
                <option value="Good - Mostly healthy with occasional processed foods">
                  Good - Mostly healthy with occasional processed foods
                </option>
                <option value="Fair - Mix of healthy and processed foods">
                  Fair - Mix of healthy and processed foods
                </option>
                <option value="Poor - Mostly processed or low-nutrient foods">
                  Poor - Mostly processed or low-nutrient foods
                </option>
                <option value="At Risk - Irregular meals or limited food access">
                  At Risk - Irregular meals or limited food access
                </option>
              </select>
            </div>
            <div className="form-group">
              <label>
                Hydration - Daily Intake on Average (Water, Tea, Coffee, etc.)
              </label>
              <select
                value={data.dailyHydrationIntake || ""}
                onChange={(e) => onChange("dailyHydrationIntake", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Intake</option>
                <option value="8+ cups (64+ oz)">8+ cups (64+ oz)</option>
                <option value="6-7 cups">6-7 cups</option>
                <option value="4-5 cups">4-5 cups</option>
                <option value="2-3 cups">2-3 cups</option>
                <option value="0-1 cup">0-1 cup</option>
              </select>
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Exercise</label>
              <select
                value={data.dailyExerciseLevel || ""}
                onChange={(e) => onChange("dailyExerciseLevel", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Exercise</option>
                <option value="Very Active - Exercise 5+ days per week">
                  Very Active - Exercise 5+ days per week
                </option>
                <option value="Active - Exercise 3-4 days per week">
                  Active - Exercise 3-4 days per week
                </option>
                <option value="Moderately Active - 1-2 days per week">
                  Moderately Active - 1-2 days per week
                </option>
                <option value="Light Activity Only - Walking or household movement only">
                  Light Activity Only - Walking or household movement only
                </option>
                <option value="Sedentary - Rarely physically active">
                  Sedentary - Rarely physically active
                </option>
              </select>
            </div>
            <div className="form-group">
              <label>Sleep Quality / Consistency</label>
              <select
                value={data.sleepQuality || ""}
                onChange={(e) => onChange("sleepQuality", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Sleep Quality</option>
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Self-Reported Pain (Frequency)</label>
              <select
                value={data.dailyPainFrequency || ""}
                onChange={(e) => onChange("dailyPainFrequency", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Pain Frequency</option>
                <option value="Never">Never</option>
                <option value="Occasionally">Occasionally</option>
                <option value="Several days per week">Several days per week</option>
                <option value="Daily">Daily</option>
                <option value="Constant">Constant</option>
              </select>
            </div>
            <div className="form-group">
              <label>Pet Ownership</label>
              <select
                value={data.petOwnership || ""}
                onChange={(e) => onChange("petOwnership", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Type of Pet</label>
              <select
                value={data.petType || ""}
                onChange={(e) => onChange("petType", e.target.value)}
                disabled={disabled || data.petOwnership !== "Yes"}
              >
                <option value="">Select Type</option>
                <option value="Dog">Dog</option>
                <option value="Cat">Cat</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Smoker</label>
              <select
                value={data.smoker || ""}
                onChange={(e) => onChange("smoker", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Alcohol</label>
              <select
                value={data.alcohol || ""}
                onChange={(e) => onChange("alcohol", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Alcohol Consumption Category</label>
              <select
                value={data.alcoholConsumption || ""}
                onChange={(e) => onChange("alcoholConsumption", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Category</option>
                <option value="Never">Never</option>
                <option value="Rarely (less than once per month)">
                  Rarely (less than once per month)
                </option>
                <option value="Monthly">Monthly</option>
                <option value="2-4 times per month">2-4 times per month</option>
                <option value="2-3 times per week">2-3 times per week</option>
                <option value="4+ times per week">4+ times per week</option>
                <option value="Daily">Daily</option>
              </select>
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Driving Status</label>
              <select
                value={data.drivingStatus || ""}
                onChange={(e) => onChange("drivingStatus", e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Driving Status</option>
                <option value="Drives Independently">Drives Independently</option>
                <option value="Limited">Limited</option>
                <option value="No Longer Drives">No Longer Drives</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomeClientMonitoring = () => {
  return (
    <div className="carelink360-wrap">
      <div className="carelink360-header">
        <div className="carelink360-brand">
          <div className="carelink360-brand-icon">
            <FontAwesomeIcon icon={faHome} />
          </div>
          <div className="carelink360-brand-text">
            <h2 className="carelink360-title">Home / Client Monitoring</h2>
            <p className="carelink360-subtitle">
              Third-party monitoring tools integration overview
            </p>
          </div>
        </div>
      </div>

      <div className="carelink360-note">
        <div className="carelink360-note-icon">
          <FontAwesomeIcon icon={faCircleInfo} />
        </div>
        <div className="carelink360-note-text">
          Other Agencies Monitoring tools such as: Sensi, PacSana, Pontosense,
          Medical Alert, Aloe Care, Others.
        </div>
      </div>
    </div>
  );
};

// --- Environmental Safety Tab ---
const EnvironmentalSafety = ({ data, onChange, disabled }) => {
  console.log("🏠 Environmental Safety rendering, disabled:", disabled);

  const b2cSelfAssessment = isB2cSelfAssessment(data);
  const b2cCategorySummaries = b2cSelfAssessment
    ? getEsCategorySummaries(data)
    : [];

  const categories = [
    {
      id: "fallHazards",
      title: "1. Fall Hazards",
      weight: 0.3,
      items: [
        { key: "esLooseRugs", label: "Loose rugs" },
        { key: "esClutterWalkways", label: "Cluttered walkways" },
        { key: "esPoorLighting", label: "Poor lighting" },
        { key: "esStairsNoRailings", label: "Stairs w/out railings" },
        { key: "esSlipperyFloors", label: "Slippery floors" },
        { key: "esNoGrabBars", label: "No grab bars" },
        { key: "esUnevenSteps", label: "Uneven /worn steps" },
        { key: "esCordsOnFloor", label: "Cords or wires on the floor" },
        { key: "esUnevenFlooring", label: "Uneven flooring" },
        {
          key: "esFootwearNoTraction",
          label: "Footwear without proper traction",
        },
        { key: "esNoNightlights", label: "No Nightlights" },
        { key: "esPetsUnderfoot", label: "Pets underfoot" },
        { key: "esUnstableFurniture", label: "Unstable furniture" },
        { key: "esItemsStoredImproper", label: "Items stored too high/low" },
        { key: "esOtherFallHazard", label: "Other Fall hazard" },
      ],
    },
    {
      id: "bathroomSafety",
      title: "2. Bathroom Safety",
      weight: 0.3,
      items: [
        { key: "esWetSlipperyFloors", label: "Wet/slippery floors" },
        { key: "esNoBathroomGrabBars", label: "No grab bars" },
        { key: "esSlipperyTub", label: "Slippery tub/shower" },
        { key: "esNoNonSlipStrips", label: "No non-slip mat/strips" },
        { key: "esLooseBathMats", label: "Loose bath mats" },
        { key: "esLowToiletHeight", label: "Low toilet height" },
        { key: "esLooseToiletSeat", label: "Loose toilet seat" },
        { key: "esHighTubThreshold", label: "High tub edge/threshold" },
        { key: "esBathroomPoorLighting", label: "Poor lighting" },
        { key: "esBathroomNoNightlight", label: "No nightlight" },
        { key: "esBathroomClutter", label: "Clutter on floors" },
        { key: "esItemsOutOfReach", label: "Items stored out of reach" },
        { key: "esTowelBarsForSupport", label: "Towel bars used for support" },
        {
          key: "esUnstableShowerChair",
          label: "Unstable shower chair (or none)",
        },
        {
          key: "esCrampedBathroom",
          label: "Small/ cramped space for movement",
        },
        { key: "esOtherBathroomHazard", label: "Other safety hazard" },
      ],
    },
    {
      id: "kitchenSafety",
      title: "3. Kitchen Safety",
      weight: 0.2,
      items: [
        { key: "esKitchenWetFloors", label: "Wet/slippery floors" },
        { key: "esGreaseOnFloor", label: "Grease/oil on floor" },
        { key: "esLooseKitchenMats", label: "Loose kitchen mats" },
        { key: "esNoNonSlipBacking", label: "No non-slip backing" },
        { key: "esReachingHighCabinets", label: "Reaching to high cabinets" },
        { key: "esUnsafeStepStool", label: "Unsafe step stool" },
        { key: "esKitchenClutter", label: "Clutter on floor" },
        { key: "esNoFireExtinguisher", label: "No Fire extinguisher" },
        { key: "esBendingLowCabinets", label: "Bending to low cabinets" },
        { key: "esLooseDrawersDoors", label: "Loose or broken drawers/doors" },
        {
          key: "esFrayedCords",
          label: "Frayed or damaged electrical cords",
        },
        { key: "esKitchenPoorLighting", label: "Poor lighting" },
        { key: "esApplianceCordsInPath", label: "Appliance cords in path" },
        { key: "esKitchenUnevenFlooring", label: "Uneven flooring" },
        { key: "esOverloadedOutlet", label: "Overloaded outlet" },
        {
          key: "esNoSmokeDetector",
          label: "No smoke detector or not working",
        },
        { key: "esNoCO2Detector", label: "No CO2 detector or not working" },
        { key: "esOtherKitchenHazard", label: "Other safety hazard" },
      ],
    },
    {
      id: "homeLayout",
      title: "4. Home Layout & Accessibility",
      weight: 0.2,
      items: [
        { key: "esStepsThresholds", label: "Steps/thresholds" },
        { key: "esNarrowHallways", label: "Narrow hallways" },
        { key: "esLayoutClutter", label: "Clutter" },
        {
          key: "esInaccessibleBathroom",
          label: "Inaccessible bathroom/bedroom",
        },
        { key: "esMultiLevelHome", label: "Multi-level home" },
        { key: "esTightDoorways", label: "Tight doorways" },
        { key: "esObstructedPathways", label: "Obstructed pathways" },
      ],
    },
  ];

  return (
    <div className="tab-pane">
      <div className="flex items-center justify-between gap-3">
        <h3 className="section-header-red environmental-header flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <FontAwesomeIcon icon={faHome} />
          </span>
          <span>Environmental Safety Assessment</span>
        </h3>
        {/* <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          ES Score: {esScore}/100
        </span> */}
      </div>

      {b2cSelfAssessment ? (
        <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">
            Self-reported by family — category-level only
          </p>
          <p className="mt-1 text-amber-800">
            This assessment used the family questionnaire (tier scores per area).
            Item-level hazard checklists are hidden because they are not
            collected in B2C intake.
          </p>
        </div>
      ) : null}

      <div className="mt-4 grid gap-4">
        {b2cSelfAssessment
          ? b2cCategorySummaries.map((cat) => (
              <div
                key={cat.id}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <p className="text-sm font-semibold text-slate-900">
                  {cat.title}
                </p>
                <p className="mt-2 text-sm text-slate-700">{cat.displayLine}</p>
              </div>
            ))
          : categories.map((cat) => (
          <details
            key={cat.id}
            className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
          >
            <summary className="flex cursor-pointer items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {cat.title}
                </p>
                <p className="text-xs text-slate-500">
                  Check hazards that are present
                </p>
              </div>
              <div className="flex items-center gap-2"></div>
            </summary>

            <div className="mt-4 space-y-2">
              {cat.items.map((item) => (
                <label
                  key={item.key}
                  className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm ${
                    data[item.key]
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-slate-200 bg-white text-slate-700"
                  } ${disabled ? "opacity-60" : "hover:border-slate-300"}`}
                >
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={!!data[item.key]}
                      disabled={disabled}
                      onChange={() =>
                        !disabled && onChange(item.key, !data[item.key])
                      }
                    />
                    {item.label}
                  </span>
                  <span className="text-xs font-semibold">
                    {data[item.key] ? "Hazard" : "Clear"}
                  </span>
                </label>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

function stripWellSkyResponse(body) {
  if (!body || typeof body !== "object") {
    return { payload: body, meta: null };
  }
  const { _wellskyMeta, ...payload } = body;
  return { payload, meta: _wellskyMeta || null };
}

function useWellSkyClientResource(resourceType, wellSkyId, refreshNonce = 0) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!wellSkyId) {
      setData(null);
      setLoading(false);
      return;
    }
    const isRefresh = hasLoadedRef.current;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await getWellSkyClientData(resourceType, wellSkyId, { refresh: true });
      const { payload, meta } = stripWellSkyResponse(res.data);
      setSyncedAt(meta?.syncedAt || new Date().toISOString());
      setData(payload);
      hasLoadedRef.current = true;
    } catch (err) {
      if (err?.response?.status === 404) {
        setData(
          resourceType === "careplan"
            ? { adl: [], iadl: [] }
            : resourceType === "medication"
              ? { medications: [], activeMedications: 0, stoppedMedications: 0 }
              : resourceType === "relatedperson"
                ? { contacts: [] }
                : {},
        );
      } else {
        setError(`Failed to load WellSky ${resourceType}`);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [resourceType, wellSkyId]);

  useEffect(() => {
    hasLoadedRef.current = false;
    load();
  }, [load, refreshNonce]);

  return { data, loading, refreshing, error, syncedAt, reload: load };
}

const WellSkyCarePlanTab = ({ client, refreshNonce = 0 }) => {
  const { data, loading, refreshing, error } = useWellSkyClientResource(
    "careplan",
    client?.wellSkyId,
    refreshNonce,
  );
  const levelOfNeedFromScore = {
    "0": "Independent",
    "1": "Requires Assistance",
    "2": "Dependent",
  };
  const getLevelOfNeedLabel = (item) => {
    const level = String(item?.levelOfNeed || "").trim();
    if (level && level.toLowerCase() !== "not assessed") return level;
    const fromScore = levelOfNeedFromScore[String(item?.score ?? "").trim()];
    if (fromScore) return fromScore;
    const scoreText = String(item?.score ?? "").trim();
    if (scoreText && !["0", "1", "2"].includes(scoreText)) return scoreText;
    return "Not Assessed";
  };

  if (!client?.wellSkyId) {
    return <div className="p-6 text-gray-500">No WellSky ID linked to this client.</div>;
  }
  if (loading) {
    return <div className="p-6 text-gray-500">Syncing care plan from WellSky…</div>;
  }
  if (refreshing) {
    return <div className="p-6 text-gray-500">Updating from WellSky…</div>;
  }
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (
    !data ||
    ((data.adl || []).length === 0 && (data.iadl || []).length === 0)
  ) {
    return (
      <div className="p-6 text-gray-500">No care plan data available from WellSky.</div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <h3 className="font-semibold text-gray-700 mb-3">
          ADL Activities ({data.adl?.length || 0})
        </h3>
        {(data.adl || []).length === 0 ? (
          <p className="text-gray-400 text-sm">None recorded</p>
        ) : (
          (data.adl || []).map((item, i) => (
            <div key={i} className="mb-3 p-3 bg-blue-50 rounded-lg">
              <div className="font-medium text-blue-800">
                {(item.name || "Activity") + " — " + getLevelOfNeedLabel(item)}
              </div>
              <div className="text-sm text-gray-600 mt-1">{item.taskDescription}</div>
              {item.caregiverInstruction && (
                <div className="text-xs text-gray-400 mt-1">
                  Note: {item.caregiverInstruction}
                </div>
              )}
              <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {item.status}
              </span>
            </div>
          ))
        )}
      </div>
      <div>
        <h3 className="font-semibold text-gray-700 mb-3">
          IADL Activities ({data.iadl?.length || 0})
        </h3>
        {(data.iadl || []).length === 0 ? (
          <p className="text-gray-400 text-sm">None recorded</p>
        ) : (
          (data.iadl || []).map((item, i) => (
            <div key={i} className="mb-3 p-3 bg-green-50 rounded-lg">
              <div className="font-medium text-green-800">
                {(item.name || "Activity") + " — " + getLevelOfNeedLabel(item)}
              </div>
              <div className="text-sm text-gray-600 mt-1">{item.taskDescription}</div>
              {item.caregiverInstruction && (
                <div className="text-xs text-gray-400 mt-1">
                  Note: {item.caregiverInstruction}
                </div>
              )}
              <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                {item.status}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const WellSkyClinicalTab = ({ client, refreshNonce = 0 }) => {
  const { data, loading, refreshing, error } = useWellSkyClientResource(
    "condition",
    client?.wellSkyId,
    refreshNonce,
  );

  if (!client?.wellSkyId) {
    return <div className="p-6 text-gray-500">No WellSky ID linked to this client.</div>;
  }
  if (loading) {
    return <div className="p-6 text-gray-500">Syncing clinical data from WellSky…</div>;
  }
  if (refreshing) {
    return <div className="p-6 text-gray-500">Updating from WellSky…</div>;
  }
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!data || Object.keys(data).length === 0) {
    return <div className="p-6 text-gray-500">No clinical data available from WellSky.</div>;
  }

  return (
    <div>
      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">🧠 Mental & Behavior</div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Dementia:</span> <span className="font-medium">{data.mentalBehavior?.dementia || "—"}</span></div>
          <div><span className="text-gray-500">Can be left alone:</span> <span className="font-medium">{data.mentalBehavior?.canBeLeftAlone || "—"}</span></div>
          <div><span className="text-gray-500">Wanderer:</span> <span className="font-medium">{data.mentalBehavior?.wanderer || "—"}</span></div>
          <div><span className="text-gray-500">Diagnosed disorders:</span> <span className="font-medium">{data.mentalBehavior?.diagnosedDisorders || "—"}</span></div>
          <div className="col-span-2">
            <span className="text-gray-500">Active symptoms: </span>
            <span className="font-medium">
              {Object.entries(data.mentalBehavior?.symptoms || {})
                .filter(([, v]) => v === true)
                .map(([k]) => k)
                .join(", ") || "None"}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">🚶 Ambulation</div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Fall risk:</span> <span className="font-medium">{data.ambulation?.fallRisk || "—"}</span></div>
          <div><span className="text-gray-500">Aids:</span> <span className="font-medium">{Object.entries(data.ambulation || {}).filter(([k, v]) => ["cane", "walker", "wheelchair", "scooter", "geriChair"].includes(k) && v === true).map(([k]) => k).join(", ") || "None"}</span></div>
          {data.ambulation?.notes && <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="font-medium">{data.ambulation.notes}</span></div>}
        </div>
      </div>

      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">🛁 Bathing & Dressing</div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Bathing:</span> <span className="font-medium">{data.bathingDressing?.bathing || "—"}</span></div>
          <div><span className="text-gray-500">Dressing:</span> <span className="font-medium">{data.bathingDressing?.dressing || "—"}</span></div>
          <div><span className="text-gray-500">Toileting:</span> <span className="font-medium">{data.bathingDressing?.toileting || "—"}</span></div>
          <div><span className="text-gray-500">Grooming:</span> <span className="font-medium">{data.bathingDressing?.grooming || "—"}</span></div>
        </div>
      </div>

      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">🍽 Meals</div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Appetite:</span> <span className="font-medium">{data.meals?.appetite || "—"}</span></div>
          <div><span className="text-gray-500">Diet:</span> <span className="font-medium">{data.meals?.diet || "—"}</span></div>
          <div><span className="text-gray-500">Assistance cooking:</span> <span className="font-medium">{data.meals?.assistanceCooking ? "Yes" : "No"}</span></div>
          <div><span className="text-gray-500">Assistance feeding:</span> <span className="font-medium">{data.meals?.assistanceFeeding ? "Yes" : "No"}</span></div>
        </div>
      </div>

      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">💊 Medication Management</div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Needs reminders:</span> <span className="font-medium">{data.medication?.needsReminders || "—"}</span></div>
          <div><span className="text-gray-500">Number of medications:</span> <span className="font-medium">{data.medication?.numberOfMedications || "—"}</span></div>
          <div><span className="text-gray-500">Who manages:</span> <span className="font-medium">{data.medication?.whoManages || "—"}</span></div>
        </div>
      </div>

      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">😴 Sleep</div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Pattern:</span> <span className="font-medium">{data.sleep?.pattern || "—"}</span></div>
          <div><span className="text-gray-500">Goes to bed:</span> <span className="font-medium">{data.sleep?.goesToBed || "—"}</span></div>
          <div><span className="text-gray-500">Wakes up:</span> <span className="font-medium">{data.sleep?.wakesUp || "—"}</span></div>
          <div><span className="text-gray-500">Night assistance:</span> <span className="font-medium">{data.sleep?.needsNightAssistance ? "Yes" : "No"}</span></div>
        </div>
      </div>

      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700">🏠 Equipment & Environment</div>
        <div className="p-4 text-sm">
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.equipment || {})
              .filter(([k, v]) => v === true && k !== "safetyAssessmentDone")
              .map(([k]) => (
                <span key={k} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                  {k}
                </span>
              ))}
            {Object.values(data.equipment || {}).filter((v) => v === true).length === 0 && (
              <span className="text-gray-400">None recorded</span>
            )}
          </div>
          <div className="mt-2"><span className="text-gray-500">Safety assessment done:</span> <span className="font-medium">{data.equipment?.safetyAssessmentDone || "—"}</span></div>
        </div>
      </div>
    </div>
  );
};

const WellSkyMedicationsTab = ({ client, refreshNonce = 0 }) => {
  const { data, loading, refreshing, error } = useWellSkyClientResource(
    "medication",
    client?.wellSkyId,
    refreshNonce,
  );

  if (!client?.wellSkyId) {
    return <div className="p-6 text-gray-500">No WellSky ID linked to this client.</div>;
  }
  if (loading) {
    return <div className="p-6 text-gray-500">Syncing medications from WellSky…</div>;
  }
  if (refreshing) {
    return <div className="p-6 text-gray-500">Updating from WellSky…</div>;
  }
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!data) {
    return (
      <div className="p-6 text-gray-500">No medication data available from WellSky.</div>
    );
  }

  return (
    <div>
      <p className="mb-3 text-xs text-gray-500">
        Form and strength come from WellSky. This list reloads from WellSky when you open
        the Medications tab.
      </p>
      <div className="mb-4 text-sm text-gray-600">
        <span className="font-medium text-green-600">{data.activeMedications} Active</span>
        {" · "}
        <span className="font-medium text-gray-400">{data.stoppedMedications} Stopped</span>
      </div>
      <div className="space-y-3">
        {(data.medications || []).map((med, i) => (
          <div
            key={i}
            className={`p-4 rounded-lg border ${med.status === "active" ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`}
          >
            <div className="flex items-start justify-between">
              <div className="font-semibold text-gray-800">{med.name}</div>
              <span
                className={`text-xs px-2 py-0.5 rounded ${med.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}
              >
                {med.status}
              </span>
            </div>
            {med.form && <div className="text-sm text-gray-600 mt-1">Form: {med.form}</div>}
            {med.strength && (
              <div className="text-sm text-gray-600">Strength: {med.strength}</div>
            )}
            {med.dosage && <div className="text-sm text-gray-600">Dosage: {med.dosage}</div>}
            {med.route && <div className="text-sm text-gray-600">Route: {med.route}</div>}
            {med.reason && <div className="text-sm text-gray-600">Reason: {med.reason}</div>}
            {med.notes && <div className="text-sm text-gray-400 mt-1 italic">{med.notes}</div>}
          </div>
        ))}
        {(data.medications || []).length === 0 && (
          <p className="text-gray-400">No medications recorded.</p>
        )}
      </div>
    </div>
  );
};

const WellSkyContactsTab = ({ client, refreshNonce = 0 }) => {
  const { data, loading, refreshing, error } = useWellSkyClientResource(
    "relatedperson",
    client?.wellSkyId,
    refreshNonce,
  );

  if (!client?.wellSkyId) {
    return <div className="p-6 text-gray-500">No WellSky ID linked to this client.</div>;
  }
  if (loading) {
    return <div className="p-6 text-gray-500">Syncing contacts from WellSky…</div>;
  }
  if (refreshing) {
    return <div className="p-6 text-gray-500">Updating from WellSky…</div>;
  }
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!data || (data.contacts || []).length === 0) {
    return <div className="p-6 text-gray-500">No contacts data available from WellSky.</div>;
  }

  return (
    <div>
      <div className="space-y-3">
        {(data.contacts || []).map((contact, i) => (
          <div key={i} className="p-4 rounded-lg border border-gray-200 bg-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-gray-800">
                  {contact.firstName} {contact.lastName}
                </div>
                <div className="text-sm text-gray-500">{contact.relationship}</div>
              </div>
              <div className="flex gap-1 flex-wrap justify-end">
                {contact.emergencyContact && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                    Emergency
                  </span>
                )}
                {contact.primaryContact && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    Primary
                  </span>
                )}
                {contact.poa && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                    POA
                  </span>
                )}
                {contact.payer && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                    Payer
                  </span>
                )}
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600 space-y-0.5">
              {contact.phone && <div>📞 {contact.phone}</div>}
              {contact.mobile && <div>📱 {contact.mobile}</div>}
              {contact.email && <div>✉️ {contact.email}</div>}
              {contact.address && (
                <div>
                  📍 {contact.address}
                  {contact.city ? `, ${contact.city}` : ""}
                  {contact.state ? `, ${contact.state}` : ""}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WellSkyDataPanel = ({ client }) => {
  const [activeTab, setActiveTab] = useState("careplan");
  const [refreshNonce, setRefreshNonce] = useState(0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h2 className="text-xl font-bold">WellSky Data</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-sm font-medium text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg"
            onClick={() => setRefreshNonce((n) => n + 1)}
          >
            Sync from WellSky
          </button>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            Live pull on open &amp; sync
          </span>
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        {[
          { id: "careplan", label: "Care Plan" },
          { id: "condition", label: "Clinical" },
          { id: "medication", label: "Medications" },
          { id: "relatedperson", label: "Contacts" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "careplan" && (
        <WellSkyCarePlanTab client={client} refreshNonce={refreshNonce} />
      )}
      {activeTab === "condition" && (
        <WellSkyClinicalTab client={client} refreshNonce={refreshNonce} />
      )}
      {activeTab === "medication" && (
        <WellSkyMedicationsTab client={client} refreshNonce={refreshNonce} />
      )}
      {activeTab === "relatedperson" && (
        <WellSkyContactsTab client={client} refreshNonce={refreshNonce} />
      )}
    </div>
  );
};

// --- Main Component ---
const initialData = {
  // Basic Info
  phone: "",
  phone2: "",
  dob: "",
  gender: "",
  ethnicGroup: "",
  householdIncome: "",
  housingSituation: "",
  maritalStatus: "",
  minorsUnder18: "",
  veteranStatus: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  address: "",
  primaryCondition: "",
  secondaryCondition: "",
  clinicalNotes: "",
  email: "",
  primaryLanguage: "",
  householdComposition: "",
  mailingAddress: "",
  referralSource: "",
  referralDate: "",
  admissionDate: "",
  dischargeDate: "",
  caseManagerAssignment: "",
  branchOfficeLocation: "",
  primaryPayer: "",
  secondaryTertiaryPayer: "",
  careGoals: "",
  servicesOrdered: "",
  frequencyDurationOfServices: "",
  careLevelAcuity: "",
  functionalLimitations: "",
  specialInstructionsPrecautions: "",
  initialAssessmentResults: "",
  oasisData: "",
  reassessmentFindings: "",
  woundAssessments: "",
  preferredVisitDaysTimes: "",
  caregiverPreferences: "",
  primaryCaregiverDetails: "",
  caseNotes: "",
  familyCommunicationRecords: "",
  incidentReportsClient: "",
  satisfactionSurveyResponses: "",
  medicalHistorySummary: "",
  physiciansOfRecord: "",
  advanceDirectives: "",
  diagnosedConditionDate1: "",
  diagnosedConditionDate2: "",
  diagnosedConditionDate3: "",
  diagnosedConditionDate4: "",
  familyMembers: [{ relation: "", contactName: "", phone: "", email: "" }],

  // Health Metrics
  bodyTemp: "",
  heartRate: "",
  respiratoryRate: "",
  bpStatus: "",
  oxygenSat: "",
  weight: "",
  bodyMassIndex: "",
  sleepQuality: "",
  heartRateVariability: "",
  totalCholesterol: "",
  ldlCholesterol: "",
  hdlCholesterol: "",
  fastingBloodGlucose: "",
  diagnosedCondition1: "",
  diagnosedCondition2: "",
  diagnosedCondition3: "",
  diagnosedCondition4: "",
  diagnosedConditionSeverity1: "",
  diagnosedConditionSeverity2: "",
  diagnosedConditionSeverity3: "",
  diagnosedConditionSeverity4: "",
  medicationAllergies: "",
  prescribedMedication1: "",
  prescribedMedication2: "",
  prescribedMedication3: "",
  prescribedMedication4: "",
  prescribedMedication5: "",
  medicationAdherence: "",
  dietarySupplements: "",
  supplementMultivitamin: false,
  supplementVitaminD: false,
  supplementCalcium: false,
  supplementVitaminB12: false,
  supplementFishOilOmega3: false,
  supplementMagnesium: false,
  supplementProbiotics: false,
  supplementHerbal: false,
  supplementProtein: false,
  supplementJoint: false,
  supplementSleepAids: false,
  supplementImmuneBoosters: false,
  supplementOther: false,
  foodAllergies: "",
  vocalPower: "",
  vocalSpeed: "",
  vocalDynamic: "",
  vocalDepression: "",
  vocalAnxiety: "",

  // ADL (default to valid enum values)
  adlBathing: "not-assessed",
  adlDressing: "not-assessed",
  adlToileting: "not-assessed",
  adlTransferring: "not-assessed",
  adlContinence: "not-assessed",
  adlFeeding: "not-assessed", // legacy/back-compat
  adlEating: "not-assessed",
  // Legacy field names kept for UI wiring; mapped during save
  adlHygiene: "not-assessed",
  adlRestroom: "not-assessed",
  adlMobility: "not-assessed",

  // IADL (default to valid enum values)
  iadlMealPreparation: "not-assessed",
  iadlFinances: "not-assessed",
  iadlHousekeepingLaundry: "not-assessed",
  iadlShopping: "not-assessed",
  iadlMedication: "not-assessed",
  iadlTransportation: "not-assessed",
  iadlCommunication: "not-assessed",
  iadlHomeMaintenance: "not-assessed",

  // Living Conditions / Physical (defaults to numeric values)
  bmiCategory: "",
  weightChange: "",
  bedBound: "",
  transferDevice: "",
  rightGrip: 2,
  leftGrip: 2,
  gripStrengthLbs: "",
  gripLeftLbs: "", // per-hand lbs; rightGrip/leftGrip above are the 1-4 ratings
  gripRightLbs: "",
  chairStand: "",
  timedUpAndGo: "",
  gaitSpeedMs: "", // measured m/s; gaitSpeed below is the 0-5 FWB rating
  gaitSpeed: 3,
  mobilityDevice: 2,
  gaitObservation: "",
  fallsLastYear: 1,
  livingArrangement: "",
  homeAssistance: "",
  transportation: "",
  overnightSupervision: "",
  daytimeSupervisionCoverage: "",
  dietQuality: "",
  dailyProteinIntakeAdequacy: "",
  hydrationLevel: "",
  appetiteChanges: "",
  physicalActivityFrequency: "",
  strengthResistanceExercise: "",
  timeSpentOutdoors: "",
  communityEngagementLevel: "",
  transportationAccess: "",
  drivingStatus: "",
  accessToPrimaryCare: "",
  medicationManagementSupport: "",
  fallDetectionEmergencyAlert: "",
  emergencyResponseTime: "",
  financialStabilityForCare: "",
  accessToHealthyFood: "",
  technologyComfortUsage: "",
  petOwnership: "",
  petType: "",
  smoker: "",
  alcohol: "",
  alcoholConsumption: "",
  dailyNutrition: "",
  dailyHydrationIntake: "",
  dailyExerciseLevel: "",
  dailyPainFrequency: "",
  incomeRange: "",
  assistanceNeeded: "",
  clinicalStudyInterest: "",
  educationalInterests: "",
  newsletterInterest: "",

  // Care Team (leave empty until assessed)
  careDaysPerWeek: "",
  careCoverageLevel: "",
  totalVisits: "",
  visitsBySamePerson: "",
  totalScheduledVisits: "",
  visitsOnTime: "",
  carePlanStatus: "",
  familySatisfaction: "",
  ctReliabilityPercent: "",
  ctContinuityPercent: "",
  ctCareplanCurrent: null,
  ctFamilyCommunication: "",
  ctAssessed: false,

  // Environmental Safety (leave empty until assessed) - v2.0 camelCase fields
  esLooseRugs: null,
  esClutterWalkways: null,
  esPoorLighting: null,
  esStairsNoRailings: null,
  esSlipperyFloors: null,
  esNoGrabBars: null,
  esUnevenSteps: null,
  esCordsOnFloor: null,
  esUnevenFlooring: null,
  esFootwearNoTraction: null,
  esNoNightlights: null,
  esPetsUnderfoot: null,
  esUnstableFurniture: null,
  esItemsStoredImproper: null,
  esOtherFallHazard: null,
  esWetSlipperyFloors: null,
  esNoBathroomGrabBars: null,
  esSlipperyTub: null,
  esNoNonSlipStrips: null,
  esLooseBathMats: null,
  esLowToiletHeight: null,
  esLooseToiletSeat: null,
  esHighTubThreshold: null,
  esBathroomPoorLighting: null,
  esBathroomNoNightlight: null,
  esBathroomClutter: null,
  esItemsOutOfReach: null,
  esTowelBarsForSupport: null,
  esUnstableShowerChair: null,
  esCrampedBathroom: null,
  esOtherBathroomHazard: null,
  esKitchenWetFloors: null,
  esGreaseOnFloor: null,
  esLooseKitchenMats: null,
  esNoNonSlipBacking: null,
  esReachingHighCabinets: null,
  esUnsafeStepStool: null,
  esKitchenClutter: null,
  esNoFireExtinguisher: null,
  esBendingLowCabinets: null,
  esLooseDrawersDoors: null,
  esFrayedCords: null,
  esKitchenPoorLighting: null,
  esApplianceCordsInPath: null,
  esKitchenUnevenFlooring: null,
  esOverloadedOutlet: null,
  esNoSmokeDetector: null,
  esNoCO2Detector: null,
  esOtherKitchenHazard: null,
  esStepsThresholds: null,
  esNarrowHallways: null,
  esLayoutClutter: null,
  esInaccessibleBathroom: null,
  esMultiLevelHome: null,
  esTightDoorways: null,
  esObstructedPathways: null,
  environmentAssessed: false,
};

const AIP_ALLOWED_KEYS = new Set([
  ...Object.keys(initialData),
  "dateOfBirth",
  "firstName",
  "lastName",
  "street",
  "zip",
  "address",
  "phoneNumber",
]);

// Sections that live under the Integration sidebar tab rather than Client
// Snapshot. Kept as one list — when the nav cards and this set drift apart, a
// card lands on a section the set does not know about and the sidebar silently
// flips back to Snapshot.
// Clients the MedWand panel is switched on for, by id. A client is reachable
// under several ids depending on where the value came from — the route param,
// Mongo's _id, and the human-facing client number — so any of them may be
// listed here and all three are checked.
const MEDWAND_PILOT_CLIENT_IDS = new Set([
  "4403", // Bill Hess — same demo client as the Able Care sample
]);

const MEDWAND_PILOT_CLIENT_NAMES = new Set(["bill hess", "ethan foster"]);

const INTEGRATION_SIDEBAR_SECTIONS = new Set([
  "sensi-report",
  "pacsana",
  "activity-tracker",
  "health-indicators",
  "medwand",
  "care-link-360",
  "carelink360",
  "home-client-monitoring",
  "wellsky-data",
  "care-count",
  "vital-insights",
]);

function readActiveLocationVitalInsights() {
  try {
    const loc = JSON.parse(localStorage.getItem("activeLocation") || "null");
    if (!loc || typeof loc !== "object") {
      return {
        enabled: false,
        dashboardUrl: normalizeViAgencyDashboardUrl(null),
        hearthAgencyId: "",
      };
    }
    return {
      enabled: Boolean(loc.vitalInsightsIntegrationEnabled),
      dashboardUrl: normalizeViAgencyDashboardUrl(loc.vitalInsightsDashboardUrl),
      hearthAgencyId: String(loc.vitalInsightsHearthAgencyId || "").trim(),
    };
  } catch (_err) {
    return {
      enabled: false,
      dashboardUrl: normalizeViAgencyDashboardUrl(null),
      hearthAgencyId: "",
    };
  }
}

const AipAnalysis = () => {
  const { clientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialSection = (() => {
    const tab = String(searchParams.get("tab") || "").trim();
    if (tab === "visitNote" || location.state?.openVisitNote) return "visit-notes";
    if (
      tab === "shiftQuestions" ||
      tab === "clientQuestions" ||
      location.state?.openShiftQuestions
    ) {
      return "client-questions";
    }
    return null;
  })();

  const [activeSection, setActiveSection] = useState(initialSection);
  // SensiReport stays mounted once opened, so moving to another card doesn't
  // drop uploads still waiting their turn.
  const [sensiReportOpened, setSensiReportOpened] = useState(false);
  useEffect(() => {
    if (activeSection === "sensi-report") setSensiReportOpened(true);
  }, [activeSection]);
  const [snapshotSidebarTab, setSnapshotSidebarTab] = useState(() =>
    INTEGRATION_SIDEBAR_SECTIONS.has(initialSection) ? "integration" : "snapshot",
  );
  const [vitalInsightsAccess, setVitalInsightsAccess] = useState(
    readActiveLocationVitalInsights,
  );
  const [isLoading, setIsLoading] = useState(true);
  const contentDisplayRef = useRef(null);
  const integrationMainRef = useRef(null);
  const visitNotesDisplayRef = useRef(null);
  const [historyRange, setHistoryRange] = useState("last3m");
  const [aipHistory, setAipHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [sectionHistoryTab, setSectionHistoryTab] = useState({});
  const [sectionArchivedIndex, setSectionArchivedIndex] = useState({});

  // ✅ MODE DETECTION: Determine if this is Read-Only or Modeling Mode
  const isModelingMode = location.pathname.startsWith("/aip-smart-care");
  const isReadOnlyMode = location.pathname.startsWith("/client-management");

  // ✅ ADMIN CHECK: Get user role from localStorage
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = (currentUser?.role || "").toString().toUpperCase();

  // Allow manual edit for top-level admins/full access (no editors)
  const isAdmin =
    userRole === "ADMIN" ||
    userRole === "FULL_ACCESS" ||
    userRole === "SUPER ADMIN" ||
    userRole === "SUPER_ADMIN";

  // Debug: Log user info
  console.log("🔍 Admin Access Check:", {
    currentUser,
    userRole,
    isAdmin,
    canAccessManualEdit: isAdmin,
  });

  useEffect(() => {
    const refreshVitalInsightsAccess = () => {
      setVitalInsightsAccess(readActiveLocationVitalInsights());
    };
    refreshVitalInsightsAccess();
    const onStorage = (event) => {
      if (!event.key || event.key === "activeLocation") {
        refreshVitalInsightsAccess();
      }
    };
    const onActiveLocationUpdated = () => refreshVitalInsightsAccess();
    window.addEventListener("storage", onStorage);
    window.addEventListener("activeLocationUpdated", onActiveLocationUpdated);
    window.addEventListener("focus", refreshVitalInsightsAccess);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("activeLocationUpdated", onActiveLocationUpdated);
      window.removeEventListener("focus", refreshVitalInsightsAccess);
    };
  }, [location.pathname]);

  // ✅ MANUAL EDIT MODE: Allow admin to manually edit in read-only mode
  const [isManualEdit, setIsManualEdit] = useState(false);
  const [editBackup, setEditBackup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);

  // ✅ FIELD ENABLE LOGIC: Fields are editable when:
  // - In Modeling Mode (AIP Smart Care), OR
  // - In Manual Edit Mode (Admin editing in read-only)
  const fieldsAreEditable = isModelingMode || isManualEdit;
  const fieldsAreDisabled = !fieldsAreEditable;

  console.log("📝 Field Edit Status:", {
    isModelingMode,
    isReadOnlyMode,
    isManualEdit,
    fieldsAreEditable,
    fieldsAreDisabled,
  });

  // ✅ CONTEXT-AWARE NAVIGATION: Back button goes to correct parent
  const handleBack = () => {
    const careReturn = location.state?.careHubReturnTo;
    if (typeof careReturn === "string" && careReturn.startsWith("/")) {
      navigate(careReturn, { state: location.state?.careHubReturnState || undefined });
      return;
    }
    if (isModelingMode) {
      navigate("/aip-smart-care");
    } else {
      navigate("/client-management");
    }
  };

  const backLabel = (() => {
    if (location.state?.careHubReturnLabel) return location.state.careHubReturnLabel;
    if (isModelingMode) return "Back to AIP Smart Care";
    return "Back to All Clients";
  })();

  // Layer 4 → Layer 5: open the simulator for this client, and come back here
  // (keeping this page's own "Back to Triage" context) when they leave it.
  const handleExploreScenarios = () => {
    navigate(`/aip-smart-care/client/${clientId}`, {
      state: {
        careHubReturnTo: `${location.pathname}${location.search}`,
        careHubReturnLabel: "Back to AIP Score",
        careHubReturnState: location.state || null,
      },
    });
  };

  const [data, setData] = useState(initialData);
  // Keep a ref of latest data so save always uses current state (avoids stale closure when user picks avatar then immediately saves)
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  const [careTeamModel, setCareTeamModel] = useState(() => {
    const initialTotalVisits = 10;
    return {
      daysPerWeek: 7,
      scheduledVisits: initialTotalVisits,
      deliveredOnTime: initialTotalVisits,
      visitsBySamePerson: initialTotalVisits,
      totalVisits: initialTotalVisits,
      carePlanUpToDate: true,
      familySatisfaction: 3,
    };
  });
  const originalCareTeam = useRef(null);
  // Store original fetched data to allow resetting to DB state
  const [originalData, setOriginalData] = useState(initialData);
  const [isDirty, setIsDirty] = useState(false);
  const [canReset, setCanReset] = useState(false);
  const [careTeamDirty, setCareTeamDirty] = useState(false); // Track if CT has been modified
  const [successMessage, setSuccessMessage] = useState("");
  const [clientMeta, setClientMeta] = useState({});

  // The MedWand panel is shown for every client; only the pilot client's
  // sample exam data is. Everyone else sees the not-linked message.
  const medWandPilotClient =
    [clientId, clientMeta.id, clientMeta.clientId, clientMeta.clientNumber].some(
      (id) => id != null && MEDWAND_PILOT_CLIENT_IDS.has(String(id)),
    ) ||
    MEDWAND_PILOT_CLIENT_NAMES.has(
      `${clientMeta.firstName || ""} ${clientMeta.lastName || ""}`.trim().toLowerCase(),
    );

  // Sample Able Care screenings are shown for the demo client only, and only
  // until that client has real readings.
  const ableCareSampleClient =
    [clientId, clientMeta.id, clientMeta.clientId, clientMeta.clientNumber].some(
      (id) => id != null && ABLECARE_SAMPLE_CLIENT_IDS.has(String(id)),
    ) ||
    ABLECARE_SAMPLE_CLIENT_NAMES.has(
      `${clientMeta.firstName || ""} ${clientMeta.lastName || ""}`.trim().toLowerCase(),
    );

  // Sample PacSana sleep history, for the demo client only.
  const pacSanaSampleClient =
    [clientId, clientMeta.id, clientMeta.clientId, clientMeta.clientNumber].some(
      (id) => id != null && PACSANA_SAMPLE_CLIENT_IDS.has(String(id)),
    ) ||
    PACSANA_SAMPLE_CLIENT_NAMES.has(
      `${clientMeta.firstName || ""} ${clientMeta.lastName || ""}`.trim().toLowerCase(),
    );

  const [voiceHistory, setVoiceHistory] = useState([]);
  const [mobileVisits, setMobileVisits] = useState([]);
  const [mobileLoading, setMobileLoading] = useState(false);
  const { trackScoreChange } = useAipHistoryTracker(clientId);
  const [questionSetsCache, setQuestionSetsCache] = useState({});
  const [questionSetsLoading, setQuestionSetsLoading] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  // Modal State
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [calculatedScores, setCalculatedScores] = useState({
    aipScore: 0,
    wellBeingScore: 0,
    careTeamScore: 0,
    environment: 0,
  });

  // Legacy function - kept for backward compatibility (not used by new progress bar design)
  // eslint-disable-next-line no-unused-vars
  const scoreBand = (val) => {
    const v = Number(val) || 0;
    if (v === 0) return { cls: "gray", text: "No Data" };
    if (v >= 75) return { cls: "green", text: "Good" };
    if (v >= 40) return { cls: "orange", text: "Moderate" };
    return { cls: "red", text: "At Risk" };
  };

  // ✨ New Design Helper Functions for Progress Bars
  // (Still used for snapshot risk CSS class if needed elsewhere)

  // Care Team modeling calculator (frontend, modeling mode)
  const calculateCareTeamScore = (model) => {
    const coverageScore = Math.min(
      (Number(model.daysPerWeek || 0) / 7) * 100,
      100,
    );
    const delivered = Math.min(
      Number(model.deliveredOnTime || 0),
      Number(model.scheduledVisits || 0),
    );
    const reliabilityScore =
      Number(model.scheduledVisits || 0) > 0
        ? (delivered / Number(model.scheduledVisits || 1)) * 100
        : 0;
    const continuityScore =
      Number(model.totalVisits || 0) > 0
        ? (Number(model.visitsBySamePerson || 0) /
            Number(model.totalVisits || 1)) *
          100
        : 0;
    const coordinationScore =
      (model.carePlanUpToDate ? 60 : 0) +
      (Number(model.familySatisfaction || 1) / 5) * 40;
    const finalScore =
      coverageScore * 0.25 +
      reliabilityScore * 0.25 +
      continuityScore * 0.25 +
      coordinationScore * 0.25;
    return Math.round(Math.min(100, Math.max(0, finalScore)));
  };

  const careTeamScoreModel = useMemo(
    () => calculateCareTeamScore(careTeamModel),
    [careTeamModel],
  );

  useEffect(() => {
    if (fieldsAreEditable && careTeamDirty) {
      setCalculatedScores((prev) => ({
        ...prev,
        careTeamScore: Math.round(careTeamScoreModel || 0),
      }));
    }
  }, [fieldsAreEditable, careTeamDirty, careTeamScoreModel]);

  // ✅ FIX: Use stored CT score until user actively models Care Team
  // - Read-only mode: Always use stored score
  // - Modeling mode: Use stored score initially, switch to calculated once user makes changes
  const activeCareTeamScore =
    fieldsAreEditable && careTeamDirty
      ? careTeamScoreModel
      : calculatedScores.careTeamScore;

  // Align detail page with list page: prefer stored AIP score if available
  const storedAipScore = Number.isFinite(Number(clientMeta?.aipScore))
    ? Math.round(Number(clientMeta.aipScore))
    : null;
  const liveAipScore = Math.round(calculatedScores.aipScore || 0);
  // Well-Being / overall AIP follow the live v2 formula as ADL/IADL inputs change.
  const displayAipScore = liveAipScore;
  // displayAipStatus intentionally unused in snapshot-only UI

  // ✨ Note: Old band variables commented out - now using new progress bar design
  // const wbBand = scoreBand(calculatedScores.wellBeingScore);
  // const ctBand = scoreBand(activeCareTeamScore);
  // const aipBand = scoreBand(aipSummary.score ?? 0);
  // const esBand = scoreBand(calculatedScores.environment);

  const mapFunctionalValue = (val) => {
    if (val === undefined || val === null) return "not-assessed";
    const n = Number(val);
    if (n === 0) return "dependent";
    if (n === 1) return "assistance";
    if (n === 2) return "independent";
    // already string values pass through if they match expected enums
    if (typeof val === "string") return val;
    return "not-assessed";
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const client = await fetchClient(clientId);
        const derivedClientName = splitName(client?.name || client?.fullName || "");
        setClientMeta({
          ...mergeWellSkyStatusFields(client),
          id: client.id || client._id,
          clientNumber: client.clientNumber,
          clientId: client.clientId,
          dob: client.dob || "",
          companyId: client.companyId || null,
          firstName: client.firstName || derivedClientName.firstName || "",
          lastName: client.lastName || derivedClientName.lastName || "",
          street: client.street || "",
          city: client.city || "",
          state: client.state || "",
          zip: client.zip || "",
          address: client.address || "",
          latestAssessmentStatus: client.latestAssessmentStatus || "",
          isAipProvisional: client.isAipProvisional === true,
          aipScore: client.aipScore || 0,
          wellBeingScore: client.wellBeingScore || 0,
          careTeamScore: client.careTeamScore || 0,
          environmentScore: client.environmentScore || 0,
          voiceRecordedAt: client?.vocalEnergyMetrics?.recordedAt || null,
          voiceRaw: client?.vocalEnergyMetrics?.raw || null,
        });
        const applyClientProfileFallbacks = (target = {}) => {
          const next = { ...target };
          const clientStreet = String(client?.street || "").trim();
          const clientCity = String(client?.city || "").trim();
          const clientState = String(client?.state || "").trim();
          const clientZip = String(client?.zip || "").trim();
          const clientAddress = String(client?.address || "").trim();

          next.firstName = isNonEmptyText(next.firstName)
            ? next.firstName.trim()
            : (client.firstName || derivedClientName.firstName || "");
          next.lastName = isNonEmptyText(next.lastName)
            ? next.lastName.trim()
            : (client.lastName || derivedClientName.lastName || "");
          if (!isNonEmptyText(next.dob) && client.dob) {
            next.dob = client.dob;
          }

          next.street = isNonEmptyText(next.street) ? next.street.trim() : clientStreet;
          next.city = isNonEmptyText(next.city) ? next.city.trim() : clientCity;
          next.state = isNonEmptyText(next.state) ? next.state.trim() : clientState;
          next.zip = isNonEmptyText(next.zip) ? next.zip.trim() : clientZip;
          next.address = isNonEmptyText(next.address) ? next.address.trim() : clientAddress;

          next.phone = isNonEmptyText(next.phone)
            ? next.phone.trim()
            : String(client.phone || client.phoneNumber || "").trim();
          next.phone2 = isNonEmptyText(next.phone2)
            ? next.phone2.trim()
            : String(client.phone2 || "").trim();
          next.branchOfficeLocation = isNonEmptyText(next.branchOfficeLocation)
            ? next.branchOfficeLocation.trim()
            : String(client.locationName || "").trim();
          next.advanceDirectives = isNonEmptyText(next.advanceDirectives)
            ? next.advanceDirectives.trim()
            : String(client.dnr || "").trim();
          next.functionalLimitations = isNonEmptyText(next.functionalLimitations)
            ? next.functionalLimitations.trim()
            : String(client.functionalLimitationsSummary || "").trim();
          next.avatarUrl = isNonEmptyText(next.avatarUrl)
            ? next.avatarUrl.trim()
            : String(client.avatarUrl || client.profileImage || client.photoUrl || "").trim();
          return next;
        };
        const normalizedVoiceHistory = (
          Array.isArray(client?.vocalEnergyHistory) ? client.vocalEnergyHistory : []
        )
          .filter((row) => row && typeof row === "object")
          .sort((a, b) => {
            const aTs = new Date(a?.recordedAt || a?.archivedAt || 0).getTime();
            const bTs = new Date(b?.recordedAt || b?.archivedAt || 0).getTime();
            return bTs - aTs;
          });
        setVoiceHistory(normalizedVoiceHistory);
        if (
          client.aipAnalysisData &&
          Object.keys(client.aipAnalysisData).length > 0
        ) {
          const loadedData = applyClientProfileFallbacks({
            ...initialData,
            ...client.aipAnalysisData,
          });

          // Capture raw numeric ADL values before mapping to display enums
          loadedData.adlBathingRaw = Number.isFinite(
            Number(loadedData.adlBathing),
          )
            ? Number(loadedData.adlBathing)
            : null;
          loadedData.adlDressingRaw = Number.isFinite(
            Number(loadedData.adlDressing),
          )
            ? Number(loadedData.adlDressing)
            : null;
          loadedData.adlEatingRaw = Number.isFinite(
            Number(loadedData.adlEating ?? loadedData.adlFeeding),
          )
            ? Number(loadedData.adlEating ?? loadedData.adlFeeding)
            : null;
          loadedData.adlTransferringRaw = Number.isFinite(
            Number(loadedData.adlTransferring ?? loadedData.adlMobility),
          )
            ? Number(loadedData.adlTransferring ?? loadedData.adlMobility)
            : null;
          loadedData.adlToiletingRaw = Number.isFinite(
            Number(loadedData.adlToileting ?? loadedData.adlRestroom),
          )
            ? Number(loadedData.adlToileting ?? loadedData.adlRestroom)
            : null;
          loadedData.adlContinenceRaw = Number.isFinite(
            Number(loadedData.adlContinence),
          )
            ? Number(loadedData.adlContinence)
            : null;

          // Capture raw numeric IADL values before mapping to display enums
          loadedData.iadlMealPreparationRaw = Number.isFinite(
            Number(loadedData.iadlMealPreparation ?? loadedData.iadlMeals),
          )
            ? Number(loadedData.iadlMealPreparation ?? loadedData.iadlMeals)
            : null;
          loadedData.iadlFinancesRaw = Number.isFinite(
            Number(loadedData.iadlFinances),
          )
            ? Number(loadedData.iadlFinances)
            : null;
          loadedData.iadlHousekeepingLaundryRaw = Number.isFinite(
            Number(
              loadedData.iadlHousekeepingLaundry ??
                loadedData.iadlHousekeeping ??
                loadedData.iadlHomemaintenance,
            ),
          )
            ? Number(
                loadedData.iadlHousekeepingLaundry ??
                  loadedData.iadlHousekeeping ??
                  loadedData.iadlHomemaintenance,
              )
            : null;
          loadedData.iadlShoppingRaw = Number.isFinite(
            Number(loadedData.iadlShopping),
          )
            ? Number(loadedData.iadlShopping)
            : null;
          loadedData.iadlMedicationRaw = Number.isFinite(
            Number(loadedData.iadlMedication),
          )
            ? Number(loadedData.iadlMedication)
            : null;
          loadedData.iadlTransportationRaw = Number.isFinite(
            Number(loadedData.iadlTransportation),
          )
            ? Number(loadedData.iadlTransportation)
            : null;
          loadedData.iadlCommunicationRaw = Number.isFinite(
            Number(loadedData.iadlCommunication),
          )
            ? Number(loadedData.iadlCommunication)
            : null;
          loadedData.iadlHomeMaintenanceRaw = Number.isFinite(
            Number(
              loadedData.iadlHomeMaintenance ?? loadedData.iadlHomemaintenance,
            ),
          )
            ? Number(
                loadedData.iadlHomeMaintenance ??
                  loadedData.iadlHomemaintenance,
              )
            : null;

          // Map numeric ADL/IADL values (0/1/2) into UI enums
          const adlKeys = [
            "adlBathing",
            "adlDressing",
            "adlEating",
            "adlFeeding",
            "adlTransferring",
            "adlMobility",
            "adlToileting",
            "adlRestroom",
            "adlContinence",
          ];
          adlKeys.forEach((k) => {
            if (loadedData[k] !== undefined) {
              loadedData[k] = mapFunctionalValue(loadedData[k]);
            }
          });

          const iadlKeys = [
            "iadlFinances",
            "iadlTransportation",
            "iadlShopping",
            "iadlMeals",
            "iadCommunication",
            "iadlMedication",
            "iadlHousekeeping",
            "iadlHomemaintenance",
          ];
          iadlKeys.forEach((k) => {
            if (loadedData[k] !== undefined) {
              loadedData[k] = mapFunctionalValue(loadedData[k]);
            }
          });

          if (client.vocalEnergyMetrics) {
            loadedData.vocalPower = client.vocalEnergyMetrics.power ?? "";
            loadedData.vocalSpeed = client.vocalEnergyMetrics.speed ?? "";
            loadedData.vocalDynamic = client.vocalEnergyMetrics.dynamic ?? "";
            loadedData.vocalDepression =
              client.vocalEnergyMetrics.depression ?? "";
            loadedData.vocalAnxiety = client.vocalEnergyMetrics.anxiety ?? "";
            loadedData.vocalAggression = client.vocalEnergyMetrics.dynamic ?? 0;
            loadedData.vocalWellness = client.vocalEnergyMetrics.wellness ?? 0;
          }
          const mergedLoadedData = client.wellSkyId
            ? mergeWellSkySnapshotPatch(
                loadedData,
                await loadWellSkySnapshotPatch(client.wellSkyId).catch(() => ({})),
              )
            : loadedData;
          setData(mergedLoadedData);
          setOriginalData(mergedLoadedData);
          setCalculatedScores((prev) => ({
            ...prev,
            vocalAnxiety: Math.round(
              client.vocalEnergyMetrics?.anxiety ??
                loadedData.vocalAnxiety ??
                0,
            ),
            vocalDepression: Math.round(
              client.vocalEnergyMetrics?.depression ??
                loadedData.vocalDepression ??
                0,
            ),
            vocalAggression: Math.round(
              client.vocalEnergyMetrics?.dynamic ??
                loadedData.vocalAggression ??
                0,
            ),
            vocalWellness: Math.round(
              client.vocalEnergyMetrics?.wellness ??
                loadedData.vocalWellness ??
                0,
            ),
          }));
          setCanReset(false);
        } else {
          const loadedData = applyClientProfileFallbacks({ ...initialData });
          if (client.vocalEnergyMetrics) {
            loadedData.vocalPower = client.vocalEnergyMetrics.power ?? "";
            loadedData.vocalSpeed = client.vocalEnergyMetrics.speed ?? "";
            loadedData.vocalDynamic = client.vocalEnergyMetrics.dynamic ?? "";
            loadedData.vocalDepression =
              client.vocalEnergyMetrics.depression ?? "";
            loadedData.vocalAnxiety = client.vocalEnergyMetrics.anxiety ?? "";
            loadedData.vocalAggression = client.vocalEnergyMetrics.dynamic ?? 0;
            loadedData.vocalWellness = client.vocalEnergyMetrics.wellness ?? 0;
          }
          const mergedEmptyData = client.wellSkyId
            ? mergeWellSkySnapshotPatch(
                loadedData,
                await loadWellSkySnapshotPatch(client.wellSkyId).catch(() => ({})),
              )
            : loadedData;
          setData(mergedEmptyData);
          setOriginalData(mergedEmptyData);
          setCanReset(false);
        }
        // Set initial scores from client record
        setCareTeamDirty(false); // ✅ Reset Care Team dirty flag on load
        setCalculatedScores({
          aipScore: client.aipScore || 0,
          wellBeingScore: client.wellBeingScore || 0,
          careTeamScore: client.careTeamScore || 0,
          environment: client.environmentScore || client.environment || 0,
        });
        // Initialize care team model from client data
        // IMPORTANT: Care team fields are stored in client.aipAnalysisData, not at client top-level.
        // Use aip first to avoid stale React state (data) and ensure saved values persist on reload.
        const aipForInit = client.aipAnalysisData || {};
        const initialTotalVisits = Number(
          aipForInit.totalVisits ??
            client.totalVisits ??
            data.totalVisits ??
            10,
        );
        const initialCareTeam = {
          daysPerWeek: Number(
            aipForInit.careDaysPerWeek ??
              client.careDaysPerWeek ??
              data.careDaysPerWeek ??
              7,
          ),
          scheduledVisits: Number(
            aipForInit.totalScheduledVisits ??
              client.totalScheduledVisits ??
              data.totalScheduledVisits ??
              initialTotalVisits,
          ),
          deliveredOnTime: Number(
            aipForInit.visitsOnTime ??
              client.visitsOnTime ??
              data.visitsOnTime ??
              initialTotalVisits,
          ),
          visitsBySamePerson: Number(
            aipForInit.visitsBySamePerson ??
              client.visitsBySamePerson ??
              data.visitsBySamePerson ??
              initialTotalVisits,
          ),
          totalVisits: initialTotalVisits,
          carePlanUpToDate:
            client.carePlanStatus === "Up to Date" ||
            client.ctCareplanCurrent === true ||
            aipForInit.ctCareplanCurrent === true ||
            aipForInit.carePlanStatus === "Up to Date" ||
            data.carePlanStatus === "Up to Date",
          familySatisfaction: Number(
            aipForInit.ctFamilyCommunication ??
              aipForInit.familySatisfaction ??
              client.ctFamilyCommunication ??
              client.familySatisfaction ??
              data.familySatisfaction ??
              3,
          ),
        };
        const initialFromWellSky = hydrateCareTeamModelFromWellSkyMetrics(
          { ...aipForInit, ...(client.aipAnalysisData || {}) },
          initialCareTeam,
        );
        const resolvedInitialCareTeam = initialFromWellSky || initialCareTeam;
        setCareTeamModel(resolvedInitialCareTeam);
        originalCareTeam.current = resolvedInitialCareTeam;

        // Fetch latest advice/assessment to populate ES (and other fields) if available
        try {
          const latest = await fetchLatestAdviceForClient(clientId);
          console.log("Latest assessment for ES:", latest);
          if (latest) {
            if (latest.status) {
              setClientMeta((prev) => ({
                ...prev,
                latestAssessmentStatus: latest.status,
                isAipProvisional: latest.status === "auto-provisional",
              }));
            }
            // Merge latest assessment fields into form data so ES calc has full context
            // IMPORTANT: Prioritize client.aipAnalysisData for care team fields - 'latest'
            // often comes from mobile check-ins that don't have care team data and would
            // overwrite with zeros.
            const mergedData = {
              ...initialData,
              ...(client.aipAnalysisData || {}),
              ...latest,
            };
            if (mergedData.iadlMeals && !mergedData.iadlMealPreparation) {
              mergedData.iadlMealPreparation = mergedData.iadlMeals;
            }
            if (mergedData.iadlHousekeeping && !mergedData.iadlHousekeepingLaundry) {
              mergedData.iadlHousekeepingLaundry = mergedData.iadlHousekeeping;
            }
            const hydratedMergedData = applyClientProfileFallbacks(mergedData);
            setData(hydratedMergedData);
            setOriginalData(hydratedMergedData);
            const aip = client.aipAnalysisData || {};
            const defTotal = 10;
            const mergedTotalVisits = Number(
              client.totalVisits ??
                aip.totalVisits ??
                hydratedMergedData.totalVisits ??
                data.totalVisits ??
                defTotal,
            );
            const mergedCareTeam = {
              daysPerWeek: Number(
                aip.careDaysPerWeek ??
                  client.careDaysPerWeek ??
                  hydratedMergedData.careDaysPerWeek ??
                  hydratedMergedData.ctDaysPerWeek ??
                  7,
              ),
              scheduledVisits: Number(
                aip.totalScheduledVisits ??
                  client.totalScheduledVisits ??
                  hydratedMergedData.totalScheduledVisits ??
                  mergedTotalVisits ??
                  defTotal,
              ),
              deliveredOnTime: Number(
                aip.visitsOnTime ??
                  client.visitsOnTime ??
                  hydratedMergedData.visitsOnTime ??
                  mergedTotalVisits ??
                  defTotal,
              ),
              visitsBySamePerson: Number(
                aip.visitsBySamePerson ??
                  client.visitsBySamePerson ??
                  hydratedMergedData.visitsBySamePerson ??
                  mergedTotalVisits ??
                  defTotal,
              ),
              totalVisits: mergedTotalVisits,
              carePlanUpToDate:
                (aip.carePlanStatus ?? hydratedMergedData.carePlanStatus) ===
                  "Up to Date" ||
                aip.ctCareplanCurrent === true ||
                hydratedMergedData.ctCareplanCurrent === true,
              familySatisfaction: Number(
                aip.ctFamilyCommunication ??
                  aip.familySatisfaction ??
                  client.ctFamilyCommunication ??
                  client.familySatisfaction ??
                  hydratedMergedData.ctFamilyCommunication ??
                  hydratedMergedData.familySatisfaction ??
                  3,
              ),
            };
            const mergedFromWellSky = hydrateCareTeamModelFromWellSkyMetrics(
              hydratedMergedData,
              mergedCareTeam,
            );
            const resolvedMergedCareTeam = mergedFromWellSky || mergedCareTeam;
            setCareTeamModel(resolvedMergedCareTeam);
            originalCareTeam.current = resolvedMergedCareTeam;

            if (
              latest.scores &&
              !latest.isModeling &&
              latest.source !== "AIP Care Modeling"
            ) {
              setCalculatedScores((prev) => ({
                ...prev,
                environment:
                  latest.scores.environment ??
                  latest.esScore ??
                  prev.environment ??
                  0,
                wellBeingScore:
                  latest.scores.wellBeing ??
                  latest.fwbScore ??
                  prev.wellBeingScore,
                careTeamScore:
                  latest.scores.careTeam ??
                  latest.ctScore ??
                  prev.careTeamScore,
                aipScore: latest.scores.aip ?? latest.aipScore ?? prev.aipScore,
              }));
            }
          }
        } catch (innerErr) {
          console.warn("Could not fetch latest assessment for ES:", innerErr);
        }
      } catch (err) {
        console.warn("Could not fetch client data (might be mock ID):", err);
      } finally {
        setIsLoading(false);
      }
    };
    if (clientId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    const loadMobile = async () => {
      if (!clientId) return;
      setMobileVisits([]); // reset on client change to avoid stale bleed
      setMobileLoading(true);
      try {
        const appts = await fetchAppointments({ clientId });
        const clientIdStr = (clientId || "").toString();
        const filtered = (appts || [])
          .filter((a) => {
            const apptClient =
              a.clientId && typeof a.clientId === "object"
                ? a.clientId._id || a.clientId.id || a.clientId
                : a.clientId;
            return apptClient && apptClient.toString() === clientIdStr;
          })
          .filter(
            (a) =>
              a.status === "Checked In" ||
              a.status === "Checked Out" ||
              (a.questionAnswers &&
                Object.keys(a.questionAnswers || {}).length > 0),
          )
          .sort(
            (a, b) =>
              new Date(b.checkOutTime || b.checkInTime || b.start) -
              new Date(a.checkOutTime || a.checkInTime || a.start),
          );
        setMobileVisits(filtered);
      } catch (err) {
        console.error("Failed to load mobile visit answers", err);
      } finally {
        setMobileLoading(false);
      }
    };
    loadMobile();
  }, [clientId]);

  useEffect(() => {
    const uniqueIds = new Set();
    mobileVisits.forEach((visit) => {
      const showCheckIn = visit?.showCheckInQuestions !== false;
      const showCheckOut = visit?.showCheckOutQuestions !== false;
      const checkInId = normalizeQuestionSetId(
        visit.checkInQuestionSetId || visit.checkInQuestionSet,
      );
      const checkOutId = normalizeQuestionSetId(
        visit.checkOutQuestionSetId || visit.checkOutQuestionSet,
      );
      if (showCheckIn && checkInId) uniqueIds.add(checkInId);
      if (showCheckOut && checkOutId) uniqueIds.add(checkOutId);
    });

    const missing = Array.from(uniqueIds).filter(
      (id) => id && !questionSetsCache[id],
    );
    if (missing.length === 0) return;

    let isActive = true;
    const fetchSets = async () => {
      setQuestionSetsLoading(true);
      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const set = await fetchQuestionSet(id);
              return { id, set };
            } catch (err) {
              console.warn(`Failed to fetch question set ${id}`, err);
              return null;
            }
          }),
        );
        if (!isActive) return;
        setQuestionSetsCache((prev) => {
          const next = { ...prev };
          results.forEach((entry) => {
            if (entry?.set) {
              next[entry.id] = entry.set;
            }
          });
          return next;
        });
      } finally {
        if (isActive) {
          setQuestionSetsLoading(false);
        }
      }
    };

    fetchSets();
    return () => {
      isActive = false;
    };
  }, [mobileVisits, questionSetsCache]);

  const fallHazardKeys = [
    "esLooseRugs",
    "esClutterWalkways",
    "esPoorLighting",
    "esStairsNoRailings",
    "esSlipperyFloors",
    "esNoGrabBars",
    "esUnevenSteps",
    "esCordsOnFloor",
    "esUnevenFlooring",
    "esFootwearNoTraction",
    "esNoNightlights",
    "esPetsUnderfoot",
    "esUnstableFurniture",
    "esItemsStoredImproper",
    "esOtherFallHazard",
  ];
  const bathroomHazardKeys = [
    "esUnstableShowerChair",
    "esWetSlipperyFloors",
    "esNoBathroomGrabBars",
    "esSlipperyTub",
    "esNoNonSlipStrips",
    "esLooseBathMats",
    "esLowToiletHeight",
    "esLooseToiletSeat",
    "esHighTubThreshold",
    "esBathroomPoorLighting",
    "esBathroomNoNightlight",
    "esBathroomClutter",
    "esItemsOutOfReach",
    "esTowelBarsForSupport",
    "esCrampedBathroom",
    "esOtherBathroomHazard",
  ];
  const kitchenHazardKeys = [
    "esKitchenWetFloors",
    "esGreaseOnFloor",
    "esLooseKitchenMats",
    "esNoNonSlipBacking",
    "esReachingHighCabinets",
    "esUnsafeStepStool",
    "esKitchenClutter",
    "esNoFireExtinguisher",
    "esBendingLowCabinets",
    "esLooseDrawersDoors",
    "esFrayedCords",
    "esKitchenPoorLighting",
    "esApplianceCordsInPath",
    "esKitchenUnevenFlooring",
    "esOverloadedOutlet",
    "esNoSmokeDetector",
    "esNoCO2Detector",
    "esOtherKitchenHazard",
  ];
  const layoutHazardKeys = [
    "esStepsThresholds",
    "esNarrowHallways",
    "esLayoutClutter",
    "esInaccessibleBathroom",
    "esMultiLevelHome",
    "esTightDoorways",
    "esObstructedPathways",
  ];

  const deriveHomeSafetyScores = (values) => {
    const scoreFromHazards = (keys) => {
      const count = keys.filter((key) => values[key]).length;
      if (count >= 6) return 0;
      if (count >= 4) return 1;
      if (count >= 2) return 2;
      if (count === 1) return 3;
      return 4;
    };

    return {
      homeSafetyFallHazards: scoreFromHazards(fallHazardKeys),
      homeSafetyBathroom: scoreFromHazards(bathroomHazardKeys),
      homeSafetyKitchen: scoreFromHazards(kitchenHazardKeys),
      homeSafetyLayout: scoreFromHazards(layoutHazardKeys),
    };
  };

  const handleChange = (field, value) => {
    console.log(`🔄 Field changed: ${field} = ${value}`);
    setData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field.startsWith("es")) {
        console.log(
          `📊 ES Field Update: ${field} = ${value} (type: ${typeof value})`,
        );
        const scores = deriveHomeSafetyScores(updated);
        const result = { ...updated, ...scores, environmentAssessed: true };
        dataRef.current = result;
        return result;
      }
      dataRef.current = updated;
      return updated;
    });
    setIsDirty(true);
    setCanReset(true);
  };

  const handleReset = () => {
    setData(originalData);
    setIsDirty(true); // Mark as dirty so user can save the reverted state
    setCanReset(false);
    setCareTeamDirty(false); // ✅ Reset Care Team dirty flag on reset
    if (originalCareTeam.current) {
      setCareTeamModel(originalCareTeam.current);
    }
  };

  const handleCareTeamChange = (updater) => {
    setCareTeamModel((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const toNonNegative = (v) => Math.max(0, Number(v ?? 0) || 0);
      const scheduledVisits = toNonNegative(next.scheduledVisits);
      const totalVisits = toNonNegative(next.totalVisits);
      const deliveredOnTime = Math.min(
        toNonNegative(next.deliveredOnTime),
        scheduledVisits,
      );
      const visitsBySamePerson = Math.min(
        toNonNegative(next.visitsBySamePerson),
        totalVisits,
      );
      const normalized = {
        ...next,
        daysPerWeek: toNonNegative(next.daysPerWeek),
        scheduledVisits,
        deliveredOnTime,
        visitsBySamePerson,
        totalVisits,
        familySatisfaction: toNonNegative(next.familySatisfaction),
      };

      setData((prevData) => ({
        ...prevData,
        careDaysPerWeek: normalized.daysPerWeek,
        totalScheduledVisits: normalized.scheduledVisits,
        visitsOnTime: normalized.deliveredOnTime,
        visitsBySamePerson: normalized.visitsBySamePerson,
        totalVisits: normalized.totalVisits,
        carePlanStatus: normalized.carePlanUpToDate
          ? "Up to Date"
          : "Needs Update",
        familySatisfaction: normalized.familySatisfaction,
        ctAssessed: true,
      }));

      return normalized;
    });
    setCareTeamDirty(true); // ✅ Mark Care Team as modified
    setIsDirty(true);
    setCanReset(true);
  };

  const formatVisitRange = (visit) => {
    const start = visit?.checkInTime || visit?.start;
    const end = visit?.checkOutTime || visit?.end;
    if (!start) return "--";
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;
    const dateFmt = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeFmt = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    const base = `${dateFmt.format(startDate)} • ${timeFmt.format(startDate)}`;
    if (!endDate || Number.isNaN(endDate.getTime())) return base;
    return `${base} – ${timeFmt.format(endDate)}`;
  };

  const normalizeQuestionSetId = (rawId) => {
    if (!rawId) return "";
    if (typeof rawId === "object") {
      return rawId._id || rawId.id || rawId.toString?.() || "";
    }
    return rawId.toString();
  };

  const getQuestionSetForStage = (visit, stage) => {
    const enabled =
      stage === "checkIn"
        ? visit?.showCheckInQuestions !== false
        : visit?.showCheckOutQuestions !== false;
    if (!enabled) return null;
    const rawId =
      stage === "checkIn"
        ? visit.checkInQuestionSetId || visit.checkInQuestionSet
        : visit.checkOutQuestionSetId || visit.checkOutQuestionSet;
    const id = normalizeQuestionSetId(rawId);
    if (!id) return null;
    return questionSetsCache[id] || null;
  };

  const getStageAnswers = (visit, stage) => {
    const enabled =
      stage === "checkIn"
        ? visit?.showCheckInQuestions !== false
        : visit?.showCheckOutQuestions !== false;
    if (!enabled) return null;
    const qa = visit.questionAnswers || {};
    const checkInCandidates = [
      visit.checkInQuestionAnswers,
      visit.checkInAnswers,
      visit.checkinAnswers,
      visit.checkInResponse,
      visit.checkinResponse,
      visit.checkInData,
      qa?.checkInAnswers,
      qa?.check_in_answers,
      qa?.checkIn,
      qa?.check_in,
      qa?.checkin,
    ].filter(Boolean);
    const checkOutCandidates = [
      visit.checkOutQuestionAnswers,
      visit.checkOutAnswers,
      visit.checkoutAnswers,
      visit.checkOutResponse,
      visit.checkoutResponse,
      qa?.checkOutAnswers,
      qa?.check_out_answers,
      qa?.checkOut,
      qa?.check_out,
      qa?.checkout,
    ].filter(Boolean);

    if (stage === "checkIn") {
      if (checkInCandidates.length > 0) return checkInCandidates[0];
      if (visit.status === "Checked In") return qa;
      // Fallback: if nothing found, use the shared answers blob
      if (Object.keys(qa || {}).length > 0) return qa;
    }
    if (stage === "checkOut") {
      if (checkOutCandidates.length > 0) return checkOutCandidates[0];
      if (visit.status === "Checked Out") return qa;
      if (Object.keys(qa || {}).length > 0) return qa;
    }
    return null;
  };

  const formatAnswerValue = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const buildQuestionRows = (questions = [], answers = {}) => {
    if (questions.length === 0 && answers && Object.keys(answers).length > 0) {
      return Object.entries(answers).map(([key, val]) => ({
        key,
        label: `Q${Number(key) + 1 || key}`,
        answer: formatAnswerValue(val),
      }));
    }

    return questions.map((q, idx) => {
      const answer =
        answers?.[idx] ??
        answers?.[String(idx)] ??
        answers?.[q.text] ??
        answers?.[q.id];
      return {
        key: q.text || `q-${idx}`,
        label: q.text || `Question ${idx + 1}`,
        answer: formatAnswerValue(answer),
      };
    });
  };

  const renderStageTable = (visit, stage) => {
    const enabled =
      stage === "checkIn"
        ? visit?.showCheckInQuestions !== false
        : visit?.showCheckOutQuestions !== false;
    if (!enabled) return null;
    const isCheckIn = stage === "checkIn";
    const stageAnswers = getStageAnswers(visit, stage) || {};
    const questionSet = getQuestionSetForStage(visit, stage);
    const rows = buildQuestionRows(questionSet?.questions || [], stageAnswers);
    const hasRows = rows.length > 0;

    return (
      <div className={`qa-table-card ${isCheckIn ? "check-in" : "check-out"}`}>
        <div className="qa-table-head">
          <div className={`stage-tag ${isCheckIn ? "check-in" : "check-out"}`}>
            {isCheckIn ? "Check-in" : "Check-out"}
          </div>
          <div className="qa-table-meta">
            {questionSet?.title || "No question set assigned"}
            {questionSet?.questions?.length
              ? ` • ${questionSet.questions.length} question${
                  questionSet.questions.length === 1 ? "" : "s"
                }`
              : ""}
          </div>
        </div>
        {hasRows ? (
          <table className="qa-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Answer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${stage}-${row.key}`}>
                  <td>{row.label}</td>
                  <td>
                    {row.answer && row.answer.trim() !== "" ? (
                      row.answer
                    ) : (
                      <span className="muted">No response</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="qa-empty muted">No responses recorded.</div>
        )}
      </div>
    );
  };

  // ✅ MANUAL EDIT HANDLERS: For admin to edit assessment data
  const handleEnterManualEdit = () => {
    // ✅ SECURITY: Only admins can enter manual edit mode
    if (!isAdmin) {
      console.error("❌ Access Denied: Manual Edit requires Admin role");
      setSuccessMessage("❌ Access Denied: Admin role required");
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }

    console.log("🔘 Manual Edit button clicked by Admin");
    console.log("✅ Admin verified - Entering manual edit mode");

    // Backup current data
    setEditBackup(JSON.parse(JSON.stringify({ data, careTeamModel })));
    setIsManualEdit(true);

    console.log("✏️ Manual edit mode enabled");
  };

  const handleCancelManualEdit = () => {
    // Restore backup
    if (editBackup) {
      setData(editBackup.data);
      setCareTeamModel(editBackup.careTeamModel);
    }
    setIsManualEdit(false);
    setEditBackup(null);
    setIsDirty(false);
    console.log("❌ Cancelled manual edit mode");
  };

  const handleCloseClientInfoModal = () => {
    // If user was editing, cancel to avoid leaving the page in manual edit mode.
    if (isManualEdit) {
      handleCancelManualEdit();
    }
    setShowClientInfoModal(false);
  };

  const handleSaveManualEdit = async () => {
    // ✅ SECURITY: Only admins can save manual edits
    if (!isAdmin) {
      console.error(
        "❌ Access Denied: Saving manual edits requires Admin role",
      );
      setSuccessMessage("❌ Access Denied: Admin role required");
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }

    try {
      // Commit any pending onBlur handlers (DOB, clamps, etc.) before reading `data`.
      if (typeof document !== "undefined") {
        const active = document.activeElement;
        if (active && typeof active.blur === "function") active.blur();
        await new Promise((r) => setTimeout(r, 0));
      }

      setSaving(true);
      console.log("💾 Saving manual edits by Admin...");
      const previousScores = getCurrentScoresSnapshot();

      // Persist edits directly to the client record (includes aipAnalysisData + scores)
      const careTeamData = careTeamDirty
        ? {
            careDaysPerWeek: Number(careTeamModel.daysPerWeek ?? 0),
            totalScheduledVisits: Number(careTeamModel.scheduledVisits ?? 0),
            visitsOnTime: Number(careTeamModel.deliveredOnTime ?? 0),
            visitsBySamePerson: Number(careTeamModel.visitsBySamePerson ?? 0),
            totalVisits: Number(careTeamModel.totalVisits ?? 0),
            carePlanStatus: careTeamModel.carePlanUpToDate
              ? "Up to Date"
              : "Needs Update",
            familySatisfaction: Number(careTeamModel.familySatisfaction ?? 0),
          }
        : {};
      // Use dataRef to avoid stale closure when user selects photo then clicks Save quickly
      const latestData = dataRef.current ?? data;
      const mergedSaveData = { ...latestData, ...careTeamData };
      // Keep the nested assessment payload lean: do not duplicate avatar blobs there.
      // Large base64 images in `aipAnalysisData` can trigger proxy/body limits and surface
      // as generic "Network Error" on save.
      const sanitizedSaveData = { ...mergedSaveData };
      delete sanitizedSaveData.avatarUrl;
      delete sanitizedSaveData.profileImage;
      delete sanitizedSaveData.photoUrl;
      const filteredSaveData = Object.fromEntries(
        Object.entries(sanitizedSaveData).filter(([key]) =>
          AIP_ALLOWED_KEYS.has(key),
        ),
      );

      const MAX_INLINE_AVATAR_CHARS = 140000;
      const AVATAR_UPLOAD_TARGET_MAX_CHARS = 9000;
      const AVATAR_UPLOAD_RETRY_MAX_CHARS = 7000;
      const nextAvatarValue =
        typeof latestData?.avatarUrl === "string"
          ? latestData.avatarUrl
          : "";
      const previousAvatarValue =
        typeof originalData?.avatarUrl === "string"
          ? originalData.avatarUrl
          : "";
      const avatarTooLarge =
        nextAvatarValue.startsWith("data:") &&
        nextAvatarValue.length > MAX_INLINE_AVATAR_CHARS;
      const avatarChanged = nextAvatarValue !== previousAvatarValue;
      const shouldSendAvatar =
        avatarChanged && (nextAvatarValue === "" || !avatarTooLarge);
      // Normalize DOB fields so both legacy/new backend fields stay in sync.
      // Some clients store `dob`, others store `dateOfBirth`.
      if (filteredSaveData.dob && !filteredSaveData.dateOfBirth) {
        filteredSaveData.dateOfBirth = filteredSaveData.dob;
      } else if (filteredSaveData.dateOfBirth && !filteredSaveData.dob) {
        filteredSaveData.dob = filteredSaveData.dateOfBirth;
      }
      // Always compute scores locally so the UI updates immediately,
      // even if the backend response is stale or has a different shape in prod.
      const localCfg = {
        lambda: Number(filteredSaveData.lambda ?? 0.3),
        gamma: Number(filteredSaveData.gamma ?? 0.5),
        sigma: Number(filteredSaveData.sigma ?? 0.8),
        enableSubstitution: filteredSaveData.substitutionEnabled !== false,
      };
      const localResult = calculateScoresV2(filteredSaveData, localCfg);
      const localComputed = {
        aipScore: Math.round(localResult?.aipScore || 0),
        wellBeingScore: Math.round(localResult?.wellBeingScore || 0),
        careTeamScore: Math.round(localResult?.careTeamScore || 0),
        environment: Math.round(localResult?.environmentScore || 0),
      };
      // IMPORTANT:
      // If admin edits only Well-Being inputs (e.g., ADL/IADL/Health Metrics),
      // we should NOT zero-out Care Team / Environment due to missing assessment flags
      // or missing modeling inputs. Preserve stored CT/ES unless those sections changed.
      const prevCT = Number(
        clientMeta?.careTeamScore ?? calculatedScores?.careTeamScore ?? 0,
      );
      const prevES = Number(
        clientMeta?.environmentScore ?? calculatedScores?.environment ?? 0,
      );
      const prevSnapshot = (editBackup && editBackup.data) || originalData || {};
      const envKey = (k) =>
        k === "environmentAssessed" ||
        k.startsWith("es") ||
        k.startsWith("homeSafety");
      const envDirty = Object.keys(filteredSaveData || {}).some((k) => {
        if (!envKey(k)) return false;
        return filteredSaveData[k] !== prevSnapshot[k];
      });
      const keepCareTeam = !careTeamDirty;
      const keepEnvironment = !envDirty;

      const finalLocal = {
        wellBeingScore: localComputed.wellBeingScore,
        careTeamScore: keepCareTeam ? Math.round(prevCT || 0) : localComputed.careTeamScore,
        environment: keepEnvironment ? Math.round(prevES || 0) : localComputed.environment,
      };
      // Overall: use simple average of components for immediate UI. Backend will recompute exactly.
      finalLocal.aipScore = Math.round(
        (Number(finalLocal.wellBeingScore || 0) +
          Number(finalLocal.careTeamScore || 0) +
          Number(finalLocal.environment || 0)) /
          3,
      );

      setCalculatedScores((prev) => ({ ...prev, ...finalLocal }));
      setClientMeta((prev) => ({
        ...prev,
        aipScore: finalLocal.aipScore,
        wellBeingScore: finalLocal.wellBeingScore,
        careTeamScore: finalLocal.careTeamScore,
        environmentScore: finalLocal.environment,
      }));

      const extractScoresFromAny = (payload) => {
        if (!payload) return null;
        // Shape A: backend returns updated Client
        if (
          payload.aipScore !== undefined ||
          payload.wellBeingScore !== undefined ||
          payload.careTeamScore !== undefined ||
          payload.environmentScore !== undefined
        ) {
          return {
            aipScore: Number(payload.aipScore) || 0,
            wellBeingScore: Number(payload.wellBeingScore) || 0,
            careTeamScore: Number(payload.careTeamScore) || 0,
            environment:
              Number(payload.environmentScore ?? payload.environment) || 0,
          };
        }
        // Shape B: { scores: { aip, wellBeing, careTeam, environment } }
        const s = payload?.scores;
        if (s && typeof s === "object") {
          return {
            aipScore: Number(s.aip ?? s.overall) || 0,
            wellBeingScore: Number(s.wellBeing) || 0,
            careTeamScore: Number(s.careTeam) || 0,
            environment: Number(s.environment) || 0,
          };
        }
        return null;
      };

      // Backend score calc is helpful, but we should not block the save UX on it.
      // If it fails (auth/network/etc), we still persist the edited data + locally computed scores.
      let savedScores = null;
      try {
        savedScores = await calculateClientScores(clientId, filteredSaveData);
      } catch (calcErr) {
        console.warn("⚠️ Failed to calculate scores on backend:", {
          status: calcErr?.response?.status,
          message:
            calcErr?.response?.data?.message ||
            calcErr?.response?.data?.error ||
            calcErr?.message,
        });
      }

      // Also persist the full assessment snapshot to the client document to avoid reload loss
      const derivedTypedName = splitName(
        latestData?.name || latestData?.fullName || "",
      );
      const resolvedFirstName = (
        (latestData?.firstName ?? "") ||
        (data?.firstName ?? "") ||
        (clientMeta?.firstName ?? "") ||
        derivedTypedName.firstName
      )
        .toString()
        .trim();
      const resolvedLastName = (
        (latestData?.lastName ?? "") ||
        (data?.lastName ?? "") ||
        (clientMeta?.lastName ?? "") ||
        derivedTypedName.lastName
      )
        .toString()
        .trim();
      if (!resolvedFirstName || !resolvedLastName) {
        setSuccessMessage(
          "❌ First Name and Last Name are required. Please complete Client Information before saving.",
        );
        setTimeout(() => setSuccessMessage(""), 5000);
        return false;
      }

      const baseUpdatePayload = {
        aipAnalysisData: filteredSaveData,
        // Persist DOB at the top-level too (the client list/detail may read these fields directly)
        dob: filteredSaveData.dob ?? "",
        dateOfBirth: filteredSaveData.dateOfBirth ?? filteredSaveData.dob ?? "",
        // Persist family members at the top-level too (backend stores it separately)
        familyMembers: Array.isArray(filteredSaveData.familyMembers)
          ? filteredSaveData.familyMembers
          : [],
        // Persist scores explicitly too (prevents "save succeeded but scores don't change")
        // even if calculate-scores response shape differs or caching occurs.
        aipScore: finalLocal.aipScore,
        wellBeingScore: finalLocal.wellBeingScore,
        careTeamScore: finalLocal.careTeamScore,
        environmentScore: finalLocal.environment,
        // keep basic location details in sync
        street: data.street ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        zip: data.zip ?? "",
        // keep Edit Client form fields in sync
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        phone: latestData?.phone ?? data.phone ?? "",
        phoneNumber: latestData?.phone ?? data.phone ?? "",
        address:
          latestData?.address ||
          [latestData?.street ?? data.street, latestData?.city ?? data.city, latestData?.state ?? data.state, latestData?.zip ?? data.zip]
            .filter(Boolean)
            .join(", "),
        diagnosis:
          latestData?.diagnosis ||
          latestData?.primaryCondition ||
          data.diagnosis ||
          data.primaryCondition ||
          "",
      };
      const basePayloadBytes = (() => {
        try {
          return new Blob([JSON.stringify(baseUpdatePayload)]).size;
        } catch {
          return 0;
        }
      })();
      console.log("AIP base update payload bytes:", basePayloadBytes);

      let updatedClient = null;
      let avatarUploadFailed = false;
      let avatarSavedAfterCompression = false;
      updatedClient = await updateClient(clientId, baseUpdatePayload);

      let avatarUploadErrorInfo = "";
      if (shouldSendAvatar) {
        try {
          let avatarToUpload = nextAvatarValue;
          if (
            typeof avatarToUpload === "string" &&
            avatarToUpload.startsWith("data:image/") &&
            avatarToUpload.length > AVATAR_UPLOAD_TARGET_MAX_CHARS
          ) {
            try {
              avatarToUpload = await recompressAvatarDataUrl(
                avatarToUpload,
                AVATAR_UPLOAD_TARGET_MAX_CHARS,
              );
            } catch (compressErr) {
              console.warn("Avatar pre-upload compression failed:", compressErr);
            }
          }

          const avatarPayload = { avatarUrl: avatarToUpload };
          const avatarPayloadBytes = (() => {
            try {
              return new Blob([JSON.stringify(avatarPayload)]).size;
            } catch {
              return 0;
            }
          })();
          console.log("AIP avatar payload bytes:", avatarPayloadBytes);
          const avatarUpdatedClient = await updateClient(clientId, avatarPayload);
          if (avatarUpdatedClient) {
            updatedClient = avatarUpdatedClient;
          } else {
            updatedClient = {
              ...updatedClient,
              avatarUrl: avatarToUpload,
            };
          }
        } catch (avatarErr) {
          const avatarStatus = avatarErr?.response?.status;
          const avatarMsg =
            avatarErr?.response?.data?.message ||
            avatarErr?.response?.data?.error ||
            avatarErr?.message ||
            "";
          avatarUploadErrorInfo = avatarStatus
            ? `HTTP ${avatarStatus}${avatarMsg ? `: ${avatarMsg}` : ""}`
            : avatarMsg;
          const isNetworkLike =
            !avatarErr?.response &&
            /network|timeout|failed to fetch|load failed/i.test(
              String(avatarErr?.message || ""),
            );
          if (
            isNetworkLike &&
            typeof nextAvatarValue === "string" &&
            nextAvatarValue.startsWith("data:image/")
          ) {
            try {
              const compressedAvatar = await recompressAvatarDataUrl(
                nextAvatarValue,
                AVATAR_UPLOAD_RETRY_MAX_CHARS,
              );
              if (
                typeof compressedAvatar === "string" &&
                compressedAvatar.length < nextAvatarValue.length
              ) {
                const retryAvatarPayload = { avatarUrl: compressedAvatar };
                const avatarRetryClient = await updateClient(
                  clientId,
                  retryAvatarPayload,
                );
                updatedClient = avatarRetryClient || {
                  ...updatedClient,
                  avatarUrl: compressedAvatar,
                };
                avatarSavedAfterCompression = true;
              } else {
                avatarUploadFailed = true;
              }
            } catch (retryErr) {
              console.warn("Avatar upload retry failed:", retryErr);
              const retryStatus = retryErr?.response?.status;
              const retryMsg =
                retryErr?.response?.data?.message ||
                retryErr?.response?.data?.error ||
                retryErr?.message ||
                "";
              avatarUploadErrorInfo = retryStatus
                ? `HTTP ${retryStatus}${retryMsg ? `: ${retryMsg}` : ""}`
                : retryMsg || avatarUploadErrorInfo;
              avatarUploadFailed = true;
            }
          } else {
            console.warn("Avatar upload failed:", avatarErr);
            avatarUploadFailed = true;
          }
        }
      }

      // Sync UI with saved data/scores
      const persistedData = {
        ...(updatedClient?.aipAnalysisData || filteredSaveData),
        avatarUrl:
          updatedClient?.avatarUrl ??
          (avatarUploadFailed
            ? previousAvatarValue
            : latestData?.avatarUrl ?? previousAvatarValue),
      };
      setData(persistedData);
      setOriginalData(persistedData);
      loadAssessmentHistory();

      // Sync careTeamModel and originalCareTeam from persisted data so Care Team inputs
      // don't revert to 0% after save (and Reset uses saved state)
      const savedCareTeam = {
        daysPerWeek: Number(
          persistedData.careDaysPerWeek ?? careTeamModel.daysPerWeek ?? 7,
        ),
        scheduledVisits: Number(
          persistedData.totalScheduledVisits ??
            careTeamModel.scheduledVisits ??
            10,
        ),
        deliveredOnTime: Number(
          persistedData.visitsOnTime ?? careTeamModel.deliveredOnTime ?? 10,
        ),
        visitsBySamePerson: Number(
          persistedData.visitsBySamePerson ??
            careTeamModel.visitsBySamePerson ??
            10,
        ),
        totalVisits: Number(
          persistedData.totalVisits ?? careTeamModel.totalVisits ?? 10,
        ),
        carePlanUpToDate:
          persistedData.carePlanStatus != null &&
          persistedData.carePlanStatus !== ""
            ? String(persistedData.carePlanStatus).toLowerCase() === "up to date"
            : (careTeamModel.carePlanUpToDate ?? true),
        familySatisfaction: Number(
          persistedData.familySatisfaction ??
            careTeamModel.familySatisfaction ??
            3,
        ),
      };
      setCareTeamModel(savedCareTeam);
      originalCareTeam.current = savedCareTeam;

      const resolved =
        extractScoresFromAny(savedScores) ||
        extractScoresFromAny(updatedClient) ||
        localComputed;

      setCalculatedScores((prev) => ({
        ...prev,
        aipScore: resolved.aipScore,
        wellBeingScore: resolved.wellBeingScore,
        careTeamScore: resolved.careTeamScore,
        environment: resolved.environment,
      }));
      setClientMeta((prev) => ({
        ...prev,
        aipScore: resolved.aipScore,
        wellBeingScore: resolved.wellBeingScore,
        careTeamScore: resolved.careTeamScore,
        environmentScore: resolved.environment,
      }));

      // Final safety net: re-fetch client so stored scores match UI.
      try {
        const freshClient = await fetchClient(clientId);
        if (freshClient) {
          const normalizedVoiceHistory = (
            Array.isArray(freshClient?.vocalEnergyHistory)
              ? freshClient.vocalEnergyHistory
              : []
          )
            .filter((row) => row && typeof row === "object")
            .sort((a, b) => {
              const aTs = new Date(a?.recordedAt || a?.archivedAt || 0).getTime();
              const bTs = new Date(b?.recordedAt || b?.archivedAt || 0).getTime();
              return bTs - aTs;
            });
          setVoiceHistory(normalizedVoiceHistory);
          const fresh = extractScoresFromAny(freshClient);
          if (fresh) {
            setCalculatedScores((prev) => ({
              ...prev,
              aipScore: Math.round(fresh.aipScore || 0),
              wellBeingScore: Math.round(fresh.wellBeingScore || 0),
              careTeamScore: Math.round(fresh.careTeamScore || 0),
              environment: Math.round(fresh.environment || 0),
            }));
            setClientMeta((prev) => ({
              ...prev,
              aipScore: Math.round(fresh.aipScore || 0),
              wellBeingScore: Math.round(fresh.wellBeingScore || 0),
              careTeamScore: Math.round(fresh.careTeamScore || 0),
              environmentScore: Math.round(fresh.environment || 0),
              latestAssessmentStatus:
                freshClient.latestAssessmentStatus || prev.latestAssessmentStatus || "",
              isAipProvisional:
                freshClient.isAipProvisional === true ||
                String(
                  freshClient.latestAssessmentStatus ||
                    prev.latestAssessmentStatus ||
                    "",
                ).toLowerCase() === "auto-provisional",
            }));
          }
        }
      } catch (refetchErr) {
        console.warn("⚠️ Failed to refetch client after save:", refetchErr);
      }
      setCareTeamDirty(false);
      setIsDirty(false);

      // Exit edit mode
      setIsManualEdit(false);
      setEditBackup(null);
      setSuccessMessage(
        avatarSavedAfterCompression
          ? "✅ Changes saved. Profile photo was auto-compressed and uploaded."
          : avatarUploadFailed
          ? `✅ Changes saved, but profile photo upload failed${
              avatarUploadErrorInfo ? ` (${avatarUploadErrorInfo})` : "."
            }`
          : avatarTooLarge
          ? "✅ Changes saved. Profile photo was too large and was not updated."
          : "✅ Manual edits saved to client record",
      );
      setTimeout(() => setSuccessMessage(""), 5000);
      if (showClientInfoModal) {
        setShowClientInfoModal(false);
      }

      // Track history
      try {
        const nextScores = {
          overall:
            Math.round(savedScores?.aipScore ?? updatedClient?.aipScore ?? 0) ||
            0,
          wellBeing:
            Math.round(
              savedScores?.wellBeingScore ?? updatedClient?.wellBeingScore ?? 0,
            ) || 0,
          careTeam:
            Math.round(
              savedScores?.careTeamScore ?? updatedClient?.careTeamScore ?? 0,
            ) || 0,
          environment:
            Math.round(
              savedScores?.environmentScore ??
                updatedClient?.environmentScore ??
                savedScores?.environment ??
                updatedClient?.environment ??
                0,
            ) || 0,
          vocalAnxiety: Math.round(data.vocalAnxiety || 0),
          vocalDepression: Math.round(data.vocalDepression || 0),
          vocalAggression: Math.round(data.vocalAggression || 0),
          vocalWellness: Math.round(data.vocalWellness || 0),
        };
        const savedHistory = await trackScoreChange(
          previousScores,
          nextScores,
          "Manual edit",
        );

        // IMPORTANT:
        // If the history write fails (or history fetch is stale), we should NOT
        // overwrite freshly-saved scores in the UI with old history scores.
        // Instead, hydrate local history optimistically from the save response.
        if (savedHistory && savedHistory.scores) {
          const s = savedHistory.scores || {};
          const toInt = (v, fallback = 0) => {
            const n = Number(v);
            return Number.isFinite(n) ? Math.round(n) : fallback;
          };

          setAipHistory((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const id = savedHistory._id || savedHistory.id;
            const deduped = id
              ? list.filter((x) => (x?._id || x?.id) !== id)
              : list;
            return [savedHistory, ...deduped];
          });

          // Keep the score cards in sync with the newly saved history entry.
          setCalculatedScores((prev) => ({
            ...prev,
            aipScore: toInt(s.overall, prev?.aipScore ?? 0),
            wellBeingScore: toInt(s.wellBeing, prev?.wellBeingScore ?? 0),
            careTeamScore: toInt(s.careTeam, prev?.careTeamScore ?? 0),
            environment: toInt(s.environment, prev?.environment ?? 0),
            vocalAnxiety: toInt(s.vocalAnxiety, prev?.vocalAnxiety ?? 0),
            vocalDepression: toInt(s.vocalDepression, prev?.vocalDepression ?? 0),
            vocalAggression: toInt(s.vocalAggression, prev?.vocalAggression ?? 0),
            vocalWellness: toInt(s.vocalWellness, prev?.vocalWellness ?? 0),
          }));
        }
      } catch (historyErr) {
        console.warn("⚠️ Failed to record history entry:", historyErr);
      }
      // Ensure confirm overlay is closed on success even if caller forgets.
      setShowSaveConfirm(false);
      return true;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      console.error("❌ Failed to save manual edits:", {
        status,
        message: serverMsg,
        error,
      });
      setSuccessMessage(
        `❌ Failed to save changes${status ? ` (HTTP ${status})` : ""}${
          serverMsg ? `: ${serverMsg}` : ""
        }`,
      );
      setTimeout(() => setSuccessMessage(""), 3000);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // AbleCare tab "Manual entry": the page's Save bar is hidden on integration
  // panels, so the tab saves its four values itself. In modeling mode the edit
  // stays local like every other field; otherwise it is persisted (admin only).
  const canEditAbleCareManual = isModelingMode || isAdmin;
  const handleSaveAbleCareManual = async (values) => {
    const next = { ...(dataRef.current ?? data), ...values };
    dataRef.current = next;
    setData(next);
    if (isModelingMode) {
      setIsDirty(true);
      return true;
    }
    return handleSaveManualEdit();
  };

  const handleRequestManualSave = () => {
    setShowSaveConfirm(true);
  };

  const handleConfirmManualSave = async () => {
    const ok = await handleSaveManualEdit();
    // Only close confirm on success. If it fails, keep modal open so user sees error.
    if (ok) {
      setShowSaveConfirm(false);
      setShowClientInfoModal(false);
    }
  };

  const handleDismissSaveConfirm = () => {
    if (!saving) {
      setShowSaveConfirm(false);
    }
  };

  useEffect(() => {
    if (!showSaveConfirm) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        handleDismissSaveConfirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSaveConfirm, saving]);

  // ✅ SECURITY: Auto-exit manual edit mode if user loses admin access
  useEffect(() => {
    if (isManualEdit && !isAdmin) {
      console.warn("⚠️ User lost admin access - exiting manual edit mode");
      setIsManualEdit(false);
      setEditBackup(null);
      setSuccessMessage("❌ Manual edit mode disabled - Admin access required");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  }, [isAdmin, isManualEdit]);

  // Live v2 formula: Well-Being / AIP / ES update whenever form inputs change.
  useEffect(() => {
    if (isReadOnlyMode) return;

    const config = {
      lambda: Number(data?.lambda ?? 0.3),
      gamma: Number(data?.gamma ?? 0.5),
      sigma: Number(data?.sigma ?? 0.8),
      enableSubstitution: data?.substitutionEnabled !== false,
    };
    const result = calculateScoresV2(data, config);
    const nextWellBeing = Math.round(result.wellBeingScore || 0);
    const nextEnvironment = Math.round(result.environmentScore || 0);
    const nextCareTeam = careTeamDirty
      ? Math.round(careTeamScoreModel || result.careTeamScore || 0)
      : Math.round(result.careTeamScore || 0);
    const nextAip = Math.round(result.aipScore || 0);

    setCalculatedScores((prev) => {
      if (
        prev.aipScore === nextAip &&
        prev.wellBeingScore === nextWellBeing &&
        prev.careTeamScore === nextCareTeam &&
        prev.environment === nextEnvironment
      ) {
        return prev;
      }
      return {
        aipScore: nextAip,
        wellBeingScore: nextWellBeing,
        careTeamScore: nextCareTeam,
        environment: nextEnvironment,
      };
    });
  }, [
    JSON.stringify(data),
    isReadOnlyMode,
    careTeamDirty,
    careTeamScoreModel,
  ]);

  // ✅ Save as Advice (Modeling Mode Only) — do NOT mutate real client data
  const handleSaveAsAdvice = async () => {
    try {
      const token = localStorage.getItem("token");

      const aipBefore = clientMeta.aipScore || 0;
      const fwbBefore = clientMeta.wellBeingScore || 0;
      const ctBefore = clientMeta.careTeamScore || 0;
      const esBefore = clientMeta.environmentScore || 0;
      const aipAfter = calculatedScores.aipScore;
      const fwbAfter = calculatedScores.wellBeingScore;
      const ctAfter = calculatedScores.careTeamScore;
      const esAfter = calculatedScores.environment;

      const payload = {
        clientId,
        isModeling: true,
        source: "AIP Care Modeling",
        title: `Care Modeling for ${clientMeta.firstName} ${clientMeta.lastName}`,
        notes: "Generated from AIP Smart Care modeling session",
        aipScoreBefore: aipBefore,
        wellBeingScoreBefore: fwbBefore,
        careTeamScoreBefore: ctBefore,
        environmentScoreBefore: esBefore,
        aipScoreAfter: aipAfter,
        wellBeingScoreAfter: fwbAfter,
        careTeamScoreAfter: ctAfter,
        environmentScoreAfter: esAfter,
        aipScoreDelta: aipAfter - aipBefore,
        wellBeingScoreDelta: fwbAfter - fwbBefore,
        careTeamScoreDelta: ctAfter - ctBefore,
        environmentScoreDelta: esAfter - esBefore,
        // Keep modeled snapshot separate from real client assessment data
        modeledData: data,
      };

      console.log(
        "📤 Sending payload:",
        payload._id ? `UPDATE _id: ${payload._id}` : "CREATE new",
      );
      console.log("📤 Payload details:", {
        _id: payload._id || "new",
        clientId: payload.clientId,
        hasADL: !!payload.adlBathing,
        hasES: Object.keys(payload).some(
          (key) => key.startsWith("es") && payload[key],
        ),
      });

      const response = await fetch(`${API_BASE}/api/advice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("❌ Save failed:", errorData);
        throw new Error(
          errorData.message || errorData.error || "Failed to save assessment",
        );
      }

      const result = await response.json();
      console.log("✅ [Advice] Created modeling record successfully:", result);

      // Update calculated scores with response (including environment)
      if (result.scores) {
        setCalculatedScores({
          aipScore: result.scores.aip,
          wellBeingScore: result.scores.wellBeing,
          careTeamScore: result.scores.careTeam,
          environment: result.scores.environment,
        });
        console.log("📊 Updated scores:", result.scores);
      }

      // Never attach assessment _id to modeling data

      setSuccessMessage("✅ Modeling advice saved successfully!");
      setTimeout(() => setSuccessMessage(""), 5000);
      setIsDirty(false);
      setCareTeamDirty(false); // ✅ Reset Care Team dirty flag after save
    } catch (err) {
      console.error("❌ Failed to save assessment:", err);
      console.error("❌ Error message:", err.message);
      console.error("❌ Error stack:", err.stack);
      setSuccessMessage(
        `❌ Error: ${err.message || "Could not save assessment"}`,
      );
      setTimeout(() => setSuccessMessage(""), 10000);
    }
  };

  const handleCalculateScores = async () => {
    if (isModelingMode) {
      const config = {
        lambda: 0.3,
        gamma: 0.5,
        sigma: 0.8,
        enableSubstitution: true,
      };
      const result = calculateScoresV2(data, config);
      setCalculatedScores({
        aipScore: Math.round(result.aipScore || 0),
        wellBeingScore: Math.round(result.wellBeingScore || 0),
        careTeamScore: Math.round(result.careTeamScore || 0),
        environment: Math.round(result.environmentScore || 0),
      });
      setIsScoreModalOpen(true);
      setSuccessMessage("Scores calculated (modeling only).");
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }
    // Save first if dirty? Or just calculate based on current data?
    // Usually better to save then calculate, or send current data to calculate endpoint.
    // The backend endpoint accepts data in body, so we can send current 'data' state.

    if (clientId === "101") {
      setSuccessMessage("Scores calculated! (Demo Mode)");
      // Simulate score calculation for demo
      setCalculatedScores({
        aipScore: 80,
        wellBeingScore: 74,
        careTeamScore: 83,
        environment: 65,
      });
      setIsScoreModalOpen(true);
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }

    try {
      // We send current form data to be used for calculation
      // The backend will also update the client's score fields in DB
      const updatedClient = await calculateClientScores(clientId, data);

      // Update local state with returned scores
      setCalculatedScores({
        aipScore: updatedClient.aipScore,
        wellBeingScore: updatedClient.wellBeingScore,
        careTeamScore: updatedClient.careTeamScore,
        environment:
          Number(updatedClient.environmentScore ?? updatedClient.environment) || 0,
        environmentScore:
          Number(updatedClient.environmentScore ?? updatedClient.environment) || 0,
      });

      setIsScoreModalOpen(true); // Open the result modal

      setSuccessMessage("Scores calculated and updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Failed to calculate scores", err);
      setSuccessMessage("Error: Could not calculate scores.");
    }
  };

  const scrollToContent = (section) => {
    setTimeout(() => {
      const targetRef =
        section === "visit-notes"
          ? visitNotesDisplayRef
          : INTEGRATION_SIDEBAR_SECTIONS.has(section)
            ? integrationMainRef
            : contentDisplayRef;
      targetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleCardClick = (section) => {
    setSnapshotSidebarTab(
      INTEGRATION_SIDEBAR_SECTIONS.has(section) ? "integration" : "snapshot",
    );
    setActiveSection((prev) => {
      const isOpening = prev !== section;
      if (isOpening) scrollToContent(section);
      return isOpening ? section : null;
    });
  };

  const loadAipHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      setHistoryLoading(true);
      const data = await fetchAipHistory(clientId, historyRange);
      setAipHistory(Array.isArray(data) ? data : []);
      // If history exists, lock cards to latest history entry to stay in sync
      if (Array.isArray(data) && data.length > 0) {
        const latest = data[0]?.scores || {};
        setCalculatedScores((prev) => ({
          aipScore: Math.round(latest.overall ?? prev.aipScore ?? 0),
          wellBeingScore: Math.round(
            latest.wellBeing ?? prev.wellBeingScore ?? 0,
          ),
          careTeamScore: Math.round(latest.careTeam ?? prev.careTeamScore ?? 0),
          environment: Math.round(latest.environment ?? prev.environment ?? 0),
          vocalAnxiety: Math.round(
            latest.vocalAnxiety ?? prev.vocalAnxiety ?? 0,
          ),
          vocalDepression: Math.round(
            latest.vocalDepression ?? prev.vocalDepression ?? 0,
          ),
          vocalAggression: Math.round(
            latest.vocalAggression ?? prev.vocalAggression ?? 0,
          ),
          vocalWellness: Math.round(
            latest.vocalWellness ?? prev.vocalWellness ?? 0,
          ),
        }));
      }
    } catch (err) {
      console.error("Failed to load AIP history", err);
      setAipHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [clientId, historyRange]);

  useEffect(() => {
    if (activeSection === "aip-analysis") {
      loadAipHistory();
    }
  }, [activeSection, loadAipHistory]);

  const loadAssessmentHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      const records = await fetchAdviceForClient(clientId);
      const normalized = (Array.isArray(records) ? records : [])
        .filter((r) => r && r.isModeling !== true)
        .sort((a, b) => {
          const aTs = new Date(a.assessmentDate || a.createdAt || 0).getTime();
          const bTs = new Date(b.assessmentDate || b.createdAt || 0).getTime();
          return bTs - aTs;
        });
      setAssessmentHistory(normalized);
    } catch (err) {
      console.warn("Failed to load assessment history", err);
      setAssessmentHistory([]);
    }
  }, [clientId]);

  useEffect(() => {
    loadAssessmentHistory();
  }, [loadAssessmentHistory]);

  const sectionsWithHistoryTabs = useMemo(
    () =>
      new Set([
        "voice-assessment",
        "carelink360",
        "daily-life-resilience",
        "health-indicators",
        "careTeam",
        "environment",
        "environmental-safety",
        "wellbeing",
      ]),
    [],
  );

  const archivedAssessments = useMemo(
    () => (Array.isArray(assessmentHistory) ? assessmentHistory.slice(1) : []),
    [assessmentHistory],
  );

  const voiceTimeline = useMemo(() => {
    const latestVoiceEntry = {
      recordedAt: clientMeta?.voiceRecordedAt || null,
      raw: clientMeta?.voiceRaw || {},
    };
    const hasLatestRaw =
      latestVoiceEntry.raw &&
      typeof latestVoiceEntry.raw === "object" &&
      Object.keys(latestVoiceEntry.raw).length > 0;

    const withLatest = hasLatestRaw
      ? [latestVoiceEntry, ...(Array.isArray(voiceHistory) ? voiceHistory : [])]
      : Array.isArray(voiceHistory)
      ? [...voiceHistory]
      : [];

    return withLatest
      .filter((row) => row && typeof row === "object")
      .sort((a, b) => {
        const aTs = new Date(a?.recordedAt || a?.archivedAt || 0).getTime();
        const bTs = new Date(b?.recordedAt || b?.archivedAt || 0).getTime();
        return bTs - aTs;
      });
  }, [clientMeta?.voiceRecordedAt, clientMeta?.voiceRaw, voiceHistory]);

  const archivedVoiceEntries = useMemo(
    () => (Array.isArray(voiceTimeline) ? voiceTimeline.slice(1) : []),
    [voiceTimeline],
  );

  const formatAssessmentHistoryLabel = (row) => {
    const stamp = row?.assessmentDate || row?.createdAt;
    if (!stamp) return "Archived assessment";
    const d = new Date(stamp);
    if (Number.isNaN(d.getTime())) return "Archived assessment";
    return d.toLocaleString();
  };

  const formatVoiceHistoryLabel = (row) => {
    const stamp = row?.recordedAt || row?.archivedAt;
    if (!stamp) return "Archived voice record";
    const d = new Date(stamp);
    if (Number.isNaN(d.getTime())) return "Archived voice record";
    return d.toLocaleString();
  };

  const getSectionTabMode = (sectionKey) => sectionHistoryTab[sectionKey] || "latest";

  const setSectionTabMode = (sectionKey, mode) => {
    setSectionHistoryTab((prev) => ({ ...prev, [sectionKey]: mode }));
  };

  const getSectionArchivedRecord = (sectionKey) => {
    const idx = Number(sectionArchivedIndex[sectionKey] || 0);
    if (sectionKey === "voice-assessment") {
      return archivedVoiceEntries[idx] || null;
    }
    return archivedAssessments[idx] || null;
  };

  const getSectionDataSource = (sectionKey) => {
    if (getSectionTabMode(sectionKey) !== "archived") return data;
    return getSectionArchivedRecord(sectionKey) || data;
  };

  const buildCareTeamModelFromSource = (source = {}) => {
    const totalVisits = Number(source?.totalVisits ?? 10);
    const base = {
      daysPerWeek: Number(source?.careDaysPerWeek ?? source?.ctDaysPerWeek ?? 7),
      scheduledVisits: Number(source?.totalScheduledVisits ?? totalVisits),
      deliveredOnTime: Number(source?.visitsOnTime ?? totalVisits),
      visitsBySamePerson: Number(source?.visitsBySamePerson ?? totalVisits),
      totalVisits,
      carePlanUpToDate:
        String(source?.carePlanStatus || "").toLowerCase() === "up to date" ||
        source?.ctCareplanCurrent === true,
      familySatisfaction: Number(
        source?.familySatisfaction ?? source?.ctFamilyCommunication ?? 3,
      ),
    };
    return hydrateCareTeamModelFromWellSkyMetrics(source, base) || base;
  };

  const renderSectionHistoryTabs = (sectionKey) => {
    if (!sectionsWithHistoryTabs.has(sectionKey)) return null;
    const isVoiceSection = sectionKey === "voice-assessment";
    const archivedList = isVoiceSection ? archivedVoiceEntries : archivedAssessments;
    const archivedCount = archivedList.length;
    const mode = getSectionTabMode(sectionKey);

    return (
      <div style={{ marginBottom: 12 }}>
        <div className="mobile-toggle">
          <button
            className={`toggle-btn ${mode === "latest" ? "active" : ""}`}
            onClick={() => setSectionTabMode(sectionKey, "latest")}
            type="button"
          >
            Latest
          </button>
          <button
            className={`toggle-btn ${mode === "archived" ? "active" : ""}`}
            onClick={() => setSectionTabMode(sectionKey, "archived")}
            type="button"
          >
            Archived ({archivedCount})
          </button>
        </div>

        {mode === "archived" && (
          <>
            {archivedCount > 0 ? (
              <div className="history-filters" style={{ marginTop: 10 }}>
                <label>Archived record</label>
                <select
                  value={String(sectionArchivedIndex[sectionKey] || 0)}
                  onChange={(e) =>
                    setSectionArchivedIndex((prev) => ({
                      ...prev,
                      [sectionKey]: Number(e.target.value || 0),
                    }))
                  }
                >
                  {archivedList.map((row, idx) => (
                    <option key={row?._id || row?.id || idx} value={idx}>
                      {isVoiceSection
                        ? formatVoiceHistoryLabel(row)
                        : formatAssessmentHistoryLabel(row)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="empty-state" style={{ marginTop: 10 }}>
                No archived records yet.
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const getCurrentScoresSnapshot = () => ({
    overall: Math.round(calculatedScores.aipScore || 0),
    wellBeing: Math.round(calculatedScores.wellBeingScore || 0),
    careTeam: Math.round(activeCareTeamScore || 0),
    environment: Math.round(calculatedScores.environment || 0),
    vocalAnxiety: Math.round(data.vocalAnxiety || 0),
    vocalDepression: Math.round(data.vocalDepression || 0),
    vocalAggression: Math.round(data.vocalAggression || 0),
    vocalWellness: Math.round(data.vocalWellness || 0),
  });

  const handleScoreCardClick = (section) => {
    setSnapshotSidebarTab(
      INTEGRATION_SIDEBAR_SECTIONS.has(section) ? "integration" : "snapshot",
    );
    setActiveSection(section);
    // Always scroll so clicks reliably jump to the details section,
    // even if the same section is already active.
    scrollToContent(section);
  };

  const activeVoiceEntry = useMemo(() => {
    const mode = getSectionTabMode("voice-assessment");
    if (mode === "archived") {
      const archived = getSectionArchivedRecord("voice-assessment");
      return archived || null;
    }
    return voiceTimeline[0] || null;
  }, [voiceTimeline, sectionHistoryTab, sectionArchivedIndex, archivedVoiceEntries]);

  const getVoiceResults = useMemo(() => {
    const raw =
      activeVoiceEntry?.raw && typeof activeVoiceEntry.raw === "object"
        ? activeVoiceEntry.raw
        : {};
    const asNumber = (value, fallback = 0) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };
    const asText = (value, fallback = "") => {
      const t = String(value ?? "").trim();
      return t || fallback;
    };
    return {
      cognitiveScreening: asText(
        raw.cognitiveScreening,
        "Additional Screening Not Recommended",
      ),
      alzheimerCategory: asText(raw.alzheimerCategory, "Alzheimer's not detected"),
      mciCategory: asText(raw.mciCategory, "MCI not detected"),
      mood: asText(raw.mood, "excellent"),
      stress: asText(raw.stress, "low"),
      aggression: asText(raw.aggression || raw.aggressionCategory, "low"),
      wellness: asNumber(raw.wellness, asNumber(data.vocalWellness, 0)),
      wellnessCategory: asText(raw.wellnessCategory, "High"),
      vocalPower: asNumber(raw.vocalPower, asNumber(data.vocalPower, 0)),
      vocalPowerCategory: asText(raw.vocalPowerCategory, "Med"),
      vocalSpeed: asNumber(raw.vocalSpeed, asNumber(data.vocalSpeed, 0)),
      vocalSpeedCategory: asText(raw.vocalSpeedCategory, "Med"),
      vocalDynamics: asNumber(raw.vocalDynamics, asNumber(data.vocalDynamic, 0)),
      vocalDynamicsCategory: asText(raw.vocalDynamicsCategory, "Med"),
      energyOverall: asNumber(raw.energyOverall, asNumber(raw.energy, 0)),
      energyCategory: asText(raw.energyCategory, "Med"),
    };
  }, [activeVoiceEntry, data.vocalDynamic, data.vocalPower, data.vocalSpeed, data.vocalWellness]);

  const getVoiceCategoryColor = (value) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    const greenSet = new Set([
      "low",
      "excellent",
      "high",
      "negative",
      "mci not detected",
      "alzheimer's not detected",
      "additional screening not recommended",
    ]);
    const amberSet = new Set(["medium", "good", "med", "moderate"]);
    const redSet = new Set([
      "positive",
      "mci detected",
      "alzheimer's detected",
      "additional screening recommended",
    ]);
    if (greenSet.has(normalized)) return "#10B981";
    if (amberSet.has(normalized)) return "#3B82F6";
    if (redSet.has(normalized)) return "#EF4444";
    if (normalized === "low") return "#F59E0B";
    return "#6B7280";
  };

  // --- Client Snapshot (UI only) ---
  const toScoreOrNull = (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.round(n);
  };
  const hasAnyAssessed = (keys) =>
    keys.some((k) => {
      const v = data?.[k];
      return v !== undefined && v !== null && v !== "" && v !== "not-assessed";
    });
  const isAipProvisional =
    clientMeta?.isAipProvisional === true ||
    String(clientMeta?.latestAssessmentStatus || "").toLowerCase() ===
      "auto-provisional";
  const hasNoAssessmentAtAll =
    clientMeta?.aipScore === undefined || clientMeta?.aipScore === null;
  const hasProvisionalWellBeingScore =
    isAipProvisional &&
    Number.isFinite(Number(calculatedScores?.wellBeingScore)) &&
    Number(calculatedScores?.wellBeingScore) > 0;
  const hasWellBeingData = hasAnyAssessed([
    "adlBathing",
    "adlDressing",
    "adlToileting",
    "adlTransferring",
    "adlContinence",
    "adlEating",
    // legacy/back-compat fields that may appear in older records/UI state
    "adlFeeding",
    "adlHygiene",
    "adlRestroom",
    "adlMobility",
    "iadlMealPreparation",
    "iadlFinances",
    "iadlHousekeepingLaundry",
    "iadlShopping",
    "iadlMedication",
    "iadlTransportation",
    "iadlCommunication",
    "iadlHomeMaintenance",
  ]) || hasProvisionalWellBeingScore;
  const hasCareTeamData =
    data?.ctAssessed === true ||
    hasAnyAssessed([
      "careDaysPerWeek",
      "careCoverageLevel",
      "totalVisits",
      "visitsBySamePerson",
      "totalScheduledVisits",
      "visitsOnTime",
      "carePlanStatus",
      "familySatisfaction",
    ]);
  // "manual" = real assessment form was submitted (even all-clear)
  // "default" = WellSky-synced, never actually assessed; shows 100 provisional
  // undefined  = document predates this field
  const esSource = data?.esSource;

  const hasEnvironmentData =
    esSource === "manual" ||
    data?.environmentAssessed === true ||
    hasAnyAssessed([
      "esLooseRugs",
      "esClutterWalkways",
      "esPoorLighting",
      "esStairsNoRailings",
      "esSlipperyFloors",
      "esNoGrabBars",
      "esUnevenSteps",
      "esCordsOnFloor",
      "esUnevenFlooring",
      "esFootwearNoTraction",
      "esNoNightlights",
      "esPetsUnderfoot",
      "esUnstableFurniture",
      "esItemsStoredImproper",
      "esOtherFallHazard",
      "esWetSlipperyFloors",
      "esNoBathroomGrabBars",
      "esSlipperyTub",
      "esNoNonSlipStrips",
      "esLooseBathMats",
      "esLowToiletHeight",
      "esLooseToiletSeat",
      "esHighTubThreshold",
      "esBathroomPoorLighting",
      "esBathroomNoNightlight",
      "esBathroomClutter",
      "esItemsOutOfReach",
      "esTowelBarsForSupport",
      "esUnstableShowerChair",
      "esCrampedBathroom",
      "esOtherBathroomHazard",
      "esKitchenWetFloors",
      "esGreaseOnFloor",
      "esLooseKitchenMats",
      "esNoNonSlipBacking",
      "esReachingHighCabinets",
      "esUnsafeStepStool",
      "esKitchenClutter",
      "esNoFireExtinguisher",
      "esBendingLowCabinets",
      "esLooseDrawersDoors",
      "esFrayedCords",
      "esKitchenPoorLighting",
      "esApplianceCordsInPath",
      "esKitchenUnevenFlooring",
      "esOverloadedOutlet",
      "esNoSmokeDetector",
      "esNoCO2Detector",
      "esOtherKitchenHazard",
      "esStepsThresholds",
      "esNarrowHallways",
      "esLayoutClutter",
      "esInaccessibleBathroom",
      "esMultiLevelHome",
      "esTightDoorways",
      "esObstructedPathways",
    ]);

  const wellSkyLineage = useMemo(
    () =>
      deriveAipWellSkyLineage(data, {
        clientLinked: isWellSkyClient(clientMeta),
      }),
    [data, clientMeta],
  );

  const snapshotAreas = [
    {
      id: "wellbeing",
      title: "Functional Well Being Score",
      score: hasWellBeingData ? toScoreOrNull(calculatedScores.wellBeingScore) : null,
      icon: "wellbeing",
      blurb: "Mood swings and sleep issues.",
      wellSkyBadge: wellSkyLineage.carePlanAdl,
    },
    {
      id: "careTeam",
      title: "Care Team Score",
      score: hasCareTeamData ? toScoreOrNull(activeCareTeamScore) : null,
      icon: "careTeam",
      blurb: "Staff shortages affecting care.",
      wellSkyBadge: wellSkyLineage.careTeamVisits,
    },
    {
      id: "environment",
      title: "Environmental Safety Score",
      // When a real assessment exists use the calculated score.
      // When esSource === "default" (WellSky-synced, never assessed), show 100
      // as a provisional best-case value with a distinct badge.
      // Otherwise (truly unknown) show null → "NO DATA".
      score: hasEnvironmentData
        ? toScoreOrNull(calculatedScores.environment)
        : esSource === "default"
          ? 100
          : null,
      esSource,
      icon: "environment",
      blurb: "",
    },
  ];
  const classifyArea = (score, source) => {
    // Provisional default: never been assessed but WellSky provides a best-case 100.
    // Rendered as a distinct "provisional-good" tier so it can't be confused with a
    // real completed assessment.
    if (source === "default") return "provisional-good";
    if (score === null || score === undefined) return "nodata";
    const s = Number(score);
    if (!Number.isFinite(s)) return "nodata";
    if (s < 50) return "critical";
    if (s < 75) return "moderate";
    return "good";
  };
  const areaBadge = (score, source) => {
    if (source === "default") return { text: "GOOD (PROVISIONAL)", cls: "provisional" };
    const tier = classifyArea(score, source);
    if (tier === "critical") return { text: "AT RISK", cls: "risk" };
    if (tier === "moderate") return { text: "MODERATE", cls: "moderate" };
    if (tier === "nodata") return { text: "NO DATA", cls: "nodata" };
    return { text: "GOOD", cls: "good" };
  };
  const areaIcon = (key) => {
    if (key === "environment") return { icon: faHome, cls: "ic-yellow" };
    if (key === "careTeam") return { icon: faUsers, cls: "ic-red" };
    return { icon: faUsers, cls: "ic-red" };
  };
  const attentionCount = snapshotAreas.filter((a) => {
    const tier = classifyArea(a.score, a.esSource);
    return tier === "critical" || tier === "moderate";
  }).length;
  const attentionAreas = snapshotAreas
    .map((a) => ({ ...a, tier: classifyArea(a.score, a.esSource), badge: areaBadge(a.score, a.esSource) }))
    .filter((a) => a.tier === "critical" || a.tier === "moderate");
  const scoreInsights = useMemo(
    () =>
      buildScoreInsights({
        snapshotAreas,
        classifyArea,
        data: data || {},
      }),
    [snapshotAreas, data],
  );
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const lastAssessmentDate =
    aipHistory?.[0]?.createdAt || aipHistory?.[0]?.date || null;
  const lastAssessmentLabel = lastAssessmentDate
    ? new Date(lastAssessmentDate).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "—";

  const ageLabel = calculateAge(data.dob || data.dateOfBirth);
  const ageNum = (() => {
    const m = (ageLabel || "").match(/(\d+)/);
    return m ? m[1] : "";
  })();
  // Overall AIP tier (UI label + colors must match)
  // Requested mapping: LOW=red, MODERATE=orange, GOOD=green
  const aipTier = displayAipScore < 50 ? "low" : displayAipScore < 75 ? "moderate" : "good";
  const riskLabel = aipTier === "low" ? "LOW" : aipTier === "moderate" ? "MODERATE" : "GOOD";
  const tierColor =
    aipTier === "low" ? "#ef4444" : aipTier === "moderate" ? "#faad14" : "#52c41a";
  const ringDeg = Math.max(0, Math.min(100, Number(displayAipScore || 0))) * 3.6;

  // Show loading state
  if (isLoading) {
    return (
      <div
        className="aip-analysis-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div style={{ fontSize: "18px", color: "#666" }}>
          Loading client data...
        </div>
      </div>
    );
  }

  const isIntegrationView =
    Boolean(activeSection) && INTEGRATION_SIDEBAR_SECTIONS.has(activeSection);

  const renderActiveIntegrationPanel = () => {
    switch (activeSection) {
      case "sensi-report":
        return sensiReportOpened ? (
          <SensiReportSection key={clientId} clientId={clientId} />
        ) : null;
      case "vital-insights":
        return (
          <VitalInsightsSection
            dashboardUrl={vitalInsightsAccess.dashboardUrl}
            enabled={vitalInsightsAccess.enabled}
            hearthAgencyId={vitalInsightsAccess.hearthAgencyId}
            clientId={clientId}
            clientName={
              [clientMeta.firstName, clientMeta.lastName].filter(Boolean).join(" ") ||
              ""
            }
          />
        );
      case "pacsana":
        return <PacSanaSection clientId={clientId} showSampleSleep={pacSanaSampleClient} />;
      case "health-indicators":
        return (
          <>
            <GripStrengthGaitSpeed
              data={data}
              clientId={clientId}
              showSampleData={ableCareSampleClient}
              canEdit={canEditAbleCareManual}
              onSaveManual={handleSaveAbleCareManual}
            />
          </>
        );
      case "medwand":
        return (
          <MedWandSection
            clientId={clientId}
            showSampleData={medWandPilotClient}
          />
        );
      case "care-link-360":
        return <CareLink360Section clientId={clientId} />;
      case "carelink360":
        return (
          <>
            {renderSectionHistoryTabs("carelink360")}
            <CareLink360
              clientIdDisplay={
                clientMeta.clientId ??
                clientMeta.clientNumber ??
                clientMeta.id ??
                ""
              }
              data={
                getSectionDataSource("carelink360")?.careLink360 ??
                getSectionDataSource("carelink360")
              }
            />
          </>
        );
      case "home-client-monitoring":
        return <HomeClientMonitoring />;
      case "wellsky-data":
        return <WellSkyDataPanel client={clientMeta} />;
      case "care-count":
        return <CareCountSection clientId={clientId} />;
      case "activity-tracker":
        return (
          <div className="activity-tracker-pane">
            <div className="activity-tracker-grid">
              <section className="activity-widget-card">
                <header className="activity-widget-header">
                  <h3>Time &amp; Location</h3>
                  <p>Social activity, sleep regularity</p>
                </header>
                <div className="activity-time-ring-wrap">
                  <div className="activity-time-ring">
                    <div className="activity-time-ring-inner">
                      <div className="activity-time-center">🚶</div>
                    </div>
                  </div>
                  <div className="activity-time-mark top">12 pm</div>
                  <div className="activity-time-mark right">6 pm</div>
                  <div className="activity-time-mark bottom">12 am</div>
                  <div className="activity-time-mark left">6 am</div>
                </div>
              </section>

              <section className="activity-widget-card">
                <header className="activity-widget-header">
                  <h3>Exercise</h3>
                  <p>Movement &amp; exercise per day</p>
                </header>
                <svg
                  className="activity-line-chart"
                  viewBox="0 0 640 240"
                  role="img"
                  aria-label="Exercise movement chart"
                >
                  <line x1="50" y1="200" x2="610" y2="200" className="chart-axis" />
                  <line x1="50" y1="170" x2="610" y2="170" className="chart-grid" />
                  <line x1="50" y1="140" x2="610" y2="140" className="chart-grid" />
                  <line x1="50" y1="110" x2="610" y2="110" className="chart-grid" />
                  <line x1="50" y1="80" x2="610" y2="80" className="chart-grid" />
                  <line x1="50" y1="150" x2="610" y2="150" className="chart-typical" />
                  <line x1="50" y1="178" x2="610" y2="178" className="chart-min" />
                  <polyline
                    points="70,165 150,60 240,115 330,140 420,178 500,186 590,138"
                    className="chart-line-main"
                  />
                  <text x="414" y="146" className="chart-label-typical">
                    Daily Living Typical
                  </text>
                  <text x="390" y="174" className="chart-label-min">
                    Daily Living Minimum
                  </text>
                </svg>
              </section>

              <section className="activity-widget-card">
                <header className="activity-widget-header">
                  <h3>Coping Metrics</h3>
                  <p>Gait Speed, Longest Immobility</p>
                </header>
                <div className="coping-gauge">
                  <div className="coping-gauge-arc">
                    <div className="coping-gauge-hole">
                      <div className="coping-gauge-value">42</div>
                      <div className="coping-gauge-unit">steps/minute</div>
                    </div>
                  </div>
                  <div className="coping-gauge-scale">
                    <span>Very Slow</span>
                    <span>Good</span>
                  </div>
                </div>
              </section>

              <section className="activity-widget-card">
                <header className="activity-widget-header">
                  <h3>Live Activity Feed</h3>
                  <p>Device + motion + location status (mock data)</p>
                </header>
                <div className="activity-feed-table-wrap">
                  <table className="activity-feed-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Gateway</th>
                        <th>Bracelet</th>
                        <th>Battery</th>
                        <th>Gait</th>
                        <th>Activity</th>
                        <th>Location</th>
                        <th>Alert</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          user: "FI Jim Clarke",
                          gateway: "online",
                          bracelet: "offline",
                          battery: "high",
                          gait: "normal",
                          activity: "low",
                          location: "Out of range",
                          alert: "27.09.2023",
                        },
                        {
                          user: "FI John.D",
                          gateway: "online",
                          bracelet: "online",
                          battery: "low",
                          gait: "normal",
                          activity: "high",
                          location: "Living area",
                          alert: "08:02",
                        },
                        {
                          user: "FI Val.D",
                          gateway: "online",
                          bracelet: "online",
                          battery: "high",
                          gait: "normal",
                          activity: "high",
                          location: "Living area",
                          alert: "23.09.2023",
                        },
                      ].map((row) => (
                        <tr key={row.user}>
                          <td>{row.user}</td>
                          <td>
                            <span className={`tracker-dot ${row.gateway}`} />
                          </td>
                          <td>
                            <span className={`tracker-dot ${row.bracelet}`} />
                          </td>
                          <td>
                            <span className={`tracker-battery ${row.battery}`} />
                          </td>
                          <td>
                            <span className={`tracker-dot ${row.gait}`} />
                          </td>
                          <td>
                            <span className={`tracker-dot ${row.activity}`} />
                          </td>
                          <td>{row.location}</td>
                          <td>{row.alert}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`aip-analysis-container${
        isIntegrationView ? " integration-focus" : ""
      }`}
    >
      {successMessage && (
        <div
          className={`success-banner ${
            successMessage.includes("Error") || successMessage.includes("❌")
              ? "error"
              : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {successMessage}
        </div>
      )}

      {/* Breadcrumb / Back Navigation */}
      <div
        style={{
          padding: "12px 24px",
          borderBottom: "1px solid #e8e8e8",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
          }}
          onClick={handleBack}
        >
          <span style={{ fontSize: "16px", color: "#1890ff" }}>←</span>
          <span
            style={{ fontSize: "14px", color: "#595959", fontWeight: "500" }}
          >
            {backLabel}
          </span>
        </div>

        {/* Intentionally removed: Client: {Name} from top bar */}
      </div>

      {/* Client Snapshot (matches provided model; UI only) */}
      <div className="client-snapshot">
        <div className="client-snapshot-grid">
          <div className="client-snapshot-sidebar">
            <div className="snapshot-sidebar-tabs" role="tablist" aria-label="Client sidebar sections">
              <button
                type="button"
                role="tab"
                aria-selected={snapshotSidebarTab === "snapshot"}
                className={`snapshot-sidebar-tab ${
                  snapshotSidebarTab === "snapshot" ? "active" : ""
                }`}
                onClick={() => {
                  setSnapshotSidebarTab("snapshot");
                  if (INTEGRATION_SIDEBAR_SECTIONS.has(activeSection)) {
                    setActiveSection(null);
                  }
                }}
              >
                Client Snapshot
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={snapshotSidebarTab === "integration"}
                className={`snapshot-sidebar-tab ${
                  snapshotSidebarTab === "integration" ? "active" : ""
                }`}
                onClick={() => setSnapshotSidebarTab("integration")}
              >
                Integration
              </button>
            </div>

            <div className="snapshot-nav-cards-five">
              {snapshotSidebarTab === "snapshot" ? (
                <>
                  <button
                    className={`snapshot-nav-card nav-purple ${
                      activeSection === "client-info" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleCardClick("client-info")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Client Information</span>
                        <span className="nav-subtitle">
                          View and edit client details
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-red ${
                      activeSection === "health-metrics" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleCardClick("health-metrics")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faHeartbeat} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Health Metrics</span>
                        <span className="nav-subtitle">
                          View vital signs and health measurements
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-blue ${
                      activeSection === "daily-life-resilience" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("daily-life-resilience")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faHeartPulse} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Daily Life &amp; Resilience</span>
                        <span className="nav-subtitle">
                          Lifestyle and resilience indicators
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-violet ${
                      activeSection === "referral-information" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleCardClick("referral-information")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faFileLines} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Referral Information</span>
                        <span className="nav-subtitle">
                          Intake and referral details
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-teal ${
                      activeSection === "care-information" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleCardClick("care-information")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faClipboardCheck} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Care Information</span>
                        <span className="nav-subtitle">
                          Current care arrangements and support
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-green ${
                      activeSection === "medical-history" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleCardClick("medical-history")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faPills} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Medical History</span>
                        <span className="nav-subtitle">
                          Conditions, medications, and allergies
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`snapshot-nav-card nav-violet ${
                      activeSection === "sensi-report" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("sensi-report")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <LucideFileText size={16} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">SensiReport</span>
                        <span className="nav-subtitle">
                          Upload Sensi PDF reports &amp; track their status
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-orange ${
                      activeSection === "vital-insights" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("vital-insights")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faHeartPulse} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Vital Insights</span>
                        <span className="nav-subtitle">
                          MyAlbert.ai ↔ VI — visits, Grace Care Reports &amp; flags
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  {/* "Activity Tracker" is hidden — replaced by PacSana */}
                  <button
                    className={`snapshot-nav-card nav-orange ${
                      activeSection === "pacsana" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("pacsana")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <LucideWatch size={16} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">PacSana</span>
                        <span className="nav-subtitle">
                          Sleep, activity &amp; gait from the PacSana bracelet
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-teal ${
                      activeSection === "health-indicators" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("health-indicators")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faChartLine} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">AbleCare</span>
                        <span className="nav-subtitle">
                          Falls risk: grip, chair stand, gait &amp; TUG
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-green ${
                      activeSection === "medwand" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("medwand")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <LucideStethoscope size={16} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">MedWand</span>
                        <span className="nav-subtitle">
                          Vitals, imaging &amp; auscultation from the MedWand device
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  {/* "Cognitive and Wellness Monitoring" is hidden — replaced
                      by CareLink 360 */}
                  <button
                    className={`snapshot-nav-card nav-indigo ${
                      activeSection === "care-link-360" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("care-link-360")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faLaptop} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">CareLink 360</span>
                        <span className="nav-subtitle">
                          Activity, calls, media &amp; schedule from CareLink360
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-pink ${
                      activeSection === "home-client-monitoring" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("home-client-monitoring")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faHome} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Home / Client Monitoring</span>
                        <span className="nav-subtitle">
                          Sensi, PacSana, Pontosense and others
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-blue ${
                      activeSection === "wellsky-data" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("wellsky-data")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <LucideActivity size={16} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">WellSky Data</span>
                        <span className="nav-subtitle">
                          Care plan, clinical, medications &amp; contacts
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                  <button
                    className={`snapshot-nav-card nav-blue ${
                      activeSection === "care-count" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleScoreCardClick("care-count")}
                  >
                    <span className="nav-left">
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={faUsers} />
                      </span>
                      <span className="nav-text">
                        <span className="nav-title">Care Count</span>
                        <span className="nav-subtitle">
                          Visit &amp; caregiver activity from WellSky
                        </span>
                      </span>
                    </span>
                    <span className="nav-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>

                </>
              )}
            </div>
          </div>

          <div
            className={`client-snapshot-main${
              isIntegrationView ? " client-snapshot-main--integration" : ""
            }`}
          >
            {isIntegrationView ? (
              <div
                ref={integrationMainRef}
                className="client-snapshot-integration-panel tab-pane"
              >
                {renderActiveIntegrationPanel()}
              </div>
            ) : null}

            {!isIntegrationView ? (
            <>
            <div
              className={`snapshot-top-card ${
                hasNoAssessmentAtAll ? "tier-none" : `tier-${aipTier}`
              }`}
            >
              <div className="snapshot-top-left">
                <div className="snapshot-top-row">
                  <div className="snapshot-name">
                    {clientMeta.firstName} {clientMeta.lastName}
                    {ageNum ? <span className="snapshot-age">, Age {ageNum}</span> : null}
                    {isWellSkyClient(clientMeta) ? (
                      <span
                        className={`snapshot-wellsky-status-pill ${
                          getClientWellSkyStatusLabel(clientMeta) === "DeActive"
                            ? "snapshot-wellsky-status-deactive snapshot-wellsky-status-has-tooltip"
                            : "snapshot-wellsky-status-active"
                        }`}
                        title={
                          getClientWellSkyStatusLabel(clientMeta) === "DeActive"
                            ? getWellSkyDeactivationTooltip(clientMeta)
                            : undefined
                        }
                      >
                        {getClientWellSkyStatusLabel(clientMeta) || "—"}
                      </span>
                    ) : null}
                  </div>
                  <button
                    className="snapshot-details-btn"
                    type="button"
                    onClick={() => setShowClientInfoModal(true)}
                  >
                    Important Client Information
                  </button>
                </div>

                <div className="snapshot-subline">
                  <span className="snapshot-subhead-label">Client ID:</span>
                  <span className="snapshot-subhead-value">
                    {clientMeta.clientNumber ??
                      clientMeta.clientId ??
                      clientMeta._id ??
                      clientId ??
                      "—"}
                  </span>
                </div>

                <div className="snapshot-aip-line">
                  <span className="snapshot-aip-icon">
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </span>
                  <span className="snapshot-aip-title">OVERALL AIP SCORE</span>
                  {hasNoAssessmentAtAll ? (
                    <>
                      <span className="snapshot-aip-score snapshot-aip-score-none">
                        No Data
                      </span>
                      <span className="snapshot-needs-assessment-badge">
                        NEEDS ASSESSMENT
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="snapshot-aip-score">{displayAipScore || 0}</span>
                      <span className="snapshot-aip-risk">{riskLabel}</span>
                      {isAipProvisional ? (
                        <span className="snapshot-provisional-pill">Provisional</span>
                      ) : null}
                    </>
                  )}
                </div>
                {hasNoAssessmentAtAll ? (
                  <div className="snapshot-no-assessment-banner">
                    This client has no assessment data yet. WellSky care plan
                    activities may not be scored, or no manual assessment has been
                    completed. Complete an assessment to see this client&apos;s AIP
                    score.
                  </div>
                ) : wellSkyLineage.showBanner ? (
                  <div className="aip-wellsky-lineage-banner">
                    <strong>WellSky-linked AIP.</strong>{" "}
                    {wellSkyLineage.summaryText ||
                      "This client is linked to WellSky; complete a full assessment for fields not synced from WellSky."}{" "}
                    Gait, mobility, grip, and falls are manual unless you complete
                    a full well-being assessment.
                  </div>
                ) : isAipProvisional ? (
                  <div className="snapshot-provisional-banner">
                    This AIP score is based on WellSky care plan data only. Care
                    Team and Environmental Safety sections have not yet been
                    assessed - complete a full assessment for an accurate
                    composite score.
                  </div>
                ) : null}

                <div className="snapshot-meta">
                  Needs Attention:{" "}
                  <button
                    type="button"
                    className="snapshot-attention-link"
                    onClick={() => setShowAttentionModal(true)}
                    disabled={attentionCount === 0}
                    aria-disabled={attentionCount === 0}
                    aria-label={
                      attentionCount === 0
                        ? "No attention areas"
                        : `View ${attentionCount} attention areas`
                    }
                  >
                    {attentionCount} Areas
                  </button>
                </div>
                <div className="snapshot-meta">
                  Last Assessment: <strong>{lastAssessmentLabel}</strong>
                </div>
                {/* Intentionally removed: Start New Assessment */}
              </div>
              <div className="snapshot-top-right">
                <div
                  className="snapshot-ring-wrap"
                  style={{
                    background: hasNoAssessmentAtAll
                      ? "conic-gradient(#d9d9d9 0deg, #f3f4f6 0deg)"
                      : `conic-gradient(${tierColor} ${ringDeg}deg, #f3f4f6 0deg)`,
                  }}
                >
                  <div className="snapshot-ring-inner">
                    <div className="snapshot-ring-label">
                      {hasNoAssessmentAtAll
                        ? "AIP —/100"
                        : `AIP${displayAipScore || 0}/100`}
                    </div>
                    <div className="snapshot-ring-score snapshot-ring-score-none">
                      {hasNoAssessmentAtAll ? "—" : displayAipScore || 0}
                    </div>
                  </div>
                </div>
                <div
                  className={
                    hasNoAssessmentAtAll
                      ? "snapshot-needs-assessment-pill"
                      : "snapshot-risk-pill"
                  }
                >
                  {hasNoAssessmentAtAll ? "NEEDS ASSESSMENT" : riskLabel}
                </div>
              </div>
            </div>

            {showAttentionModal && (
              <div
                className="attention-modal-overlay"
                onClick={() => setShowAttentionModal(false)}
              >
                <div
                  className="attention-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Needs attention areas"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="attention-modal-header">
                    <div className="attention-modal-title">Needs Attention</div>
                    <button
                      type="button"
                      className="attention-modal-close"
                      onClick={() => setShowAttentionModal(false)}
                    >
                      Close
                    </button>
                  </div>

                  <div className="attention-modal-body">
                    {attentionAreas.length === 0 ? (
                      <div className="attention-empty">No areas need attention.</div>
                    ) : (
                      attentionAreas.map((area) => (
                        <button
                          key={area.id}
                          type="button"
                          className="attention-item"
                          onClick={() => {
                            setShowAttentionModal(false);
                            handleScoreCardClick(area.id);
                          }}
                        >
                          <span className="attention-item-left">
                            <span className={`attention-dot ${area.badge.cls}`} />
                            <span className="attention-item-title">{area.title}</span>
                          </span>
                          <span className={`risk-badge ${area.badge.cls}`}>
                            {area.badge.text}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="snapshot-area-grid snapshot-area-grid-6">
              {snapshotAreas.map((area) => (
                <div
                  key={area.id}
                  className={`snapshot-risk-card ${classifyArea(area.score, area.esSource)} ${
                    isAipProvisional &&
                    (area.id === "careTeam" || area.id === "environment")
                      ? "snapshot-risk-card-provisional"
                      : ""
                  }`}
                >
                  <div className="risk-left-bar" />
                  <div className="risk-body">
                    <div className="risk-head">
                      <div className="risk-head-left">
                        <span className={`risk-icon ${areaIcon(area.id).cls}`}>
                          <FontAwesomeIcon icon={areaIcon(area.id).icon} />
                        </span>
                        <span className="risk-title">
                          {area.title.toUpperCase()}
                          {area.wellSkyBadge ? (
                            <span className="aip-wellsky-score-badge">WELLSKY</span>
                          ) : null}
                        </span>
                      </div>
                      <div className="risk-head-right">
                        <span className="risk-score">
                          {area.score === null ? "—" : area.score}
                        </span>
                        {area.score === null ? null : (
                          <span className="risk-scale">/100</span>
                        )}
                      </div>
                    </div>

                    <div className="risk-mid">
                      <span className={`risk-badge ${areaBadge(area.score, area.esSource).cls}`}>
                        {areaBadge(area.score, area.esSource).text}
                      </span>
                      {isAipProvisional &&
                      (area.id === "careTeam" || area.id === "environment") ? (
                        <span className="risk-provisional-note">
                          Default baseline
                        </span>
                      ) : null}
                      {area.blurb ? (
                        <span className="risk-blurb">{area.blurb}</span>
                      ) : null}
                    </div>

                    <div className="risk-foot">
                      <button
                        className="risk-view-btn"
                        type="button"
                        onClick={() => handleScoreCardClick(area.id)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Other Assessments card — shown as a 4th card */}
              {(() => {
                const latestVoiceRaw = voiceTimeline?.[0]?.raw;
                const hasVoice = !!(
                  latestVoiceRaw &&
                  typeof latestVoiceRaw === "object" &&
                  Object.keys(latestVoiceRaw).length
                );

                return (
                  <div
                    className={`snapshot-risk-card ${
                      hasVoice ? "good" : "nodata"
                    } other-assessments`}
                  >
                <div className="risk-left-bar" />
                <div className="risk-body other-body">
                  <div className="risk-head">
                    <div className="risk-head-left">
                      <span className="risk-icon ic-yellow">
                        <FontAwesomeIcon icon={faClipboardCheck} />
                      </span>
                      <span className="risk-title">Voice Assessment</span>
                    </div>
                  </div>
                  <div className="risk-mid">
                    <span className="risk-desc risk-desc-card">Cognitive, Mental, Wellness Screening</span>
                  </div>
                  <div className="risk-foot">
                    <button
                      className="risk-view-btn"
                      type="button"
                      onClick={() => handleScoreCardClick("voice-assessment")}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
                );
              })()}

              {/* Client Questions card */}
              <div className="snapshot-risk-card good">
                <div className="risk-left-bar" />
                <div className="risk-body">
                  <div className="risk-head">
                    <div className="risk-head-left">
                      <span className="risk-icon ic-yellow">
                        <FontAwesomeIcon icon={faQuestionCircle} />
                      </span>
                      <span className="risk-title">Client Questions</span>
                    </div>
                  </div>
                  <div className="risk-mid">
                    <span className="risk-desc risk-desc-card">Check In | Check Out Questions</span>
                  </div>
                  <div className="risk-foot">
                    <button
                      className="risk-view-btn"
                      type="button"
                      onClick={() => handleCardClick("client-questions")}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>

              {/* Agent Albert Notes card */}
              <div className="snapshot-risk-card good">
                <div className="risk-left-bar" />
                <div className="risk-body">
                  <div className="risk-head">
                    <div className="risk-head-left">
                      <span className="risk-icon ic-yellow">
                        <FontAwesomeIcon icon={faFileLines} />
                      </span>
                      <span className="risk-title">Shift Report Notes</span>
                    </div>
                  </div>
                  <div className="risk-mid">
                    <span className="risk-desc risk-desc-card">Client report with shift reporting agent</span>
                  </div>
                  <div className="risk-foot">
                    <button
                      className="risk-view-btn"
                      type="button"
                      onClick={() => handleCardClick("visit-notes")}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>

            </div>

            <div className="snapshot-insights-panel">
              <div className="snapshot-insights-header">
                <h3 className="snapshot-insights-title">Score Insights &amp; Recommendations</h3>
                <p className="snapshot-insights-summary">
                  Overall AIP Score: <strong>{displayAipScore || 0}</strong>
                  <span className={`snapshot-insights-tier tier-${aipTier}`}>
                    {riskLabel}
                  </span>
                </p>
              </div>

              {scoreInsights.assessmentNotes ? (
                <div className="snapshot-insights-doc-notes">
                  <span className="snapshot-insights-label">Assessment notes</span>
                  <p>{scoreInsights.assessmentNotes}</p>
                </div>
              ) : null}

              {scoreInsights.categories.length === 0 ? (
                <p className="snapshot-insights-empty">
                  {hasNoAssessmentAtAll
                    ? "No categories have been assessed yet for this client."
                    : "All assessed categories are in Good standing — no flagged areas requiring deeper review."}
                </p>
              ) : (
                <div className="snapshot-insights-list">
                  {scoreInsights.categories.map((item) => (
                    <div key={item.id} className="snapshot-insights-row">
                      <div className="snapshot-insights-row-head">
                        <span className="snapshot-insights-cat">{item.title}</span>
                        <span className="snapshot-insights-score">
                          {item.score == null ? "—" : `${item.score}/100`}
                        </span>
                        <span className={`risk-badge ${item.badge?.cls || "moderate"}`}>
                          {item.badge?.text || "—"}
                        </span>
                      </div>
                      <div className="snapshot-insights-why">
                        <span className="snapshot-insights-label">Why this score</span>
                        <p>{item.explanation}</p>
                      </div>
                      <div className="snapshot-insights-rec">
                        <span className="snapshot-insights-label">Recommendation</span>
                        <p className="snapshot-insights-rec-placeholder">
                          Recommendations coming soon — category-specific guidance
                          requires a product decision on how recommendations are
                          generated.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {scoreInsights.hasDocumentRecommendations ? (
                <div className="snapshot-insights-doc-rec">
                  <span className="snapshot-insights-label">Assessment recommendations</span>
                  <p>{scoreInsights.assessmentRecommendations}</p>
                </div>
              ) : null}
            </div>
            </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Action Buttons & Content (hidden for visit notes and integration panels) */}
      {activeSection !== "visit-notes" && !isIntegrationView && (
        <>
          <div className="analysis-actions-bar">
            <div className="left-actions">
              {isModelingMode && (
                <span
                  style={{
                    fontSize: "14px",
                    color: "#1890ff",
                    fontWeight: "500",
                  }}
                >
                  🧪 Modeling Mode - Changes are temporary
                </span>
              )}
              {isReadOnlyMode && !isManualEdit && (
                <span
                  style={{
                    fontSize: "14px",
                    color: "#8c8c8c",
                    fontWeight: "500",
                  }}
                >
                  🔒 Read-Only Mode - Data cannot be modified
                </span>
              )}
              {isReadOnlyMode && isManualEdit && (
                <span
                  style={{
                    fontSize: "14px",
                    color: "#f59e0b",
                    fontWeight: "bold",
                  }}
                >
                  ✏️ Manual Edit Mode - Make changes and click Save
                </span>
              )}
            </div>
            <div className="right-actions">
              {/* READ-ONLY MODE: Export/Print + Manual Edit (Admin) + Report */}
              {isReadOnlyMode && !isManualEdit && (
                <>
                  <button
                    className="action-btn secondary"
                    onClick={() => window.print()}
                    type="button"
                  >
                    Print / Export
                  </button>

                  <button
                    className="action-btn secondary"
                    onClick={handleExploreScenarios}
                    type="button"
                  >
                    Explore Care Scenarios
                  </button>

                  {/* ADMIN ONLY: Manual Edit Button */}
                  {isAdmin && (
                    <button
                      className="action-btn warning"
                      onClick={handleEnterManualEdit}
                      type="button"
                      style={{
                        backgroundColor: "#f59e0b",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      ✏️ Manual Edit
                    </button>
                  )}

                  <button
                    className="action-btn primary-blue"
                    onClick={handleCalculateScores}
                    type="button"
                  >
                    Report
                  </button>
                </>
              )}

              {/* MANUAL EDIT MODE: Cancel + Save + Report */}
              {isReadOnlyMode && isManualEdit && (
                <>
                  <button
                    className="action-btn secondary"
                    onClick={handleCancelManualEdit}
                    type="button"
                  >
                    ❌ Cancel
                  </button>
                  <button
                    className="action-btn primary-save"
                    onClick={handleRequestManualSave}
                    disabled={saving}
                    type="button"
                    style={{
                      backgroundColor: "#22c55e",
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {saving ? "💾 Saving..." : "💾 Save Changes"}
                  </button>
                  <button
                    className="action-btn primary-blue"
                    onClick={handleCalculateScores}
                    type="button"
                  >
                    Report
                  </button>
                </>
              )}

              {/* MODELING MODE: Reset + Save as Advice + Report */}
              {isModelingMode && (
                <>
                  <button
                    className={`action-btn secondary ${
                      !canReset ? "disabled" : ""
                    }`}
                    onClick={handleReset}
                    disabled={!canReset}
                    type="button"
                  >
                    Reset
                  </button>
                  <button
                    className={`action-btn primary-save ${
                      !isDirty ? "disabled" : ""
                    }`}
                    onClick={handleSaveAsAdvice}
                    disabled={!isDirty}
                    type="button"
                  >
                    💡 Save as Advice
                  </button>
                  <button
                    className="action-btn primary-blue"
                    onClick={handleCalculateScores}
                    type="button"
                  >
                    Report
                  </button>
                </>
              )}
            </div>
          </div>

          <div
            ref={contentDisplayRef}
            className="analysis-content-area"
            style={isReadOnlyMode && !isManualEdit ? { opacity: 0.98 } : {}}
          >
            {activeSection === "aip-analysis" && (
              <div className="tab-pane">
                <div className="history-header">
                  <div className="history-title">
                    <h3>AIP Client History</h3>
                    <span className="text-muted">
                      Review trends and recent assessments
                    </span>
                  </div>
                  <div className="history-filters">
                    <label>Time Range</label>
                    <select
                      value={historyRange}
                      onChange={(e) => setHistoryRange(e.target.value)}
                    >
                      <option value="last7d">Last Week</option>
                      <option value="last1m">Last Month</option>
                      <option value="last3m">Last 3 Months</option>
                      <option value="last6m">Last 6 Months</option>
                      <option value="all">All Time</option>
                    </select>
                  </div>
                </div>

                <AipHistoryEnhanced
                  history={aipHistory}
                  isLoading={historyLoading}
                  selectedRange={historyRange}
                  onRangeChange={(range) => setHistoryRange(range)}
                />
              </div>
            )}

            {activeSection === "client-info" && (
              <div className="tab-pane client-info-section">
                <div className="client-info-section-header">
                  <div>
                    <h3 className="client-info-section-title">Client Information</h3>
                    <p className="client-info-section-subtitle">View and edit client details</p>
                  </div>
                </div>
                <div className="client-info-cards-grid">
                  <BasicInfo
                    data={data}
                    onChange={handleChange}
                    clientIdDisplay={
                      clientMeta.clientNumber ??
                      clientMeta.clientId ??
                      clientMeta._id ??
                      clientId ??
                      "—"
                    }
                    clientMeta={clientMeta}
                    disabled={fieldsAreDisabled}
                    renderAsCards
                  />
                </div>
              </div>
            )}

            {activeSection === "health-metrics" && (
              <HealthMetrics
                data={data}
                onChange={handleChange}
                disabled={fieldsAreDisabled}
              />
            )}

            {activeSection === "referral-information" && (
              <ReferralInformation
                data={data}
                onChange={handleChange}
                disabled={fieldsAreDisabled}
              />
            )}

            {activeSection === "care-information" && (
              <CareInformation
                data={data}
                onChange={handleChange}
                disabled={fieldsAreDisabled}
              />
            )}

            {activeSection === "medical-history" && (
              <MedicalHistory
                data={data}
                onChange={handleChange}
                disabled={fieldsAreDisabled}
              />
            )}

            {activeSection === "adl-assessment" && (
              <AdlAssessment
                data={data}
                onChange={handleChange}
                disabled={fieldsAreDisabled}
              />
            )}

            {activeSection === "iadl-assessment" && (
              <IadlAssessment
                data={data}
                onChange={handleChange}
                disabled={fieldsAreDisabled}
              />
            )}

            {activeSection === "health-indicators" && (
              <div className="tab-pane">
                <GripStrengthGaitSpeed
                  data={data}
                  clientId={clientId}
                  showSampleData={ableCareSampleClient}
                  canEdit={canEditAbleCareManual}
                  onSaveManual={handleSaveAbleCareManual}
                />
              </div>
            )}

            {activeSection === "environmental-safety" && (
              <div className="tab-pane">
                {renderSectionHistoryTabs("environmental-safety")}
                <EnvironmentalSafety
                  data={getSectionDataSource("environmental-safety")}
                  onChange={handleChange}
                  disabled={
                    fieldsAreDisabled ||
                    getSectionTabMode("environmental-safety") === "archived"
                  }
                />
              </div>
            )}

            {activeSection === "client-questions" && (
              <div className="tab-pane">
                <div className="mobile-hero">
                  <div className="mobile-hero-title">
                    <div className="pill soft">Mobile</div>
                    <h3>Shift Check-in & Check-out Questions</h3>
                    <p>
                      Answers to the questions presented to the caregiver at shift check-in & check-out.
                    </p>
                  </div>
                  <div className="mobile-hero-meta">
                    <div className="meta-block">
                      <div className="meta-label">Latest activity</div>
                      <div className="meta-value">
                        {mobileVisits[0]
                          ? formatVisitRange(mobileVisits[0])
                          : "—"}
                      </div>
                    </div>
                    <div className="meta-block">
                      <div className="meta-label">Total sessions</div>
                      <div className="meta-value">
                        {mobileVisits.length || 0}
                      </div>
                    </div>
                    {questionSetsLoading && (
                      <div className="meta-block subtle">
                        Loading question sets…
                      </div>
                    )}
                  </div>
                </div>

                {mobileVisits.length > 1 && (
                  <div className="mobile-toggle">
                    <button
                      className={`toggle-btn ${!showArchive ? "active" : ""}`}
                      onClick={() => setShowArchive(false)}
                    >
                      Latest
                    </button>
                    <button
                      className={`toggle-btn ${showArchive ? "active" : ""}`}
                      onClick={() => setShowArchive(true)}
                    >
                      Archived
                    </button>
                  </div>
                )}

                {mobileLoading && (
                  <div className="loading">Loading mobile visits...</div>
                )}
                {!mobileLoading && mobileVisits.length === 0 && (
                  <div className="empty-state">
                    No mobile check-ins or check-outs recorded.
                  </div>
                )}

                {!mobileLoading && mobileVisits.length > 0 && (
                  <div className="mobile-session-list">
                    {(showArchive
                      ? mobileVisits.slice(1)
                      : mobileVisits.slice(0, 1)
                    ).map((visit, idx) => {
                      return (
                        <div
                          className={`mobile-session-card ${
                            !showArchive && idx === 0 ? "featured" : ""
                          }`}
                          key={visit._id || visit.id || visit.start || idx}
                        >
                          <div className="mobile-session-header">
                            <div className="mobile-session-provider">
                              <div className="provider-name">
                                {visit.providerName || "Provider"}
                              </div>
                              <div className="provider-role">
                                {visit.providerRole || "Caregiver/Nurse"}
                              </div>
                            </div>
                            <div className="mobile-session-status">
                              <span className="status-pill">
                                {visit.status || "Checked In"}
                              </span>
                              <div className="mobile-visit-datetime">
                                {formatVisitRange(visit)}
                              </div>
                            </div>
                          </div>

                          <div className="qa-stage-grid">
                            {renderStageTable(visit, "checkIn")}
                            {renderStageTable(visit, "checkOut")}
                          </div>
                        </div>
                      );
                    })}
                    {showArchive && mobileVisits.slice(1).length === 0 && (
                      <div className="empty-state">
                        No archived sessions yet.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeSection === "voice-assessment" && (
              <div className="tab-pane voice-results-tab">
                {renderSectionHistoryTabs("voice-assessment")}
                {getSectionTabMode("voice-assessment") === "archived" && (
                  <div className="empty-state" style={{ marginBottom: 12 }}>
                    Showing archived voice assessment data.
                  </div>
                )}
                <div className="voice-results-header">
                  <h3>Screening Results</h3>
                  <span className="voice-results-close-note">
                    Voice Assessment
                  </span>
                </div>
                <div className="empty-state" style={{ marginBottom: 12 }}>
                  Source recorded at:{" "}
                  {activeVoiceEntry?.recordedAt
                    ? new Date(activeVoiceEntry.recordedAt).toLocaleString()
                    : "Not available"}
                </div>

                <div className="voice-assessment-summary">
                  <div className="voice-assessment-summary-card voice-assessment-card-behavior">
                    <div className="voice-assessment-summary-icon voice-assessment-behavior">
                      <FontAwesomeIcon icon={faSmile} />
                    </div>
                    <div className="voice-assessment-summary-content">
                      <h4>Behavior Assessment</h4>
                      <div className="voice-assessment-summary-values">
                        <span className="voice-assessment-badge" style={{ color: getVoiceCategoryColor(getVoiceResults.mood), borderColor: getVoiceCategoryColor(getVoiceResults.mood) }}>
                          Mood: {getVoiceResults.mood || "low"}
                        </span>
                        <span className="voice-assessment-badge" style={{ color: getVoiceCategoryColor(getVoiceResults.stress), borderColor: getVoiceCategoryColor(getVoiceResults.stress) }}>
                          Stress: {getVoiceResults.stress || "low"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="voice-assessment-summary-card voice-assessment-card-cognitive">
                    <div className="voice-assessment-summary-icon voice-assessment-cognitive">
                      <FontAwesomeIcon icon={faBrain} />
                    </div>
                    <div className="voice-assessment-summary-content">
                      <h4>Cognitive Assessment</h4>
                      <span className="voice-assessment-result" style={{ color: getVoiceCategoryColor(getVoiceResults.cognitiveScreening), borderColor: getVoiceCategoryColor(getVoiceResults.cognitiveScreening) }}>
                        {getVoiceResults.cognitiveScreening || "Additional Screening Not Recommended"}
                      </span>
                    </div>
                  </div>
                  <div className="voice-assessment-summary-card voice-assessment-card-aggression">
                    <div className="voice-assessment-summary-icon voice-assessment-aggression">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="voice-assessment-summary-content">
                      <h4>Aggression Assessment</h4>
                      <span className="voice-assessment-result" style={{ color: getVoiceCategoryColor(getVoiceResults.aggression), borderColor: getVoiceCategoryColor(getVoiceResults.aggression) }}>
                        {getVoiceResults.aggression || "low"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="voice-section-card">
                  <h4>🧠 Cognitive Health</h4>
                  <div
                    className="voice-alert"
                    style={{
                      background: `${getVoiceCategoryColor(getVoiceResults.cognitiveScreening)}1F`,
                      color: getVoiceCategoryColor(getVoiceResults.cognitiveScreening),
                    }}
                  >
                    {getVoiceResults.cognitiveScreening}
                  </div>
                  <div className="voice-chip-grid two-col">
                    <div className="voice-chip">
                      <div className="voice-chip-title">Alzheimer&apos;s</div>
                      <div
                        className="voice-chip-value"
                        style={{
                          color: getVoiceCategoryColor(
                            getVoiceResults.alzheimerCategory,
                          ),
                        }}
                      >
                        {getVoiceResults.alzheimerCategory}
                      </div>
                    </div>
                    <div className="voice-chip">
                      <div className="voice-chip-title">MCI</div>
                      <div
                        className="voice-chip-value"
                        style={{
                          color: getVoiceCategoryColor(getVoiceResults.mciCategory),
                        }}
                      >
                        {getVoiceResults.mciCategory}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="voice-section-card">
                  <h4>😊 Mental Health</h4>
                  <div className="voice-chip-grid three-col">
                    <div className="voice-chip">
                      <div className="voice-chip-title">Mood</div>
                      <div
                        className="voice-chip-value"
                        style={{ color: getVoiceCategoryColor(getVoiceResults.mood) }}
                      >
                        {getVoiceResults.mood}
                      </div>
                    </div>
                    <div className="voice-chip">
                      <div className="voice-chip-title">Stress</div>
                      <div
                        className="voice-chip-value"
                        style={{ color: getVoiceCategoryColor(getVoiceResults.stress) }}
                      >
                        {getVoiceResults.stress}
                      </div>
                    </div>
                    <div className="voice-chip">
                      <div className="voice-chip-title">Aggression</div>
                      <div
                        className="voice-chip-value"
                        style={{
                          color: getVoiceCategoryColor(getVoiceResults.aggression),
                        }}
                      >
                        {getVoiceResults.aggression}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="voice-section-card">
                  <h4>💪 Wellness &amp; Energy</h4>
                  <div className="voice-wellness-card">
                    <div className="voice-wellness-label">Overall Wellness</div>
                    <div
                      className="voice-wellness-score"
                      style={{
                        color: getVoiceCategoryColor(getVoiceResults.wellnessCategory),
                      }}
                    >
                      {Number(getVoiceResults.wellness || 0).toFixed(1)}
                    </div>
                    <div
                      className="voice-wellness-category"
                      style={{
                        color: getVoiceCategoryColor(getVoiceResults.wellnessCategory),
                      }}
                    >
                      {getVoiceResults.wellnessCategory}
                    </div>
                  </div>

                  {[
                    {
                      label: "Vocal Power",
                      value: Number(getVoiceResults.vocalPower || 0),
                      category: getVoiceResults.vocalPowerCategory || "Med",
                    },
                    {
                      label: "Vocal Speed",
                      value: Number(getVoiceResults.vocalSpeed || 0),
                      category: getVoiceResults.vocalSpeedCategory || "Med",
                    },
                    {
                      label: "Vocal Dynamics",
                      value: Number(getVoiceResults.vocalDynamics || 0),
                      category: getVoiceResults.vocalDynamicsCategory || "Med",
                    },
                    {
                      label: "Overall Energy",
                      value: Number(getVoiceResults.energyOverall || 0),
                      category: getVoiceResults.energyCategory || "Med",
                    },
                  ].map((metric) => {
                    const bounded = Math.max(0, Math.min(100, metric.value));
                    return (
                      <div className="voice-metric-row" key={metric.label}>
                        <div className="voice-metric-head">
                          <span>{metric.label}</span>
                          <span
                            style={{
                              color: getVoiceCategoryColor(metric.category),
                              fontWeight: 700,
                            }}
                          >
                            {metric.category}
                          </span>
                        </div>
                        <div className="voice-metric-track">
                          <div
                            className="voice-metric-fill"
                            style={{
                              width: `${bounded}%`,
                              background: getVoiceCategoryColor(metric.category),
                            }}
                          />
                        </div>
                        <div className="voice-metric-value">
                          {bounded.toFixed(1)}/100
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="voice-disclaimer">
                  ⚠️ This screening is for informational purposes only and does
                  not constitute medical advice. Please consult a healthcare
                  professional for proper diagnosis and treatment.
                </div>
              </div>
            )}

            {activeSection === "carelink360" && (
              <div className="tab-pane">
                {renderSectionHistoryTabs("carelink360")}
                <CareLink360
                  clientIdDisplay={
                    clientMeta.clientId ??
                    clientMeta.clientNumber ??
                    clientMeta.id ??
                    ""
                  }
                  data={
                    getSectionDataSource("carelink360")?.careLink360 ??
                    getSectionDataSource("carelink360")
                  }
                />
              </div>
            )}

            {activeSection === "home-client-monitoring" && (
              <div className="tab-pane">
                <HomeClientMonitoring />
              </div>
            )}

            {activeSection === "wellsky-data" && (
              <div className="tab-pane">
                <WellSkyDataPanel client={clientMeta} />
              </div>
            )}

            {activeSection === "care-count" && (
              <div className="tab-pane">
                <CareCountSection clientId={clientId} />
              </div>
            )}

            {activeSection === "care-link-360" && (
              <div className="tab-pane">
                <CareLink360Section clientId={clientId} />
              </div>
            )}

            {activeSection === "medwand" && (
              <div className="tab-pane">
                <MedWandSection
                  clientId={clientId}
                  showSampleData={medWandPilotClient}
                />
              </div>
            )}

            {sensiReportOpened && (
              <div
                className="tab-pane"
                style={activeSection === "sensi-report" ? undefined : { display: "none" }}
              >
                <SensiReportSection key={clientId} clientId={clientId} />
              </div>
            )}

            {activeSection === "pacsana" && (
              <div className="tab-pane">
                <PacSanaSection clientId={clientId} showSampleSleep={pacSanaSampleClient} />
              </div>
            )}

            {activeSection === "vital-insights" && (
              <div className="tab-pane">
                <VitalInsightsSection
                  dashboardUrl={vitalInsightsAccess.dashboardUrl}
                  enabled={vitalInsightsAccess.enabled}
                  hearthAgencyId={vitalInsightsAccess.hearthAgencyId}
                  clientId={clientId}
                  clientName={
                    [clientMeta.firstName, clientMeta.lastName]
                      .filter(Boolean)
                      .join(" ") || ""
                  }
                />
              </div>
            )}

            {activeSection === "daily-life-resilience" && (
              <div className="tab-pane">
                {renderSectionHistoryTabs("daily-life-resilience")}
                <DailyLifeResilience
                  data={getSectionDataSource("daily-life-resilience")}
                  onChange={handleChange}
                  disabled={
                    fieldsAreDisabled ||
                    getSectionTabMode("daily-life-resilience") === "archived"
                  }
                />
              </div>
            )}

            {activeSection === "activity-tracker" && (
              <div className="tab-pane activity-tracker-pane">
                <div className="activity-tracker-grid">
                  <section className="activity-widget-card">
                    <header className="activity-widget-header">
                      <h3>Time &amp; Location</h3>
                      <p>Social activity, sleep regularity</p>
                    </header>
                    <div className="activity-time-ring-wrap">
                      <div className="activity-time-ring">
                        <div className="activity-time-ring-inner">
                          <div className="activity-time-center">🚶</div>
                        </div>
                      </div>
                      <div className="activity-time-mark top">12 pm</div>
                      <div className="activity-time-mark right">6 pm</div>
                      <div className="activity-time-mark bottom">12 am</div>
                      <div className="activity-time-mark left">6 am</div>
                    </div>
                  </section>

                  <section className="activity-widget-card">
                    <header className="activity-widget-header">
                      <h3>Exercise</h3>
                      <p>Movement &amp; exercise per day</p>
                    </header>
                    <svg
                      className="activity-line-chart"
                      viewBox="0 0 640 240"
                      role="img"
                      aria-label="Exercise movement chart"
                    >
                      <line x1="50" y1="200" x2="610" y2="200" className="chart-axis" />
                      <line x1="50" y1="170" x2="610" y2="170" className="chart-grid" />
                      <line x1="50" y1="140" x2="610" y2="140" className="chart-grid" />
                      <line x1="50" y1="110" x2="610" y2="110" className="chart-grid" />
                      <line x1="50" y1="80" x2="610" y2="80" className="chart-grid" />
                      <line x1="50" y1="150" x2="610" y2="150" className="chart-typical" />
                      <line x1="50" y1="178" x2="610" y2="178" className="chart-min" />
                      <polyline
                        points="70,165 150,60 240,115 330,140 420,178 500,186 590,138"
                        className="chart-line-main"
                      />
                      <text x="414" y="146" className="chart-label-typical">
                        Daily Living Typical
                      </text>
                      <text x="390" y="174" className="chart-label-min">
                        Daily Living Minimum
                      </text>
                    </svg>
                  </section>

                  <section className="activity-widget-card">
                    <header className="activity-widget-header">
                      <h3>Coping Metrics</h3>
                      <p>Gait Speed, Longest Immobility</p>
                    </header>
                    <div className="coping-gauge">
                      <div className="coping-gauge-arc">
                        <div className="coping-gauge-hole">
                          <div className="coping-gauge-value">42</div>
                          <div className="coping-gauge-unit">steps/minute</div>
                        </div>
                      </div>
                      <div className="coping-gauge-scale">
                        <span>Very Slow</span>
                        <span>Good</span>
                      </div>
                    </div>
                  </section>

                  <section className="activity-widget-card">
                    <header className="activity-widget-header">
                      <h3>Live Activity Feed</h3>
                      <p>Device + motion + location status (mock data)</p>
                    </header>
                    <div className="activity-feed-table-wrap">
                      <table className="activity-feed-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Gateway</th>
                            <th>Bracelet</th>
                            <th>Battery</th>
                            <th>Gait</th>
                            <th>Activity</th>
                            <th>Location</th>
                            <th>Alert</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            {
                              user: "FI Jim Clarke",
                              gateway: "online",
                              bracelet: "offline",
                              battery: "high",
                              gait: "normal",
                              activity: "low",
                              location: "Out of range",
                              alert: "27.09.2023",
                            },
                            {
                              user: "FI John.D",
                              gateway: "online",
                              bracelet: "online",
                              battery: "low",
                              gait: "normal",
                              activity: "high",
                              location: "Living area",
                              alert: "08:02",
                            },
                            {
                              user: "FI Val.D",
                              gateway: "online",
                              bracelet: "online",
                              battery: "high",
                              gait: "normal",
                              activity: "high",
                              location: "Living area",
                              alert: "23.09.2023",
                            },
                          ].map((row) => (
                            <tr key={row.user}>
                              <td>{row.user}</td>
                              <td>
                                <span className={`tracker-dot ${row.gateway}`} />
                              </td>
                              <td>
                                <span className={`tracker-dot ${row.bracelet}`} />
                              </td>
                              <td>
                                <span className={`tracker-battery ${row.battery}`} />
                              </td>
                              <td>
                                <span className={`tracker-dot ${row.gait}`} />
                              </td>
                              <td>
                                <span className={`tracker-dot ${row.activity}`} />
                              </td>
                              <td>{row.location}</td>
                              <td>{row.alert}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeSection === "wellbeing" && (
              <div className="tab-pane">
                {renderSectionHistoryTabs("wellbeing")}
                <WellBeingDetailsSection
                  data={getSectionDataSource("wellbeing")}
                  onChange={handleChange}
                  disabled={
                    fieldsAreDisabled ||
                    getSectionTabMode("wellbeing") === "archived"
                  }
                />
                <div className="section-container">
                  <h2 className="section-title">
                    ADL Assessment
                    {wellSkyLineage.carePlanAdl ? (
                      <span
                        className="wellsky-field-badge"
                        style={{ marginLeft: 10, verticalAlign: "middle" }}
                      >
                        WellSky care plan
                      </span>
                    ) : null}
                  </h2>
                  <AdlAssessment
                    data={getSectionDataSource("wellbeing")}
                    onChange={handleChange}
                    disabled={
                      fieldsAreDisabled ||
                      getSectionTabMode("wellbeing") === "archived"
                    }
                  />
                </div>
                <div className="section-container">
                  <h2 className="section-title">IADL Assessment</h2>
                  <IadlAssessment
                    data={getSectionDataSource("wellbeing")}
                    onChange={handleChange}
                    disabled={
                      fieldsAreDisabled ||
                      getSectionTabMode("wellbeing") === "archived"
                    }
                  />
                </div>
              </div>
            )}

            {activeSection === "careTeam" && (
              <div className="tab-pane">
                {renderSectionHistoryTabs("careTeam")}
                <CareTeamDetailsSection
                  data={getSectionDataSource("careTeam")}
                  onNavigate={handleCardClick}
                />
                <CareTeam
                  careTeamModel={
                    getSectionTabMode("careTeam") === "archived"
                      ? buildCareTeamModelFromSource(
                          getSectionDataSource("careTeam"),
                        )
                      : careTeamModel
                  }
                  onCareTeamChange={handleCareTeamChange}
                  wellSkyVisitMetrics={wellSkyLineage.careTeamVisits}
                  disabled={
                    fieldsAreDisabled ||
                    getSectionTabMode("careTeam") === "archived"
                  }
                />
              </div>
            )}

            {activeSection === "environment" && (
              <div className="tab-pane">
                {renderSectionHistoryTabs("environment")}
                <EnvironmentDetailsSection
                  data={getSectionDataSource("environment")}
                  onNavigate={handleCardClick}
                />
                <EnvironmentalSafety
                  data={getSectionDataSource("environment")}
                  onChange={handleChange}
                  disabled={
                    fieldsAreDisabled ||
                    getSectionTabMode("environment") === "archived"
                  }
                />
              </div>
            )}

            {!activeSection && (
              <div className="empty-state">
                <p className="text-gray-500 text-center py-12">
                  Click on a card above to view details
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Unity Visit Note Content */}
      {activeSection === "visit-notes" && (
        <div ref={visitNotesDisplayRef}>
          <UnityCareVisit clientId={clientId} />
        </div>
      )}

      {showSaveConfirm && (
        <div
          className="save-confirm-overlay"
          onClick={handleDismissSaveConfirm}
        >
          <div
            className="save-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-save-title"
            aria-describedby="confirm-save-desc"
          >
            <div className="save-confirm-header">
              <h3 id="confirm-save-title">Confirm Changes</h3>
            </div>
            <div className="save-confirm-body" id="confirm-save-desc">
              By making this change you will permanently change this client's
              record. Are you sure you would like to make this change?
            </div>
            <div className="save-confirm-actions">
              <button
                className="save-confirm-btn secondary"
                onClick={handleDismissSaveConfirm}
                disabled={saving}
                type="button"
              >
                Cancel
              </button>
              <button
                className="save-confirm-btn primary"
                onClick={handleConfirmManualSave}
                disabled={saving}
                type="button"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClientInfoModal && (
        <div
          className="client-info-modal-overlay"
          onClick={handleCloseClientInfoModal}
        >
          <div
            className="client-info-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-info-modal-title"
          >
            <div className="client-info-modal-header">
              <div className="client-info-modal-title" id="client-info-modal-title">
                Important Client Information
              </div>
              <button
                className="client-info-modal-close"
                type="button"
                onClick={handleCloseClientInfoModal}
              >
                Close
              </button>
            </div>

            <div className="client-info-modal-body">
              <ImportantClientInfoSummary
                data={data}
                clientMeta={clientMeta}
                clientId={clientId}
                currentAipScore={displayAipScore || 0}
              />
            </div>
          </div>
        </div>
      )}

      <ScoreResultModal
        isOpen={isScoreModalOpen}
        onClose={() => setIsScoreModalOpen(false)}
        scores={calculatedScores}
        clientData={{
          clientId: clientMeta.clientId || clientMeta._id || clientId,
          firstName: clientMeta.firstName,
          lastName: clientMeta.lastName,
          clientNumber: clientMeta.clientNumber,
          gender: data.gender,
          dob: data.dob,
          primaryCondition: data.primaryCondition,
          _id: data._id,
        }}
      />
    </div>
  );
};

export default AipAnalysis;
